import { LipSyncController } from "./LipSyncController.js";
import { MinimalLipSyncController } from "./LipSyncController.minimal.js";
import { ElevenLabsTTS } from "./ElevenLabsTTS.js";
import { ChatStorageManager } from "./ChatStorageManager.js";
import { FirstTimeUserPrompt } from "./FirstTimeUserPrompt.js";
import { KeywordImageDisplay } from "./KeywordImageDisplay.js";
import { VideoController } from "./VideoController.js";
import { mountTTSSelfContainedUI } from "./TTSSelfContainedUI.js";
import { TTSActive } from "./RealtimeLLMToElevenLabs.js";
import { VADLite } from "./vad-lite.js";
import { ScribeRealtimeSTT } from "./ScribeRealtimeSTT.js";
import { QuestionCounterManager } from "./QuestionCounterManager.js";
import {
    probeGreetingAutoplay,
    hasUnlockGesture,
    armGreetingStart,
    commitGreetingAudioUnlock,
} from "./GreetingAudioUnlock.js";
import { DiagnosticChatManager } from "./DiagnosticChatManager.js";
import { IdleCheckInController } from "./IdleCheckInController.js";
import { getClientTimeInfo } from "./ClientTimeOfDay.js";
import { getLabUrlForModel } from "./characterSlugMapping.js";
import { createLiteYouTubeEmbed } from "./LiteYouTubeEmbed.js";

//import { wireExistingTTSControls } from './TTSControls.existing-ui.js';
// TTS imports removed - now using global window.ttsManager

// Task #530 — MIC-LEDGER: mirror mic-path breadcrumbs into the server log
// (like AUDIO-LEDGER; the production bundle strips console.* so on-device
// Safari behavior is only provable server-side). Flip to false to silence.
const MIC_LEDGER_SERVER_LOG = true;

// Escape HTML so user content can't inject tags/scripts
function escapeHtml(str) {
    if (!str) return "";
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

// Task #199 → #519: one-time-per-device record that this device has produced
// audible greeting audio. It used to GATE the retired Safari "May <legend>
// read their first words to you?" permission card; the popup is gone and the
// flag is now a HINT ONLY — Safari idle check-ins read it to skip speaking
// up on devices that have never actually heard a greeting. It is never a
// gate on greeting playback (see GreetingAudioUnlock.js). Stored in
// localStorage so it survives the full page reload that committing a legend
// from the Hall performs.
const GREETING_AUDIO_GRANT_KEY = "legendlabs.audioGreetingGranted";
export function isGreetingAudioGranted() {
    try {
        return localStorage.getItem(GREETING_AUDIO_GRANT_KEY) === "1";
    } catch (e) {
        return false;
    }
}
export function setGreetingAudioGranted() {
    try {
        localStorage.setItem(GREETING_AUDIO_GRANT_KEY, "1");
    } catch (e) {}
}

// Very small, safe Markdown renderer for chat
function renderMarkdownToHtml(markdown) {
    if (!markdown) return "";

    // Make sure it's a string
    let text = String(markdown);

    // 1) Escape HTML tags first
    text = escapeHtml(text);

    // 2) Bold: **text**
    text = text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

    // 3) Italic: *text*
    text = text.replace(/\*(.+?)\*/g, "<em>$1</em>");

    // 4) Inline code: `code`
    text = text.replace(/`([^`]+)`/g, "<code>$1</code>");

    // 5) Line breaks: \n → <br>
    text = text.replace(/\n/g, "<br>");

    return text;
}

// Free taste for signed-out (anonymous) visitors: how many chat exchanges they
// get before the sign-in wall. Set to 0 so a brand-new visitor must sign up
// BEFORE sending their very first message (the spoken greeting is separate and
// NEVER counted here, so it still plays as a free hook on arrival).
// Single source of truth on the client; the server enforces its own daily cap.
const ANON_FREE_EXCHANGES = 0;

export class ChatController {
    constructor(lipSyncController, modelViewer) {
        this.lipSyncController = lipSyncController;
        this.modelViewer = modelViewer;
        this.animationController = modelViewer.animationController;

        // Initialize all DOM element references as null first
        this.chatHistory = null;
        this.chatInput = null;
        this.sendButton = null;
        this.micButton = null;
        this.micStatus = null;
        this.loadingIndicator = null;
        this.stopAudioButton = null;
        this.micIcon = null;
        this.micButtonContainer = null;
        this.speechSubtitle = null;
        this.speechSubtitleContainer = null;
        this._isVolumeModified = false;

        // Safari detection utility
        this.isSafari = this.detectSafari();
        this.stopButtonPressed = false; // Track when stop button is explicitly pressed

        this.userName = null;
        this.isListening = false;
        this.isAudioPlaying = false;
        this.isProcessing = false;
        this.isEnabled = false;
        this.currentRequestId = null; // Track current audio request
        this.currentAudioRequestId = null; // Track current single audio synthesis request
        this.isChunkedAudioActive = false; // Track if chunked audio is playing
        this.pollingTimeoutId = null; // Track polling timeout for cancellation
        this.tts = new ElevenLabsTTS();
        this.chatStorageManager = new ChatStorageManager();
        this.firstTimeUserPrompt = new FirstTimeUserPrompt();
        this.keywordImageDisplay = new KeywordImageDisplay();
        this.videoController = new VideoController();
        this.questionCounter = new QuestionCounterManager();
        this.diagnosticChatManager = new DiagnosticChatManager();
        this.diagnosticConfidence = 50; // Current diagnostic confidence value (0-100)
        this.lastAudioResponse = null; // Text of the last response
        this.lastAudioBlob = null; // Audio blob of the last response
        this.lastAudioUrl = null; // URL of the last audio blob
        this.currentEmotion = null;
        this.currentBotMessage = null; // Reference to current bot message for highlighting
        this.activeAudioChunks = []; // Track all active audio chunk elements for immediate stopping
        this.pendingMessage = null; // Store message to process after first-time popup is handled
        this.conversationHistory = []; // Store conversation history for context in API calls

        // VAD SYSTEM
        this.recognition = null;
        this.vad = null; // instance of VADLite
        this._captureStream = null; // MediaStream used by VAD
        this._vadTtsSyncTimer = null; // optional sync
        this.isListening = false;
        this.manualStop = false;
        this.userActivatedMicrophone = false;
        this._isVolumeModified = false;

        // Q&A Mode only - continuous listening behavior
        this.qaMode = true;
        this.continuousListening = false;
        this.manualStop = false; // Track if user manually stopped listening to prevent auto-restart

        // Initialize after DOM is ready
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () =>
                this.initializeDOM(),
            );
        } else {
            this.initializeDOM();
        }

        // Set up page unload event to clean up resources
        window.addEventListener("beforeunload", () => this.cleanUp());

        // Emotion controller removed in Task #38 — facial expression is
        // driven by the A2F renderer (see RealtimeLLMToElevenLabs).

        // Also directly connect animation controller to TTS for fallback to idle
        if (this.animationController) {
            this.tts.setAnimationController(this.animationController);
            console.log(
                "Animation controller directly connected to TTS service at initialization",
            );
        }

        // Listen for character switch events to clear conversation history
        document.addEventListener("characterSwitch", () => {
            console.log(
                "Character switch detected - clearing conversation history",
            );
            this.clearConversationHistory();
            // Update streaming TTS for new character
            this.updateStreamingTTSCharacter();
        });

        // Also listen for character-changed events
        document.addEventListener("character-changed", () => {
            console.log(
                "Character changed event detected - clearing conversation history",
            );
            this.clearConversationHistory();
            // Update streaming TTS for new character
            this.updateStreamingTTSCharacter();
        });

        // Initialize streaming properties
        this.streamingController = null;
        this.currentStreamingMessage = null;

        // Track if user has manually activated the microphone
        this.userActivatedMicrophone = false;

        // Task #530 — single-owner mic session state. `micMode` is the ONE
        // source of truth for what the microphone is doing:
        //   null      → mic idle
        //   'call'    → live conversation loop (auto-send + barge-in)
        //   'dictate' → transcript streams into the text field only; the
        //               user sends manually. NOTHING auto-sends in dictate.
        // (The old WebRTCAudioManager — its own getUserMedia + RTCPeerConnection
        // + parallel SpeechRecognition + auto AEC test — is retired; it was
        // never initialized in production and only added capture contention.)
        this.micMode = null;
        this._capturePromise = null; // single-flight getUserMedia guard
        this._micStartPromise = null; // single-flight startListening guard
        this._dictatePrevText = ""; // exact field text to restore on ✕
        this._dictateBase = ""; // prefix (prior text) transcripts append to
        this._dictateFinal = ""; // accumulated final transcript this session
        this._micLedgerQueue = [];
        this._micLedgerTimer = null;
        this._micLedgerSeq = 0;

        // Task #530 rework — ElevenLabs Scribe v2 Realtime is THE
        // transcription engine (both modes, every browser); the browser's
        // webkitSpeechRecognition survives only as a Chromium fallback.
        // On Safari SR opens a SECOND OS capture session next to our
        // getUserMedia stream — the exact contention that froze the tab —
        // so Safari never falls back to it.
        this.scribe = null; // ScribeRealtimeSTT (lazy)
        this._sttEngine = null; // 'scribe' | 'websr' while a session runs
        this._scribeCommittedText = ""; // call-mode committed accumulation
        this._scribeSendTimer = null;
        this._sttRestartWindow = [];
        this._sttBackoff = 350;

        // Initialize the new TTS Manager for advanced real-time streaming
        this.ttsManager = window.ttsManager;
        this.realtimeTTSEnabled = false; // Feature flag for new real-time TTS system
        console.log("[ChatController] Connected to global TTS Manager");

        // Connect lip sync controller to TTSManager for real-time lip sync
        if (this.lipSyncController && this.ttsManager) {
            this.ttsManager.setLipSyncController(this.lipSyncController);
            console.log(
                "[ChatController] Lip sync controller connected to TTS Manager",
            );
        }

        // Female model IDs that don't support speech recognition
        this.femaleModelIds = [
            "770e2d93-a6a4-41ed-aca1-8951327885b5$", // Amelia Earhart
            "73f610bf-d9c9-4d9f-a026-a10086a66725$", // Marie Curie
        ];

        // Listen for model changes to check microphone availability
        this.setupModelChangeListener();

        // Task #462 — idle check-ins: after a stretch of genuine user
        // inactivity the character speaks up on its own (brief, varied,
        // in-character) via the SAME hidden-streaming path as the greeting.
        // All timing/suppression rules live in IdleCheckInController (see its
        // documented tuning constants). The quiet clock only starts once
        // _greetingCompletedAt is stamped (end of sendDynamicGreeting).
        this._greetingCompletedAt = 0;
        this._checkinUsedAngles = [];
        this.idleCheckIns = new IdleCheckInController(this, {
            isAudioGranted: isGreetingAudioGranted,
        });
        this.idleCheckIns.start();
    }

    /**
     * Detect if the browser is Safari
     */

    detectSafari() {
        const userAgent = navigator.userAgent.toLowerCase();
        const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent,
        );
        const isIOS = /iphone|ipad|ipod/.test(userAgent);
        return isSafari || isIOS;
    }

    // Your existing initializeDOM method - add debug call
    initializeDOM() {
        // Initialize DOM elements
        this.chatHistory = document.getElementById("chat-history");
        this.chatInput = document.getElementById("chat-input");
        this.sendButton = document.getElementById("send-button");
        this.micButton = document.getElementById("mic-button");
        this.micStatus = document.getElementById("mic-status");
        this.loadingIndicator = document.getElementById("chat-loading");
        this.stopAudioButton = document.getElementById("stop-audio-button");
        this.micIcon = document.querySelector(".mic-icon");
        this.micButtonContainer = document.querySelector(
            ".mic-button-container",
        );
        this.chatContainer = document.querySelector(".chat-container");

        // Task #530 — Dictate controls in the visible chat input row
        this.dictateWrap = document.getElementById("dictate-wrap");
        this.dictateButton = document.getElementById("dictate-button");
        this.dictateCancelBtn = document.getElementById("dictate-cancel");
        this.dictateAcceptBtn = document.getElementById("dictate-accept");

        // Initialize mode switching elements
        this.answerModeBtn = document.getElementById("answer-mode-btn");
        this.imageModeBtn = document.getElementById("image-mode-btn");
        this.videoModeBtn = document.getElementById("video-mode-btn");
        this.answerContent = document.getElementById("answer-content");
        this.imageContent = document.getElementById("image-content");
        this.videoContent = document.getElementById("video-content");

        // Initialize exit chat button
        this.exitChatBtn = document.getElementById("exit-chat-btn");

        // Initialize notification dot elements
        this.imageNotificationDot = document.getElementById(
            "image-notification-dot",
        );
        this.videoNotificationDot = document.getElementById(
            "video-notification-dot",
        );

        // Initialize image viewer elements (with null checks for removed elements)
        this.imageDisplay = document.getElementById("image-display");
        this.imageTitle = document.getElementById("image-title") || null;
        this.imageDescription =
            document.getElementById("image-description") || null;
        this.imageCounter = document.getElementById("image-counter") || null;
        this.prevImageBtn = document.getElementById("prev-image-btn") || null;
        this.nextImageBtn = document.getElementById("next-image-btn") || null;

        // Initialize image collection and current index
        this.availableImages = [];
        this.currentImageIndex = 0;

        this.setupEventListeners();
        // Task #530 — ONE speech-recognition lifecycle (mode-routed v2).
        // The legacy fallback SR, the WebRTC manager's parallel SR and the
        // auto-run AEC chat test are retired.
        this.setupSpeechRecognition();
        this.setupSideButtonEventListeners();

        this.setupQAMode();

        // Emotion controller removed in Task #38 — facial expression is
        // driven by the A2F renderer (see RealtimeLLMToElevenLabs).

        // Connect lip sync controller to TTS for audio-based pause detection
        if (this.lipSyncController) {
            this.tts.setLipSyncController(this.lipSyncController);
            console.log(
                "LipSync controller connected to TTS service for audio analysis",
            );
        }

        // Initialize volume controller
        this.initializeVolumeController();

        // Initialize streaming TTS manager
        this.initializeStreamingTTS();

        // Listen for model loading complete to retry lip sync initialization
        // This fixes race condition where TTS initializes before blend shapes are ready
        // NOTE: Removed { once: true } so lipsync reinitializes on every model load including character switches
        document.addEventListener("modelLoadingComplete", async () => {
            console.log(
                "[ChatController] Model loading complete - attempting lip sync re-initialization",
            );

            // Wait for TTS boot to complete if it's still in progress
            if (this._ttsBootPromise) {
                try {
                    await this._ttsBootPromise;
                } catch (e) {
                    console.warn(
                        "[ChatController] TTS boot failed during model load wait:",
                        e,
                    );
                }
            }

            const blendShapeCount = this.modelViewer?.blendShapeControls
                ? Object.keys(this.modelViewer.blendShapeControls).length
                : 0;

            console.log("[ChatController] Lip sync re-init check:", {
                hasRealtimeTTS: !!this.realtimeTTS,
                hasModelViewer: !!this.modelViewer,
                blendShapeCount: blendShapeCount,
            });

            if (
                this.realtimeTTS &&
                this.modelViewer?.blendShapeControls &&
                blendShapeCount > 0
            ) {
                try {
                    this.initializeRealtimeLipSync();
                    console.log(
                        "[ChatController] Lip sync re-initialized after model load with",
                        blendShapeCount,
                        "blend shapes",
                    );
                } catch (e) {
                    console.warn(
                        "[ChatController] Lip sync re-init failed:",
                        e,
                    );
                }
            } else {
                console.warn(
                    "[ChatController] Cannot reinitialize lip sync - missing requirements",
                );
            }
        });

        // Initialize realtime TTS controller
        // TTS Controller initialization removed - using global TTS manager

        // Removed test images - no dummy images on model load

        // Check initial microphone availability based on current model (with delay for initialization)
        setTimeout(() => {
            this.checkMicrophoneAvailability();
        }, 1000);

        // Check for pending diagnostic test from Tutor Hub
        this.checkAndStartDiagnosticMode();
    }

    /**
     * Check for pending diagnostic test and start diagnostic mode if found
     */
    async checkAndStartDiagnosticMode() {
        if (this.diagnosticChatManager.checkForPendingDiagnostic()) {
            console.log("[ChatController] Pending diagnostic test detected");

            // Check for spoken introduction from uploaded content analysis
            let spokenIntroduction = null;
            try {
                const pendingTest = sessionStorage.getItem(
                    "pendingDiagnosticTest",
                );
                if (pendingTest) {
                    const data = JSON.parse(pendingTest);
                    if (data.spokenIntroduction) {
                        spokenIntroduction = data.spokenIntroduction;
                        console.log(
                            "[ChatController] Found spoken introduction:",
                            spokenIntroduction,
                        );
                    }
                }
            } catch (e) {
                console.warn(
                    "[ChatController] Error reading spoken introduction:",
                    e,
                );
            }

            // Wait for chat to be enabled
            const waitForEnabled = () => {
                return new Promise((resolve) => {
                    if (this.isEnabled) {
                        resolve();
                    } else {
                        const check = setInterval(() => {
                            if (this.isEnabled) {
                                clearInterval(check);
                                resolve();
                            }
                        }, 100);
                        setTimeout(() => {
                            clearInterval(check);
                            resolve();
                        }, 5000);
                    }
                });
            };

            await waitForEnabled();

            // Start diagnostic session
            const result =
                await this.diagnosticChatManager.startDiagnosticSession();

            if (result.success) {
                // Show diagnostic progress indicator
                this.showDiagnosticProgressUI();

                // Make chat visible
                if (this.chatContainer) {
                    this.chatContainer.classList.add("visible");
                }

                // If there's a spoken introduction from uploaded content, speak it first
                if (spokenIntroduction) {
                    console.log(
                        "[ChatController] Speaking topic introduction first",
                    );
                    this.addMessageToChat(
                        "",
                        spokenIntroduction,
                        "bot-message",
                    );

                    // Speak the introduction and wait for it to complete before showing the question
                    await this.speakTopicIntroduction(spokenIntroduction);

                    // Clear the spoken introduction from sessionStorage to prevent repeats
                    try {
                        const storedTest = sessionStorage.getItem(
                            "pendingDiagnosticTest",
                        );
                        if (storedTest) {
                            const testData = JSON.parse(storedTest);
                            delete testData.spokenIntroduction;
                            sessionStorage.setItem(
                                "pendingDiagnosticTest",
                                JSON.stringify(testData),
                            );
                        }
                    } catch (e) {
                        console.warn(
                            "[ChatController] Error clearing spoken introduction:",
                            e,
                        );
                    }

                    // Small delay before showing the first question
                    await new Promise((resolve) => setTimeout(resolve, 500));
                }

                // Display the first question as a bot message
                this.addMessageToChat("", result.firstQuestion, "bot-message");

                // Speak the diagnostic question aloud
                this.speakDiagnosticQuestion(result.firstQuestion);

                // Show confidence slider for the first question
                this.showDiagnosticConfidenceSlider();

                console.log(
                    "[ChatController] Diagnostic mode started for topic:",
                    result.topic,
                );
            } else {
                console.error(
                    "[ChatController] Failed to start diagnostic:",
                    result.error,
                );
            }
        }
    }

    /**
     * Show diagnostic progress UI indicator in chat header
     */
    showDiagnosticProgressUI() {
        const progress = this.diagnosticChatManager.getProgress();
        if (!progress) return;

        // Hide the exit chat button
        const exitBtn = document.getElementById("exit-chat-btn");
        if (exitBtn) {
            exitBtn.style.display = "none";
        }

        // Create or update progress indicator in header
        let indicator = document.getElementById(
            "diagnostic-progress-indicator",
        );
        const headerTop = document.querySelector(".chat-header-top");

        if (!indicator && headerTop) {
            indicator = document.createElement("div");
            indicator.id = "diagnostic-progress-indicator";
            indicator.className = "diagnostic-header-indicator";
            headerTop.appendChild(indicator);
        }

        if (indicator) {
            indicator.innerHTML = `
                <div class="diagnostic-header-progress">
                    <i class="fas fa-brain"></i>
                    <span class="diagnostic-header-text">Questions ${progress.current + 1}/${progress.total}</span>
                    <div class="diagnostic-header-bar">
                        <div class="diagnostic-header-bar-fill" style="width: ${progress.percentage}%"></div>
                    </div>
                </div>
                <button class="diagnostic-header-exit" title="Exit diagnostic mode">
                    <i class="fas fa-times"></i>
                </button>
            `;
            indicator.style.display = "flex";

            // Add click handler to exit button
            const exitButton = indicator.querySelector(
                ".diagnostic-header-exit",
            );
            if (exitButton) {
                exitButton.onclick = (e) => {
                    e.stopPropagation();
                    this.exitDiagnosticMode();
                };
            }
        }

        // Set up progress update callback
        this.diagnosticChatManager.onProgressUpdate = (newProgress) => {
            this.updateDiagnosticProgressUI(newProgress);
        };
    }

    /**
     * Update diagnostic progress UI in header
     */
    updateDiagnosticProgressUI(progress) {
        const indicator = document.getElementById(
            "diagnostic-progress-indicator",
        );
        if (!indicator) return;

        if (!progress) {
            indicator.style.display = "none";
            this.restoreChatCloseButton();
            return;
        }

        const progressBar = indicator.querySelector(
            ".diagnostic-header-bar-fill",
        );
        const progressText = indicator.querySelector(".diagnostic-header-text");

        if (progressBar) {
            progressBar.style.width = `${progress.percentage}%`;
        }
        if (progressText) {
            if (progress.isComplete) {
                progressText.textContent = "Complete!";
            } else {
                progressText.textContent = `Questions ${progress.current + 1}/${progress.total}`;
            }
        }
    }

    /**
     * Exit diagnostic mode
     */
    exitDiagnosticMode() {
        this.diagnosticChatManager.cancelDiagnostic();
        const indicator = document.getElementById(
            "diagnostic-progress-indicator",
        );
        if (indicator) {
            indicator.style.display = "none";
        }
        this.restoreChatCloseButton();
        this.hideDiagnosticConfidenceSlider();

        // Reset the trigger flags so Answer via Chat can be activated again
        // Both TutorState (index.html) and appState (tutor_unified.html) may be used
        if (window.TutorState) {
            window.TutorState.answerViaChatTriggered = false;
            window.TutorState.isChatModeActive = false;
            window.TutorState.isSubtopicChatModeActive = false;
            window.TutorState.activeQuizQuestionId = null;
            window.TutorState.activeInteractiveQuestionId = null;
        }
        if (window.appState) {
            window.appState.answerViaChatTriggered = false;
        }

        // Update the Tutor Hub toggle button to reflect the deactivated state
        if (window.TutorApp) {
            if (window.TutorApp.updateAnswerViaChatButton) {
                window.TutorApp.updateAnswerViaChatButton();
            }
            // Sync all interactive button labels back to "Answer Interactively"
            if (window.TutorApp.syncAllDiagnosticInteractiveButtons) {
                window.TutorApp.syncAllDiagnosticInteractiveButtons();
            }
            if (window.TutorApp.syncAllQuizInteractiveButtons) {
                window.TutorApp.syncAllQuizInteractiveButtons();
            }
        }

        console.log("[ChatController] Exited diagnostic mode");
    }

    /**
     * Restore the chat close button and hide diagnostic indicator
     */
    restoreChatCloseButton() {
        const exitBtn = document.getElementById("exit-chat-btn");
        if (exitBtn) {
            exitBtn.style.display = "";
        }
        const indicator = document.getElementById(
            "diagnostic-progress-indicator",
        );
        if (indicator) {
            indicator.style.display = "none";
        }
    }

    /**
     * Show the diagnostic confidence slider in chat input area
     * Now delays visibility until user starts typing
     */
    showDiagnosticConfidenceSlider() {
        const container = document.getElementById(
            "diagnostic-confidence-container",
        );
        if (container) {
            // Show the container but keep it invisible via CSS (opacity 0)
            container.style.display = "block";
            container.classList.remove("visible"); // Start hidden

            // Reset to 50% for each new question
            this.diagnosticConfidence = 50;
            const slider = document.getElementById("chat-confidence-slider");
            if (slider) {
                slider.value = 50;
            }

            // Set up global update function
            window.updateChatConfidenceDisplay = (value) => {
                this.updateDiagnosticConfidence(parseInt(value));
            };

            // Set up input listener to reveal slider when user starts typing
            this.setupConfidenceSliderReveal();

            console.log(
                "[ChatController] Diagnostic confidence slider prepared (hidden until typing)",
            );
        }
    }

    /**
     * Set up input listener to reveal confidence slider when user starts typing
     */
    setupConfidenceSliderReveal() {
        const chatInput = document.getElementById("chat-input");
        const container = document.getElementById(
            "diagnostic-confidence-container",
        );

        if (!chatInput || !container) return;

        // Remove previous listener if exists
        if (this._confidenceRevealHandler) {
            chatInput.removeEventListener(
                "input",
                this._confidenceRevealHandler,
            );
        }

        // Create handler to reveal slider on first input
        this._confidenceRevealHandler = () => {
            if (
                chatInput.value.trim().length > 0 &&
                !container.classList.contains("visible")
            ) {
                container.classList.add("visible");
                console.log(
                    "[ChatController] Confidence slider revealed on user input",
                );
            }
        };

        chatInput.addEventListener("input", this._confidenceRevealHandler);
    }

    /**
     * Hide the diagnostic confidence slider
     */
    hideDiagnosticConfidenceSlider() {
        const container = document.getElementById(
            "diagnostic-confidence-container",
        );
        if (container) {
            container.style.display = "none";
            container.classList.remove("visible");
        }

        // Clean up the input listener
        const chatInput = document.getElementById("chat-input");
        if (chatInput && this._confidenceRevealHandler) {
            chatInput.removeEventListener(
                "input",
                this._confidenceRevealHandler,
            );
            this._confidenceRevealHandler = null;
        }
    }

    /**
     * Update the diagnostic confidence value
     */
    updateDiagnosticConfidence(value) {
        this.diagnosticConfidence = value;

        // Sync with TutorState if available
        this.syncConfidenceToTutorState(value);
    }

    /**
     * Sync confidence value to TutorState for bidirectional sync
     */
    syncConfidenceToTutorState(value) {
        const progress = this.diagnosticChatManager.getProgress();
        if (!progress) return;

        const currentQuestionId =
            this.diagnosticChatManager.getCurrentQuestionId();
        if (!currentQuestionId) return;

        if (window.TutorState && window.TutorState.answers) {
            if (!window.TutorState.answers[currentQuestionId]) {
                window.TutorState.answers[currentQuestionId] = {
                    reasoning_text: "",
                    confidence: value / 100,
                    selected_answer: null,
                };
            } else {
                window.TutorState.answers[currentQuestionId].confidence =
                    value / 100;
            }
            console.log(
                `[ChatController] Synced confidence ${value}% for question ${currentQuestionId}`,
            );
        }
    }

    /**
     * Task #99: split a block of text into whole sentences for feeding the
     * TTS engine one sentence at a time. Keeps terminal punctuation with
     * its sentence; falls back to the whole string when no boundary is
     * found so we never drop text.
     */
    _splitIntoSentences(text) {
        if (!text || !text.trim()) return [];
        const matches = text.match(/[^.!?:;]+[.!?:;]+["')\]]?\s*|[^.!?:;]+$/g);
        const out = (matches || [text])
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
        return out.length ? out : [text.trim()];
    }

    /**
     * Speak a diagnostic question aloud using TTS with lip sync
     * Uses the same RealtimeLLMToElevenLabs system as dynamic greetings (handleLLMDelta pattern)
     */
    async speakDiagnosticQuestion(questionText) {
        if (!questionText || !questionText.trim()) {
            console.warn("[speakDiagnosticQuestion] No text provided");
            return;
        }

        console.log(
            "[speakDiagnosticQuestion] Speaking diagnostic question:",
            questionText.substring(0, 80) + "...",
        );

        // Use the same pattern as dynamic greetings - get realtimeTTS directly
        const rtts = this.realtimeTTS;

        if (!rtts) {
            console.warn(
                "[speakDiagnosticQuestion] RealtimeTTS not available - no audio playback",
            );
            // Still trigger animation for visual feedback
            if (this.animationController) {
                this.animationController.onTTSStart();
                const wordCount = questionText.split(/\s+/).length;
                const estimatedDurationMs = Math.max(3000, wordCount * 300);
                setTimeout(() => {
                    if (this.animationController) {
                        this.animationController.onTTSEnd();
                    }
                }, estimatedDurationMs);
            }
            return;
        }

        try {
            // Prep TTS - same as sendHiddenStreamingMessage does for greetings
            if (typeof rtts.ensureReadyForNextResponse === "function") {
                await rtts.ensureReadyForNextResponse(120);
            }
            if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                await rtts.audioCtx.resume();
            }

            // Start TTS connection - same as greeting system
            console.log("[speakDiagnosticQuestion] Starting TTS...");
            rtts.start();

            // Clear stop guard for intentional speech (no turn ID)
            if (typeof rtts.clearStopGuard === "function") {
                rtts.clearStopGuard();
            }

            // Trigger speak animation
            if (this.animationController) {
                this.animationController.onTTSStart();
            }

            // Task #99: feed WHOLE SENTENCES (not word-by-word). The TTS
            // engine now segments outbound text on sentence boundaries
            // internally, so handing it complete sentences gives clean
            // one-utterance-per-sentence synthesis with no fragmenting.
            const sentences = this._splitIntoSentences(questionText);
            console.log(
                `[speakDiagnosticQuestion] Streaming ${sentences.length} sentences via handleLLMDelta...`,
            );

            for (let i = 0; i < sentences.length; i++) {
                try {
                    rtts.handleLLMDelta(sentences[i] + " ");
                } catch (err) {
                    console.warn("[speakDiagnosticQuestion] Delta error:", err);
                }

                if (i < sentences.length - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 20));
                }
            }

            console.log(
                "[speakDiagnosticQuestion] All sentences streamed, calling flushAndClose...",
            );

            // Signal end of text - same as greeting system
            try {
                rtts.flushAndClose();
            } catch (err) {
                console.warn(
                    "[speakDiagnosticQuestion] flushAndClose error:",
                    err,
                );
            }
        } catch (error) {
            console.error("[speakDiagnosticQuestion] Error:", error);
            // Cleanup animation on error
            if (this.animationController) {
                this.animationController.onTTSEnd();
            }
        }
    }

    /**
     * Speak the topic introduction aloud using TTS with lip sync
     * This is used when the user uploads content and the AI explains why topics were chosen
     * Returns a promise that resolves when speech is complete (or estimated time passes)
     */
    async speakTopicIntroduction(introText) {
        if (!introText || !introText.trim()) {
            console.warn("[speakTopicIntroduction] No text provided");
            return;
        }

        console.log(
            "[speakTopicIntroduction] Speaking topic introduction:",
            introText.substring(0, 80) + "...",
        );

        const rtts = this.realtimeTTS;
        const wordCount = introText.split(/\s+/).length;
        const estimatedDurationMs = Math.max(4000, wordCount * 280);

        if (!rtts) {
            console.warn(
                "[speakTopicIntroduction] RealtimeTTS not available - using estimated wait",
            );
            if (this.animationController) {
                this.animationController.onTTSStart();
                await new Promise((resolve) =>
                    setTimeout(resolve, estimatedDurationMs),
                );
                this.animationController.onTTSEnd();
            }
            return;
        }

        return new Promise(async (resolve) => {
            try {
                if (typeof rtts.ensureReadyForNextResponse === "function") {
                    await rtts.ensureReadyForNextResponse(120);
                }
                if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                    await rtts.audioCtx.resume();
                }

                console.log("[speakTopicIntroduction] Starting TTS...");
                rtts.start();

                if (typeof rtts.clearStopGuard === "function") {
                    rtts.clearStopGuard();
                }

                if (this.animationController) {
                    this.animationController.onTTSStart();
                }

                // Task #99: feed WHOLE SENTENCES (engine segments outbound
                // text on sentence boundaries internally).
                const sentences = this._splitIntoSentences(introText);
                console.log(
                    `[speakTopicIntroduction] Streaming ${sentences.length} sentences...`,
                );

                for (let i = 0; i < sentences.length; i++) {
                    try {
                        rtts.handleLLMDelta(sentences[i] + " ");
                    } catch (err) {
                        console.warn(
                            "[speakTopicIntroduction] Delta error:",
                            err,
                        );
                    }

                    if (i < sentences.length - 1) {
                        await new Promise((r) => setTimeout(r, 20));
                    }
                }

                console.log(
                    "[speakTopicIntroduction] Flushing and waiting for completion...",
                );

                try {
                    rtts.flushAndClose();
                } catch (err) {
                    console.warn(
                        "[speakTopicIntroduction] flushAndClose error:",
                        err,
                    );
                }

                // Wait for estimated speech duration
                setTimeout(() => {
                    if (this.animationController) {
                        this.animationController.onTTSEnd();
                    }
                    console.log(
                        "[speakTopicIntroduction] Introduction complete",
                    );
                    resolve();
                }, estimatedDurationMs);
            } catch (error) {
                console.error("[speakTopicIntroduction] Error:", error);
                if (this.animationController) {
                    this.animationController.onTTSEnd();
                }
                resolve();
            }
        });
    }

    /**
     * Handle diagnostic message - route through diagnostic chat manager
     */
    async handleDiagnosticMessage(message) {
        // Show user message
        this.addMessageToChat("", message, "user-message");
        this.chatInput.value = "";

        // Get confidence from slider before submitting
        const confidence = this.diagnosticConfidence / 100; // Convert to 0-1 scale

        // Show loading
        if (this.loadingIndicator) {
            this.loadingIndicator.style.display = "flex";
            setTimeout(() => this.loadingIndicator.classList.add("show"), 10);
        }

        try {
            // Pass confidence along with the answer
            const result = await this.diagnosticChatManager.submitAnswer(
                message,
                confidence,
            );

            // Hide loading
            if (this.loadingIndicator) {
                this.loadingIndicator.classList.remove("show");
                setTimeout(() => {
                    this.loadingIndicator.style.display = "none";
                }, 300);
            }

            if (result.success) {
                // Update progress UI for all responses
                const progress = this.diagnosticChatManager.getProgress();
                this.updateDiagnosticProgressUI(progress);

                if (result.needsClarification) {
                    // AI couldn't understand the answer, ask for clarification
                    console.log(
                        "[ChatController] Answer needs clarification, prompting user to rephrase",
                    );
                    this.addMessageToChat(
                        "",
                        result.clarificationPrompt,
                        "bot-message",
                    );
                } else if (result.isComplete) {
                    // All questions answered - hide confidence slider
                    this.hideDiagnosticConfidenceSlider();
                    this.addMessageToChat(
                        "",
                        result.completionMessage,
                        "bot-message",
                    );

                    // Complete the diagnostic and prepare for evaluation
                    const completeResult =
                        await this.diagnosticChatManager.completeDiagnostic();

                    if (completeResult.success) {
                        // Show completion UI with option to go to evaluation
                        setTimeout(() => {
                            this.showDiagnosticCompletionUI();
                        }, 1500);
                    }
                } else {
                    // Show next question and reset slider
                    this.addMessageToChat(
                        "",
                        result.nextQuestion,
                        "bot-message",
                    );

                    // Speak the next question aloud
                    this.speakDiagnosticQuestion(result.nextQuestion);

                    this.showDiagnosticConfidenceSlider(); // Reset slider for next question
                }
            } else {
                this.addMessageToChat(
                    "",
                    `Sorry, there was an issue processing your answer: ${result.error}. Please try again.`,
                    "bot-message",
                );
            }
        } catch (error) {
            console.error("[ChatController] Diagnostic message error:", error);
            if (this.loadingIndicator) {
                this.loadingIndicator.classList.remove("show");
                this.loadingIndicator.style.display = "none";
            }
            this.addMessageToChat(
                "",
                "Sorry, something went wrong. Please try again.",
                "bot-message",
            );
        }
    }

    /**
     * Show diagnostic completion UI in chat header - minimal single-line
     */
    showDiagnosticCompletionUI() {
        const indicator = document.getElementById(
            "diagnostic-progress-indicator",
        );
        if (indicator) {
            indicator.innerHTML = `
                <div class="diagnostic-header-completion">
                    <i class="fas fa-check-circle"></i>
                    <button class="diagnostic-header-view-results" id="btn-view-results">
                        View Results <i class="fas fa-arrow-right"></i>
                    </button>
                </div>
            `;
            indicator.style.display = "flex";

            // Remove the click handler that exits diagnostic mode
            indicator.onclick = null;

            const viewResultsBtn = document.getElementById("btn-view-results");
            if (viewResultsBtn) {
                viewResultsBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("[ChatController] View Results button clicked");
                    this.restoreChatCloseButton();
                    this.goToEvaluation();
                });
            }
        }
    }

    /**
     * Navigate to Tutor Hub evaluation page
     * Uses the same logic as the Finish button since answers are already synced via ingestChatAnswer
     * For subtopic quizzes, calls submitQuiz() instead of finishDiagnostic()
     */
    goToEvaluation() {
        const returnTo = sessionStorage.getItem("returnToTutorHub");

        // Check if this is a subtopic quiz - use submitQuiz() instead of diagnostic evaluation
        const isSubtopicQuiz =
            this.diagnosticChatManager?.diagnosticTest?.isSubtopicQuiz;

        if (isSubtopicQuiz) {
            console.log(
                "[ChatController] Subtopic quiz detected - calling submitQuiz()",
            );
            sessionStorage.removeItem("returnToTutorHub");

            // Open the embedded tutor hub if needed
            if (typeof window.openTutorMode === "function") {
                window.openTutorMode();
            }

            // Submit the subtopic quiz for grading
            setTimeout(() => {
                if (
                    window.TutorApp &&
                    typeof window.TutorApp.submitQuiz === "function"
                ) {
                    window.TutorApp.submitQuiz();
                } else {
                    console.error(
                        "[ChatController] TutorApp.submitQuiz not available",
                    );
                }
            }, 150);

            return;
        }

        if (returnTo === "embedded") {
            sessionStorage.removeItem("returnToTutorHub");

            // Open the embedded tutor hub
            if (typeof window.openTutorMode === "function") {
                window.openTutorMode();
            }

            // Directly call finishDiagnostic - answers are already synced via ingestChatAnswer
            setTimeout(() => {
                if (window.TutorApp && window.TutorState) {
                    // Ensure diagnostic test is set
                    if (window.TutorState.diagnosticTest) {
                        console.log(
                            "[ChatController] Triggering finishDiagnostic - answers already synced:",
                            {
                                topic: window.TutorState.diagnosticTest.topic,
                                answersCount: Object.keys(
                                    window.TutorState.answers || {},
                                ).length,
                            },
                        );
                        window.TutorApp.finishDiagnostic();
                    } else {
                        console.warn(
                            "[ChatController] No diagnostic test found in TutorState, falling back to redirectToEvaluation",
                        );
                        this.diagnosticChatManager.redirectToEvaluation();
                    }
                } else {
                    console.warn(
                        "[ChatController] TutorApp or TutorState not found",
                    );
                }
            }, 150);
        } else {
            // Standalone tutor hub - use standard redirect
            this.diagnosticChatManager.redirectToEvaluation();
        }
    }

    /**
     * Initialize volume controller for audio responses
     */
    async initializeVolumeController() {
        try {
            // Import VolumeController module
            const { VolumeController } = await import("./VolumeController.js");

            // Create volume controller instance
            this.volumeController = new VolumeController();

            // Make volume controller globally available
            window.volumeController = this.volumeController;

            // Connect volume controller to TTS service
            if (this.tts) {
                this.tts.setVolumeController(this.volumeController);
                console.log("Volume controller connected to TTS service");
            }

            // Connect volume controller to chunked chat integration if available
            this.connectVolumeToChunkedAudio();

            console.log("Volume controller initialized successfully");
        } catch (error) {
            console.error("Failed to initialize volume controller:", error);
        }
    }

    /**
     * Connect volume controller to chunked audio integration with retry logic
     */
    connectVolumeToChunkedAudio() {
        const attemptConnection = (retryCount = 0) => {
            if (window.chunkedChatIntegration) {
                window.chunkedChatIntegration.setVolumeController(
                    this.volumeController,
                );
                console.log(
                    "Volume controller connected to chunked chat integration",
                );
            } else if (retryCount < 3) {
                // Retry after a short delay if chunked integration isn't ready yet
                setTimeout(() => attemptConnection(retryCount + 1), 100);
                console.log(
                    `Retrying chunked audio connection (attempt ${retryCount + 1})`,
                );
            } else {
                console.warn(
                    "Chunked chat integration not available after retries",
                );
            }
        };

        attemptConnection();
    }

    /**
     * Initialize TTS manager - placeholder for future TTS configuration
     * Returns a promise that resolves when TTS is ready (or fails)
     */
    async initializeStreamingTTS() {
        // If already initialized and ready, return immediately
        if (this.realtimeTTS) {
            console.log(
                "[initializeStreamingTTS] TTS already initialized, returning instance",
            );
            return this.realtimeTTS;
        }

        // If initialization is in progress, wait for it
        if (this._ttsBootPromise) {
            console.log(
                "[initializeStreamingTTS] Initialization in progress, waiting for promise...",
            );
            const result = await this._ttsBootPromise;
            console.log(
                "[initializeStreamingTTS] Promise resolved, result:",
                result ? "TTS instance" : "null",
            );
            return result;
        }

        // Start initialization and store the promise
        console.log("[initializeStreamingTTS] Starting new initialization...");
        this._ttsBootPromise = this._doInitializeStreamingTTS();
        const result = await this._ttsBootPromise;
        console.log(
            "[initializeStreamingTTS] New initialization complete, result:",
            result ? "TTS instance" : "null",
        );
        return result;
    }

    /**
     * Internal TTS initialization logic
     */
    async _doInitializeStreamingTTS() {
        console.log(
            "[ChatController] TTS system ready - using global TTS manager",
        );

        try {
            const { RealtimeLLMToElevenLabs } = await import(
                "./RealtimeLLMToElevenLabs.js"
            );
            // If mountTTSSelfContainedUI is in another module, import it there and keep this call.

            const elevenLabsApiKey = window.elevenLabsApiKey;
            if (!elevenLabsApiKey) {
                console.warn(
                    "[RealtimeTTS] ElevenLabs API key not found - realtime TTS disabled",
                );
                this.realtimeTTS = null;
                this._ttsBootPromise = null; // allow retry later
                return null;
            }

            const currentVoiceId = this.getCurrentCharacterVoiceId();

            // Create the realtime TTS instance with preemption + gating tuned
            this.realtimeTTS = new RealtimeLLMToElevenLabs({
                voiceId: currentVoiceId,
                elevenLabsApiKey,
                modelId: "eleven_flash_v2_5",
                outputFormat: "pcm_16000",
                syncAlignment: true,

                // Task #99: outbound phrasing is segmented on sentence
                // boundaries inside the engine (no token-count / timeout
                // knobs). Defaults for firstPhraseMaxChars (90) and
                // phraseSafetyCapChars (320) are used.
                jitterMs: 160,

                // preemption + lifecycle
                autoPreempt: true, // <<< lets new streams preempt current speech
                newResponseIdleMs: 1200, // watches for “fresh stream” intent
                inactivityTimeoutSec: 180, // keep WS warm without churn

                // stop-button gating
                tokenIdleMs: 2000, // Stop shows after 2s w/ no tokens while audio is active

                // volume defaults — phones have no in-app volume control (it is
                // hidden on mobile), so run at full app volume and let the device's
                // hardware buttons set loudness; desktop keeps the adjustable 0.6.
                volume: (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 760px)").matches) ? 1.0 : 0.6,
                muted: false,

                // lip-sync defaults (safe; will do nothing if you don't enableLipSync)
                lipSyncOpts: {
                    autoCalibrate: true,
                    fftSize: 1024,
                    smoothing: 0.6,
                },
            });

            // Start the WS+AudioContext once (prewarms for low-latency first audio)
            await this.realtimeTTS.start();

            // Mount ONE overlay (you had two; keep a single mount)
            const overlay = mountTTSSelfContainedUI(this.realtimeTTS, {
                position: "bottom-left", // pick one: "bottom-right" or "bottom-left"
                padding: 22,
                accentColor: "#ff4d57",
                accentSoft: "#ff7a82",
                glassBg: "rgba(16,16,20,0.55)",
            });
            console.log("[TTS UI] mounted api:", overlay);

            // Phones: force full app volume so the device's hardware buttons are the
            // only loudness control (the in-app volume pill is hidden on mobile).
            try {
                if (window.matchMedia && window.matchMedia("(max-width: 760px)").matches) {
                    this.realtimeTTS.setVolume(1.0);
                }
            } catch (_) {}

            // Optional: if your initializeRealtimeLipSync() creates a controller, wire it here
            // (If it internally calls enableLipSync, you can skip this.)
            try {
                this.initializeRealtimeLipSync?.();
            } catch (e) {
                console.warn("[LipSync] init skipped:", e);
            }

            // Useful UI hooks
            this.realtimeTTS.on("audio-start", () => {
                // e.g., dim mic / show “speaking” glow
                document.documentElement.classList.add("tts-speaking");
            });
            this.realtimeTTS.on("audio-end", () => {
                document.documentElement.classList.remove("tts-speaking");
            });
            this.realtimeTTS.on("stop-visibility", (e) => {
                const show = !!e.detail?.visible;
                document
                    .querySelector("#tts-stop")
                    ?.classList.toggle("hidden", !show);
            });
            this.realtimeTTS.on("volume-change", (e) => {
                const v = e.detail?.volume ?? 0;
                const slider = document.querySelector("#tts-volume");
                if (slider) slider.value = String(v);
            });

            // Expose for debugging
            window.tts = this.realtimeTTS;

            console.log(
                `[RealtimeTTS] Initialized with voice: ${currentVoiceId}`,
            );
            return this.realtimeTTS;
        } catch (error) {
            console.error(
                "[RealtimeTTS] Failed to initialize realtime TTS:",
                error,
            );
            this.realtimeTTS = null;
            this._ttsBootPromise = null; // allow retry
            return null;
        }
    }

    /**
     * Initialize real-time lip sync for the RealtimeLLMToElevenLabs instance
     */
    initializeRealtimeLipSync(retryCount = 0) {
        const maxRetries = 3;
        const retryDelayMs = 500;

        // Cancel any pending retry to prevent overlapping retries
        if (this._lipSyncRetryTimer) {
            clearTimeout(this._lipSyncRetryTimer);
            this._lipSyncRetryTimer = null;
        }

        if (!this.realtimeTTS) {
            console.warn(
                "[RealtimeTTS] Cannot initialize lip sync - RealtimeTTS not available",
            );
            return false;
        }

        // Check if we have access to model blend shapes (must have actual shapes, not just empty object)
        const blendShapeCount = this.modelViewer?.blendShapeControls
            ? Object.keys(this.modelViewer.blendShapeControls).length
            : 0;

        if (
            this.modelViewer &&
            this.modelViewer.blendShapeControls &&
            blendShapeCount > 0
        ) {
            console.log(
                "[RealtimeTTS] Initializing lip sync with",
                blendShapeCount,
                "blend shape controls",
            );

            // Create minimal LipSyncController with the blend shape controls
            const lipSyncController = new MinimalLipSyncController({
                blendShapeControls: this.modelViewer.blendShapeControls,
                debug: true,
            });

            const currentModelId = this.getCurrentModelId();

            this.realtimeTTS.enableLipSync(
                lipSyncController,
                {
                    debug: true,
                    frameMs: 33,
                    minRms: 0.015,
                    hissRatio: 1.35,
                    intensityGain: 1.1,
                },
                currentModelId,
            );

            console.log("[RealtimeTTS] Lip sync initialized successfully");
            this._lipSyncInitialized = true;
            return true;
        } else {
            console.warn(
                "[RealtimeTTS] Cannot initialize lip sync - blend shape controls not available or empty",
                `(attempt ${retryCount + 1}/${maxRetries + 1})`,
            );
            console.log("[RealtimeTTS] Available on modelViewer:", {
                modelViewer: !!this.modelViewer,
                blendShapeControls: !!(
                    this.modelViewer && this.modelViewer.blendShapeControls
                ),
                blendShapeCount: blendShapeCount,
            });

            // Schedule retry if we haven't exceeded max retries
            if (retryCount < maxRetries) {
                console.log(
                    `[RealtimeTTS] Scheduling lip sync retry in ${retryDelayMs}ms...`,
                );
                this._lipSyncRetryTimer = setTimeout(() => {
                    this._lipSyncRetryTimer = null;
                    this.initializeRealtimeLipSync(retryCount + 1);
                }, retryDelayMs);
            } else {
                console.warn(
                    "[RealtimeTTS] Max retries exceeded for lip sync initialization",
                );
            }
            return false;
        }
    }

    /**
     * Get current character ID from the model viewer
     */
    getCurrentCharacterId() {
        if (this.modelViewer && this.modelViewer.modelManager) {
            return this.modelViewer.modelManager.getCurrentModelId();
        }
        return null;
    }

    /**
     * Get current character's ElevenLabs voice ID
     */
    getCurrentCharacterVoiceId() {
        // Voice ID mappings for each character (matching backend tts_service.py)
        const voiceIdMap = {
            "bc5d31a8-047b-4f72-8f16-fea7a365ca69": "fBOMlsQwDZ1UMIVIHGU2", // Richard Feynman
            "88c314c4-5890-4e55-9418-8b46a376ef81a": "nP0opz5wvlz83TcFLppE", // Friedrich Nietzsche
            "01db85dc-81e0-4433-9440-a54ce4976178": "tTElKaMHvQc7yODvnBjy", // Erwin Schrodinger
            "2db1d398-0161-46cd-8aff-53008d29a9dc": "zziaBkXNQyJTdgtfnXKW", // Claude Shannon
            "c2cc71ce-89e7-4c94-9441-eec86ec67a60": "P8hcp8sN3uZUb0jyelE3", // Napoleon Bonaparte
            "0c6099ed-c0db-4dd7-bc46-1d720324db5a": "stppusVy9JAmYRdYM300", // Michael Faraday
            "1a35136c-42d8-46c7-a189-c4b32da87832": "zgUAs3KILGnxkPJ0cQoO", // John Neumann
            "be0dcf0f-c72b-48f7-a462-6ba7560498d7": "h014l2uTMd4vJsJtW1iw", // Alan Turing
            "d537d61d-0313-421b-ae66-48a8d7222b3f": "oINDLv2BI1AXPjmJ1KUb", // Nikola Tesla
            "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": "v8SXEF0gWMhDWCLkXAZZ", // René Descartes
            "5eb1ad8d-4c11-4174-b79a-c232991d015b": "XLeEY7IVeyP5oq3rlHX0", // Oppenheimer
            "7021ffd6-51bb-41a8-920e-935899dcd631": "dQSvZ7o2kHzOxnalxXCI", // Isaac Newton
            "93ea7051-9722-443d-90fb-18a84c51894c": "AFgNVhRp9HyRqdTRCC1L", // James Clerk Maxwell
            "b20dc26f-8924-4aee-9ef5-9b98c0367a34": "GIjERyEtqJR1HHATpwkc", // Ernest Hemingway
            "897eb00c-4f16-4f73-81d5-d1c91fad53f2": "kpvc2UA9Nm7qZsMf9q1b", // Charles Darwin
            "73f610bf-d9c9-4d9f-a026-a10086a66725": "tHTI2qB5SvRdGAzCpaQF", // Marie Curie
            "802bd089-d231-4c4e-830c-476199321af8": "wmNy3H4pgGksDO4tuY9p", // Niels Bohr
            "992e9007-b4f8-4711-80c2-884daf3d9960": "01oKE7LSV70nVMEJVFuM", // Van Gogh
            "770e2d93-a6a4-41ed-aca1-8951327885b5": "qGL698ewyT1J4ZiE4JwA", // Amelia Earhart
            "5a16ff56-6e03-414e-a89d-cda9d83fae60": "qef0vOC0TqFzilWc53v5", // Henry Ford
            "031f2eea-64e5-441d-a37d-03d260cc43dc": "ZnVEvKvqmtCLPWIsFiBu", // ABRAHAM LINCOLN
            "a59fe2c1-9046-405a-9a99-a7ddbaee13be": "xQXBNLTJU1JT7nDRU9mq", // Adam Smith
            "e3f55c33-3ff0-471f-b833-b59d7b145fc6": "qtMlELIatSryE6JXsfxe", // Al Capone

            "c4b9ac58-a66a-451e-b035-afabd8be50b9": "bTVJSD9NwVe7jUwp1QPX", // Aristotle
            "b8951237-698b-42b5-8b6e-ee381b91fb65": "0M45erIgq3juLAfoib4O", // Confucius
            "d82bc58b-5487-4af2-ad5e-fa9fa838e239": "kpvc2UA9Nm7qZsMf9q1b", // Darwin
            "8944783c-2e2b-4796-b92e-21dacca776bc": "fBOMlsQwDZ1UMIVIHGU2", // Davinci
            "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": "j6v16VCPrtFh06eKROgy", // Fermi
            "4d7d2e17-b818-4144-b78b-629c688a71ac": "tUGS6AkOqpB4R8nb1DTU", // Karl Marx
            "2baa0479-7e38-4473-a2f2-1ad54149dbac": "h3Xd6u9pEpXslweyLak1", // Muhammad Ali (live model)
            "b5b393e2-4d62-4853-8f03-5d24a2ecfb1e": "h3Xd6u9pEpXslweyLak1", // Muhammad Ali (legacy)
            "3f14429d-b2d4-4b99-8a05-58eed567c0d3": "LFI377Wp1VfhSRFROwrE", // Mansa Musa
            "e97568c5-1daa-4a95-b4ae-0fe374c6b083": "KUlh7yZHyCk7Z4JROkMq", // Marcus Aurelius
            "d0c97034-328e-4538-8107-a9825d0c1594": "QziZo9xe7ek9mbwzU7Hz", // Martin Luther King
            "7716e2f6-eb48-424d-a962-252b15ca634e": "dQSvZ7o2kHzOxnalxXCI", // Newton
            "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": "Husk9qCepRSVW8POHRgV", // Niccolo Machiavelli
            "8ded3849-e5ad-4dea-9bf5-3d8f912e29cf": "XLeEY7IVeyP5oq3rlHX0", // Oppenheimer
            "59c67a42-8c1e-45b8-aa41-b6fe6b3f5629": "R30XqI00ZaAj5iZcqo3T", // George Orwell
            "3542f19e-4e07-4828-8f9d-f330e4d6d660": "lzjJhkcDy1p6gzkO6cyv", // Plato
            "d75e63e0-2b2b-43ef-933f-589e8d6818c0": "tOFMyBIoMjbnLlmC8PXk", // Salvador Dali
            "d9f9af52-ae93-4bd2-abd4-0b7032844ddd": "oINDLv2BI1AXPjmJ1KUb", // Nikola Tesla
            "7c7ac397-c36e-4218-92d4-f437d1959cca": "PPIyCgVckEITBdJieiEF", // Euclid
            "f9f3f836-00c6-4160-8416-4b71b53f1307": "S4jJ0qvmkTvB4V1Qg8pM", // Srinivasa Ramanujan
            "5802e813-e715-491d-a0fd-92dc643c458e": "FkpMZSHFazvxuHjJPNWt", // Blaise Pascal
            "191e6103-a42c-4132-926c-230e7b1be5b6": "QKH9ENIBjn2DSyB4NLeX", // Jacob Bernoulli
            "6bcf3678-e232-46c9-996a-67beb3fd2dbf": "YOqedYwwu9Ysg49YDGYo", // Al-Khwarizmi
            "14836d13-ace6-4bf8-be26-26fbb80ed9bc": "brghYKx0AFLUift1arLh", // Socrates
            "3841a6bb-f977-453d-92f5-6cec1267546f": "0tKQyj9V8fi0dzKVh5Gx", // Sigmund Freud
            "c0063a05-916b-45d0-bc2c-c63210414b17": "fCrFEvscs3U2ic8ZG8Rr", // Ada Lovelace
            "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": "0ukJevMLQRTM99kxgYIT", // Carl Friedrich Gauss
            "127cd993-f16e-4659-84fa-882cba633097": "8LffvtWcjAU7m7pWc0Qd", // Galileo Galilei
            "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": "J2GvvoAMRTkCCLLXntDf", // Grace Hopper
            "c93478a1-ed2f-4f1a-81f6-3fa474a21566": "gHZK1r5cQDEhfehqh7pd", // Joseph-Louis Lagrange
            "5a39bed2-a551-4a03-98a2-46104a3324bf": "Wq6w5E2vc4OqPiNdmWeS", // Leonhard Euler
            "2707ca40-df64-4258-97c8-67d3695e8a42": "WNtlicQwQWd6emj75dSp", // Brahmagupta
            "e617290b-bda0-4771-91ac-3fe6939dd412": "BKmWvfLNEG96H76vdNiT", // Benjamin Franklin
            "a2a43496-9a83-4444-813e-18a1930296e6": "pJuw4iR7QH5JB6e26XO2", // Omar Khayyam
            "78376372-3d71-4fce-8650-82b5be2c006b": "vYNDkOWzdSDQUfdwCXw0", // Diophantus
            "ca6ebdca-5151-4a58-8523-56d25fce5dfa": "OTv0JS7R4v7m34HckjVr", // William Playfair
            "e3428624-bd1e-48b9-ae8c-c66ba68879cc": "rdAitqoRRLg6BcvxRfO9", // George Boole
            "3140d634-c7ca-4bda-af36-e1b49978b201": "rGB16Q5v9fLOvySGLzhb", // Thomas Bayes
            "c73fed54-81e3-46ec-9d7c-3d11d05b142d": "ZJkaRDk9HuFRIuZJB5Nz", // Eratosthenes
            "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": "Clkb0EgLjIRH27sZsH49", // Alessandro Volta
            "d13b5a81-ed77-4a25-8a01-a76a0165b349": "WsCWAjLgFpcsEWH1RGC6", // Archimedes
            "8f67d579-41c2-456d-900b-fccbcbf46bd3": "jmFKixMxkiESGgPTyPpW", // François Viète
            "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": "VICVAPcUBWXSpVAHu4Dy", // Felix Klein
            "44f87c0b-ca22-4225-9fea-8d823e6a0160": "WL6RkqUFdiy9uiTlyVd5", // Warren Buffett
            "581e7293-43cf-45ea-9fb3-0955c563cbe2": "t6bYwKcgjj0l5LxqYSDJ", // Genghis Khan
            "278dddd6-b4a8-4303-a41d-be6219dbc907": "NlcJ6gx2xZ25h12rUyq7", // Albert Einstein
            "4f50a698-6034-49c1-b9d4-221f59bf2378": "0usCDMt9MmbqzEgFEMlP", // Frida Kahlo
            "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": "dM5I3tskULdOgmbSBDmR", // Nostradamus
        };

        const currentCharacterId = this.getCurrentCharacterId();

        // Voice-source control plane: a live server-side override (injected
        // at page render, refreshed on character change) beats the shipped
        // map above — lets the founder swap a voice with zero redeploy.
        const overrideVoiceId = (window.__voiceOverrides || {})[
            currentCharacterId
        ];
        if (overrideVoiceId) {
            console.log(
                `[RealtimeTTS] Using OVERRIDE voice ID ${overrideVoiceId} for character ${currentCharacterId}`,
            );
            return overrideVoiceId;
        }

        const voiceId = voiceIdMap[currentCharacterId];

        if (voiceId) {
            console.log(
                `[RealtimeTTS] Using voice ID ${voiceId} for character ${currentCharacterId}`,
            );
            return voiceId;
        } else {
            // Fallback to default voice
            console.warn(
                `[RealtimeTTS] No voice ID found for character ${currentCharacterId}, using default`,
            );
            return "fBOMlsQwDZ1UMIVIHGU2"; // Default to Feynman's voice
        }
    }

    /**
     * Enable realtime TTS mode
     */
    enableRealtimeTTS() {
        this.useRealtimeTTS = true;
        console.log("[ChatController] Realtime TTS enabled");
    }

    /**
     * Disable realtime TTS mode
     */
    disableRealtimeTTS() {
        this.useRealtimeTTS = false;
        console.log("[ChatController] Realtime TTS disabled");
    }

    /**
     * Handle character change for streaming TTS
     */
    async onCharacterChanged(characterId) {
        console.log(`[ChatController] Character changed to: ${characterId}`);

        // Voice-source control plane: refetch the effective voice so a live
        // swap (legendctl voice set) lands without a page reload. Fire and
        // forget — the sync lookup below uses the last-known map meanwhile.
        this.refreshVoiceOverride(characterId);

        // Update realtime TTS voice if active
        if (this.realtimeTTS) {
            const newVoiceId = this.getCurrentCharacterVoiceId();
            this.realtimeTTS.setVoiceId(newVoiceId);
            console.log(
                `[RealtimeTTS] Voice updated to ${newVoiceId} for character change`,
            );
        }

        // Update TTS manager for traditional TTS if needed
        if (this.ttsManager) {
            console.log(
                `[ChatController] TTS manager updated for character: ${characterId}`,
            );
        }
    }

    /**
     * Voice-source control plane: fetch the effective voice id for a
     * character from the server and update the local override map (and the
     * live realtime engine, between utterances). Fails open — the shipped
     * voiceIdMap keeps speaking if the fetch dies.
     */
    async refreshVoiceOverride(characterId) {
        if (!characterId) return;
        try {
            const r = await fetch(
                `/api/voice-config/${encodeURIComponent(characterId)}`,
                { cache: "no-store" },
            );
            if (!r.ok) return;
            const d = await r.json();
            window.__voiceOverrides = window.__voiceOverrides || {};
            if (d && d.source === "override" && d.voiceId) {
                window.__voiceOverrides[characterId] = d.voiceId;
            } else {
                delete window.__voiceOverrides[characterId];
            }
            // SACRED AUDIO RULE: never mutate a SPEAKING engine. If speech is
            // active when this response lands, leave the engine alone — the
            // updated window.__voiceOverrides map applies on the next
            // character change / engine boot (the documented propagation).
            if (
                this.realtimeTTS &&
                !window.TTSActive &&
                this.getCurrentCharacterId() === characterId
            ) {
                const effective = this.getCurrentCharacterVoiceId();
                if (effective && this.realtimeTTS.voiceId !== effective) {
                    this.realtimeTTS.setVoiceId(effective);
                    console.log(
                        `[RealtimeTTS] Voice refreshed to ${effective} (voice control plane)`,
                    );
                }
            }
        } catch (e) {
            // fail open — never let a config fetch disturb the audio path
        }
    }

    /**
     * Update streaming TTS when character changes
     */
    async updateStreamingTTSCharacter() {
        const currentCharacterId = this.getCurrentCharacterId();
        if (currentCharacterId) {
            await this.onCharacterChanged(currentCharacterId);
        }
    }

    // Old realtime TTS controller method removed - using global TTS manager

    /**
     * Handle audio playback start events
     */
    onAudioPlaybackStart() {
        // Emotion controller removed in Task #38 — A2F renderer handles
        // facial expression on its own.

        // Show stop button and manage UI state
        if (this.stopAudioButton) {
            this.stopAudioButton.classList.remove("d-none");
        }
        if (this.sendButton) {
            this.sendButton.classList.remove("d-none");
        }

    }

    /**
     * Handle audio playback end events
     */
    onAudioPlaybackEnd() {
        // Reset UI state
        if (this.stopAudioButton) {
            this.stopAudioButton.classList.add("d-none");
        }
        if (this.sendButton) {
            this.sendButton.classList.remove("d-none");
        }


        // Stored-emotion replay removed in Task #38 — A2F renderer
        // decays back to neutral on its own after silence.
        this.currentEmotion = null;
    }

    /**
     * Hide loading indicator with smooth fade-out effect
     */
    hideLoadingIndicator() {
        if (this.loadingIndicator) {
            this.loadingIndicator.classList.remove("show");
            setTimeout(() => {
                this.loadingIndicator.style.display = "none";
            }, 300);
        }

        // Stop dynamic loading text
        this.stopLoadingTextAnimation();
    }

    /**
     * Start dynamic loading text animation
     */
    startLoadingTextAnimation() {
        const loadingMessages = [
            "AI is thinking",
            "Analyzing your message",
            "Generating response",
            "Processing thoughts",
            "Crafting answer",
        ];

        let currentIndex = 0;
        const textElement =
            this.loadingIndicator.querySelector(".typing-animation");

        if (textElement) {
            this.loadingTextInterval = setInterval(() => {
                textElement.textContent = loadingMessages[currentIndex];
                currentIndex = (currentIndex + 1) % loadingMessages.length;
            }, 1500);
        }
    }

    /**
     * Stop dynamic loading text animation
     */
    stopLoadingTextAnimation() {
        if (this.loadingTextInterval) {
            clearInterval(this.loadingTextInterval);
            this.loadingTextInterval = null;
        }
    }

    enableChat() {
        if (this.isEnabled) return;

        if (this.chatInput) {
            this.chatInput.disabled = false;
            this.chatInput.placeholder = "Type your message...";
        }

        if (this.sendButton) {
            this.sendButton.disabled = false;
        }

        if (this.micButton) {
            this.micButton.disabled = false;
        }

        this.isEnabled = true;
        console.log("Chat interface enabled");
    }

    disableChat() {
        if (!this.isEnabled) return;

        if (this.chatInput) {
            this.chatInput.disabled = true;
            this.chatInput.placeholder = "Loading...";
        }

        if (this.sendButton) {
            this.sendButton.disabled = true;
        }

        if (this.micButton) {
            this.micButton.disabled = true;
        }

        this.isEnabled = false;
        console.log("Chat interface disabled");
    }

    /**
     * Process a pending message after first-time popup is handled
     */
    processPendingMessage() {
        if (this.pendingMessage) {
            console.log(
                "Processing pending message after popup handled:",
                this.pendingMessage,
            );
            const message = this.pendingMessage;
            this.pendingMessage = null; // Clear the pending message
            this.sendMessage(message, true); // Skip interruption check for pending message
        }
    }

    // ── Anonymous free-taste helpers ──
    // Cold visitors are signed in anonymously so the greeting + a short free
    // taste work with no wall. The taste counter is keyed on the anon uid so it
    // survives a refresh (the anon uid persists) but resets if Firebase issues a
    // new anonymous identity (the server cap still backstops abuse).
    _isAnonUser() {
        const u = window.firebaseAuth?.currentUser;
        return !!(u && u.isAnonymous === true);
    }

    _anonTasteKey() {
        const u = window.firebaseAuth?.currentUser;
        return "ll_anon_taste_" + (u?.uid || "anon");
    }

    _anonTasteUsed() {
        try {
            return parseInt(localStorage.getItem(this._anonTasteKey()) || "0", 10) || 0;
        } catch (e) {
            return 0;
        }
    }

    _incrementAnonTaste() {
        try {
            const n = this._anonTasteUsed() + 1;
            localStorage.setItem(this._anonTasteKey(), String(n));
            return n;
        } catch (e) {
            return 0;
        }
    }

    // Ensure SOME Firebase identity exists. Returns the current user (anonymous
    // or real), signing in anonymously if there's no user yet. Returns null only
    // when even anonymous sign-in is impossible (then the caller shows the modal).
    async _ensureAnonymousAuth() {
        try {
            const a = window.firebaseAuth;
            if (a?.currentUser) return a.currentUser;
            const fn = window.firebaseModules?.signInAnonymously;
            if (a && typeof fn === "function") {
                const res = await fn(a);
                return res?.user || a.currentUser || null;
            }
        } catch (e) {
            console.warn("[ChatController] anonymous sign-in failed", e);
        }
        return window.firebaseAuth?.currentUser || null;
    }

    async sendMessage(message, skipInterruption = false, messageType = null) {
        // Task #530 — sending while dictating = implicit accept: finalize the
        // dictation first so SR can't keep streaming into a cleared field.
        if (this.micMode === "dictate") {
            this.stopDictation({
                keepText: true,
                refocus: false,
                reason: "send-during-dictation",
            });
        }

        if (!message || !this.isEnabled) return;

        console.log("📤 Sending message:", message, "Type:", messageType);

        // Check if diagnostic mode is active - route through diagnostic handler
        if (
            this.diagnosticChatManager &&
            this.diagnosticChatManager.isDiagnosticActive()
        ) {
            console.log(
                "[ChatController] Diagnostic mode active - routing through diagnostic handler",
            );
            await this.handleDiagnosticMessage(message);
            return;
        }

        // ── Authentication / free-taste gate ──
        // Cold visitors are signed in ANONYMOUSLY (on /lab load) so the greeting
        // + a short free taste of the conversation work with no wall. We only
        // fall back to the sign-in modal when even anonymous sign-in is
        // impossible.
        let currentUser = window.firebaseAuth?.currentUser || null;
        if (!currentUser) {
            currentUser = await this._ensureAnonymousAuth();
        }

        if (!currentUser) {
            console.log("No identity available - showing sign-in modal");

            // Store the message to be processed after successful sign-in
            this.pendingMessage = message;
            console.log("Message queued for after sign-in:", message);

            // Show the authentication modal
            if (window.authController) {
                window.authController.showAuthModal();
            } else if (
                typeof window.showNewUiAuthModal === "function" &&
                window.showNewUiAuthModal()
            ) {
                // authController not ready yet (3D scene still loading / failed):
                // open the new-UI (Tuscan-dusk) login modal directly so the
                // signed-out user never sees the legacy modal.
            } else {
                // Last resort: open the new-UI modal markup directly.
                const modal = document.getElementById("modal-auth");
                if (modal) {
                    modal.classList.add("is-open");
                }
            }

            return; // Don't process the message until user signs in
        }

        const userId = currentUser.uid;
        const isAnon = currentUser.isAnonymous === true;

        if (isAnon) {
            // ── Sign-up wall (anonymous visitors) ──
            // With ANON_FREE_EXCHANGES = 0 the wall fires on the VERY FIRST send:
            // a signed-out visitor gets zero free typed exchanges (the greeting is
            // separate and NEVER counted, so it still plays on arrival). We raise
            // the standard COLD sign-up window — not the soft "keep going"
            // continuation copy — preserving the typed message so it sends the
            // instant they sign in. Anonymous users skip the Firestore counter.
            const tasteUsed = this._anonTasteUsed();
            if (tasteUsed >= ANON_FREE_EXCHANGES) {
                this.pendingMessage = message;
                try {
                    window.trackEvent &&
                        window.trackEvent("wall_shown", {
                            taste_used: tasteUsed,
                            source: "client",
                        });
                } catch (e) {}
                if (window.authController) {
                    window.authController.showAuthModal();
                } else if (typeof window.showNewUiAuthModal === "function") {
                    window.showNewUiAuthModal();
                }
                return;
            }

            const nowUsed = this._incrementAnonTaste();
            try {
                if (nowUsed === 1) {
                    window.trackEvent &&
                        window.trackEvent("first_message_sent", { anon: true });
                }
                if (nowUsed >= ANON_FREE_EXCHANGES) {
                    window.trackEvent &&
                        window.trackEvent("taste_complete", {
                            count: nowUsed,
                        });
                }
            } catch (e) {}
        } else {
            // ── Signed-in user: Firestore question counter ──
            // Skip initialization check - it should already be done on auth or page load
            // Just check if count exists, but don't block on loading it
            if (this.questionCounter.cachedCount === null) {
                console.warn(
                    "[ChatController] Question counter not pre-initialized, loading now (this adds delay!)",
                );
                // This should rarely happen now that we pre-initialize
                try {
                    await this.questionCounter.initializeForUser(userId);
                    console.log(
                        "[ChatController] Question counter emergency init done:",
                        this.questionCounter.cachedCount,
                    );
                } catch (initError) {
                    console.error(
                        "[ChatController] Failed to initialize question counter:",
                        initError,
                    );
                    alert(
                        "Failed to load question counter. Please refresh the page.",
                    );
                    return;
                }
            }

            // FAST LOCAL CHECK - no Firebase delay!
            let hasQuota = this.questionCounter.fastLocalCheck();

            if (!hasQuota) {
                // The user looks out of quota — but a paid subscriber must NEVER be
                // blocked. Their subscription status may not have resolved yet (the
                // checkers run async and the status event is fire-and-forget). Make
                // an authoritative check before blocking; if they're subscribed,
                // bypass the count entirely.
                const subscribed =
                    await this.questionCounter.confirmSubscriptionBeforeBlock();
                if (subscribed) {
                    hasQuota = true;
                }
            }

            if (!hasQuota) {
                alert("No questions remaining. Your question count is 0.");
                return;
            }

            // Immediate UI update with new count
            window.dispatchEvent(
                new CustomEvent("questionCountChanged", {
                    detail: { count: this.questionCounter.cachedCount },
                }),
            );
            console.log(
                `[ChatController] Fast local check passed. Count updated to: ${this.questionCounter.cachedCount}`,
            );
        }

        // Firebase sync will happen in background after message is sent (non-blocking)

        // Only check for interruption if not explicitly skipped (e.g., after stop button or first interruption)
        if (
            !skipInterruption &&
            (this.realtimeTTS?.isSpeaking?.() ||
                this.isAudioPlaying ||
                this.isProcessing)
        ) {
            console.log("🔊 Audio currently playing - handling interruption");
            this.handleUserInterruption();
            await this.prepTTSForNewTurn();
            console.log("✅ TTS prepared for new response after interruption");
        } else {
            // Even if no audio is playing, ensure greeting is stopped
            if (this.modelViewer && this.modelViewer.greetingService) {
                this.modelViewer.greetingService.stop();
                console.log(
                    "🛑 Stopped any active greeting due to user message input",
                );
            }
        }

        // Show chat container when sending a message
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }

        // Play listening animation when user asks a question
        if (this.animationController && !this.isAudioPlaying) {
            this.animationController.playListenAnimation();
            console.log("👂 Playing listening animation as user asks question");
        }

        // Set processing flag to keep chat window visible during API calls
        this.isProcessing = true;
        console.log(
            "Message processing started - chat window will stay visible",
        );

        this.addMessageToChat("", message, "user-message");

        // Handle interruption: If there's a streaming response in progress, capture partial answer
        let interruptionContext = null;
        if (this.streamingController && this.currentStreamingMessage) {
            const partialResponse = this.getPartialStreamingContent();
            if (partialResponse && partialResponse.trim().length > 0) {
                interruptionContext = {
                    partial_response: partialResponse,
                    interrupted_at: new Date().toISOString(),
                };
                console.log(
                    "🔄 Interruption detected - preserving partial response:",
                    partialResponse.substring(0, 100) + "...",
                );

                // Add the partial response to conversation history before the new message
                this.addToConversationHistory(
                    "assistant",
                    `[Interrupted mid-response] ${partialResponse}`,
                );
            }
        }

        // Add user message to conversation history
        this.addToConversationHistory("user", message);

        this.chatInput.value = "";

        // Trigger input height adjustment after clearing
        const inputEvent = new Event("input", { bubbles: true });
        this.chatInput.dispatchEvent(inputEvent);

        this.loadingIndicator.style.display = "flex";
        setTimeout(() => {
            this.loadingIndicator.classList.add("show");
        }, 10);

        // Start dynamic loading text
        this.startLoadingTextAnimation();

        // Don't restart microphone immediately after message sent
        // The microphone will only restart after AI finishes speaking to prevent echo
        console.log(
            "Q&A Mode: Microphone will restart after AI completes speaking",
        );

        try {
            // Use streaming API for real-time response
            const streamingResponse = await this.sendStreamingMessage(
                message,
                messageType,
            );

            if (streamingResponse.status === "success") {
                // For streaming, the message is already added during the streaming process
                // We just need to update the current bot message reference
                this.currentBotMessage = this.currentStreamingMessage;
                this.lastAudioResponse = streamingResponse.response;

                // Add assistant's response to conversation history
                this.addToConversationHistory(
                    "assistant",
                    streamingResponse.response,
                );

                // ASYNC FIREBASE SYNC - happens in background, doesn't block anything.
                // Anonymous (free-taste) users don't use the Firestore question
                // counter, so skip the decrement for them.
                if (!isAnon) {
                    this.questionCounter
                        .syncDecrementToFirebase(userId)
                        .then(() => {
                            console.log(
                                "[ChatController] Firebase sync completed in background",
                            );
                        })
                        .catch((error) => {
                            console.error(
                                "[ChatController] Firebase sync failed (non-blocking):",
                                error,
                            );
                            // Don't interrupt user - the message was already sent successfully
                        });
                }

                // Use the streaming response data for the rest of the processing
                const data = streamingResponse;

                // Store chat data to Firestore if user is authenticated
                try {
                    const characterName = this.getCurrentCharacterName();
                    console.log(
                        "ChatController: Attempting to store chat - Character:",
                        characterName,
                    );

                    if (characterName && this.chatStorageManager) {
                        const finalTone =
                            data.tone && data.tone !== "analyzing"
                                ? data.tone
                                : null;
                        console.log(
                            "ChatController: Calling storeChatMessage with tone:",
                            finalTone,
                        );

                        const chatDocId =
                            await this.chatStorageManager.storeChatMessage(
                                characterName,
                                message,
                                data.response,
                                finalTone,
                            );
                        if (chatDocId) {
                            console.log(
                                "ChatController: ✅ Chat message stored to Firestore with ID:",
                                chatDocId,
                            );
                        } else {
                            console.log(
                                "ChatController: ⚠️ Chat storage returned null - user may not be authenticated",
                            );
                        }
                    } else {
                        console.log(
                            "ChatController: ⚠️ Cannot store chat - missing character name or storage manager",
                        );
                    }
                } catch (storageError) {
                    console.error(
                        "ChatController: ❌ Failed to store chat message:",
                        storageError,
                    );
                }

                // Log keyword images if provided (they're now embedded in the bot message)
                if (data.keyword_images && data.keyword_images.length > 0) {
                    console.log(
                        "=== KEYWORD IMAGES EMBEDDED IN BOT MESSAGE ===",
                    );
                    console.log(
                        "Number of keyword results:",
                        data.keyword_images.length,
                    );

                    // Process keyword images for the image mode viewer only
                    this.processKeywordImages(data.keyword_images);

                    // Show image notification dot if not currently in image mode
                    /*if (!this.imageModeBtn.classList.contains("active")) {
                        this.showImageNotification();
                    }*/

                    // Log image details for debugging
                    data.keyword_images.forEach((result, index) => {
                        const keyword = result.fetched_keyword || "unknown";
                        const imageCount = result.images
                            ? result.images.length
                            : 0;
                        console.log(
                            `Keyword "${keyword}": ${imageCount} images embedded`,
                        );
                    });
                } else {
                    console.log("No keyword images received");
                }

                // Handle video data if available
                if (
                    data.video_data &&
                    data.video_data.has_videos &&
                    data.video_data.videos &&
                    data.video_data.videos.length > 0
                ) {
                    console.log(
                        "Processing videos from regular chat response:",
                        data.video_data.videos,
                    );
                    this.videoController.processVideoData(data.video_data);

                    // Show video notification dot if not currently in video mode.
                    // The mode buttons were removed in the Tuscan-dusk merge, so
                    // null-guard before touching them (Task #57) — otherwise this
                    // throws and silently aborts the rest of media handling.
                    if (
                        this.videoModeBtn &&
                        !this.videoModeBtn.classList.contains("active")
                    ) {
                        //this.showVideoNotification();
                    }
                }

                // Tone handling is now done in processStreamingCompletion
                // The emotion will be applied after speaking ends through the existing system

                // Check if this is chunked processing
                if (data.processing_mode === "chunked") {
                    console.log(
                        "Chunked processing mode detected, handling chunked audio",
                    );
                    await this.handleChunkedAudioResponse(data);
                    return;
                }

                // Process TTS immediately without waiting for emotion
                if (this.lipSyncController) {
                    try {
                        if (this.stopAudioButton) {
                            this.stopAudioButton.classList.remove("d-none");
                        }
                        // Hide send button when stop button is shown
                        if (this.sendButton) {
                            this.sendButton.classList.remove("d-none");
                        }

                        // Keep the microphone button container accessible during audio playback

                        // Ensure microphone button container remains visible and accessible during audio
                        if (this.micButtonContainer) {
                            this.micButtonContainer.style.visibility =
                                "visible";
                            this.micButtonContainer.style.pointerEvents =
                                "auto";
                        }

                        this.isAudioPlaying = true;

                        // AEC (Acoustic Echo Cancellation) handles echo prevention during AI speech
                        console.log(
                            "🎵 AI audio starting - AEC prevents echo while allowing interruptions",
                        );


                        // Emotion controller removed in Task #38 — A2F
                        // renderer drives facial expression on its own.

                        if (this.animationController) {
                            this.animationController.onTTSStart();
                        }

                        // Pre-fetch ElevenLabs data and prepare synchronized playback
                        if (data.request_id) {
                            try {
                                console.log(
                                    "Pre-fetching ElevenLabs phoneme sequence for request:",
                                    data.request_id,
                                );
                                await this.waitForElevenLabsData(
                                    data.request_id,
                                );
                                console.log(
                                    "ElevenLabs data ready, preparing synchronized audio and lip sync",
                                );

                                // Data is ready, preparation will happen when audio element is available
                                console.log(
                                    "ElevenLabs timing data ready for preparation",
                                );
                            } catch (error) {
                                console.warn(
                                    "Failed to pre-fetch ElevenLabs data, will use fallback:",
                                    error,
                                );
                            }
                        }

                        // READINESS CHECK SYSTEM - Wait for all components before synchronized start
                        this.tts.onPlayStart = async (audioElement) => {
                            this.hideLoadingIndicator();


                            console.log(
                                "=== READINESS CHECK SYNCHRONIZATION ===",
                            );

                            if (data.request_id) {
                                try {
                                    console.log(
                                        "Checking component readiness for synchronized playback...",
                                    );

                                    // Wait for all components to be ready
                                    const readinessCheck =
                                        await this.checkAllComponentsReady(
                                            data.request_id,
                                            audioElement,
                                        );

                                    if (readinessCheck.allReady) {
                                        console.log(
                                            "All components ready - starting synchronized playback",
                                        );
                                        console.log("✓ Phoneme sequence ready");
                                        console.log(
                                            "✓ Duration sequence ready",
                                        );
                                        console.log("✓ Audio element ready");

                                        // Start lip sync and audio simultaneously now that everything is prepared
                                        this.lipSyncController.startPreparedElevenLabsAnimation(
                                            audioElement,
                                        );
                                        console.log(
                                            "Synchronized start: lip sync and audio beginning together",
                                        );
                                    } else {
                                        console.warn(
                                            "Component readiness check failed:",
                                            readinessCheck.missingComponents,
                                        );
                                        this.lipSyncController.speak(
                                            data.response,
                                        );
                                    }
                                } catch (error) {
                                    console.warn(
                                        "Readiness check failed, using fallback:",
                                        error,
                                    );
                                    this.lipSyncController.speak(data.response);
                                }
                            } else {
                                // Fallback to character-based lip sync
                                this.lipSyncController.speak(data.response);
                            }

                            if (this.animationController) {
                                this.animationController.onAudioPlaybackStart();
                            }

                            // Emotion replay on playback removed in Task #38.

                            console.log(
                                "=== SYNCHRONIZED PLAYBACK COMPLETE ===",
                            );
                        };
                        // Tone handling is now done in processStreamingCompletion
                        // No need for separate tone polling since streaming provides the final result

                        // Connect animation controller directly to TTS if needed
                        if (
                            this.animationController &&
                            !this.tts.animationController
                        ) {
                            this.tts.setAnimationController(
                                this.animationController,
                            );
                            console.log(
                                "Connected animation controller directly to TTS service",
                            );
                        }

                        // Ensure LipSync controller is connected for audio analysis
                        if (
                            this.lipSyncController &&
                            !this.tts.lipSyncController
                        ) {
                            this.tts.setLipSyncController(
                                this.lipSyncController,
                            );
                            console.log(
                                "Connected LipSync controller to TTS service for audio analysis",
                            );
                        }

                        // Legacy TTS disabled - using new TTS system via TTSManager
                        console.log(
                            "Legacy TTS disabled - new TTS system handles audio",
                        );

                        // Save the audio blob and URL for replay
                        if (this.tts.currentAudioBlob) {
                            // Task #274: release the previous reply's blob
                            // URL before minting a new one — otherwise one
                            // full audio blob per turn stays pinned in
                            // memory for the whole session.
                            if (this.lastAudioUrl) {
                                try {
                                    URL.revokeObjectURL(this.lastAudioUrl);
                                } catch (_) {}
                            }
                            this.lastAudioBlob = this.tts.currentAudioBlob;
                            // Create a persistent URL that won't be revoked
                            this.lastAudioUrl = URL.createObjectURL(
                                this.lastAudioBlob,
                            );
                            console.log(
                                "Stored audio blob and URL for future replay",
                            );
                        }

                        // Natural completion
                        this.stopAudio();

                        // Explicitly notify animation controller of TTS end
                        if (this.animationController) {
                            //this.animationController.onTTSEnd();
                            console
                                .log
                                //"Animation controller explicitly notified of TTS end after playback",
                                ();
                        }
                    } catch (error) {
                        if (error.name !== "AbortError") {
                            console.error("TTS error:", error);
                            this.stopAudio();
                        }
                    }
                }
            } else {
                throw new Error(data.error || "Unknown error occurred");
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            if (!error || error.message !== "FREE_LIMIT_REACHED") {
                this.addMessageToChat(
                    "System",
                    "That message didn't go through. Check your connection and try again.",
                    "error-message",
                );
            }
            this.stopAudio();
        } finally {
            this.hideLoadingIndicator();

            // Reset processing flag if no audio is playing
            if (!this.isAudioPlaying) {
                this.isProcessing = false;
                console.log(
                    "Message processing completed - chat window can be hidden again if clicked outside",
                );
            }
        }
    }

    /**
     * Send a message silently - the user's message is NOT displayed in the chat UI,
     * but authentication, quota, and other checks are still enforced.
     * Only the AI's response will be displayed.
     * @param {string} message - The message to send
     * @param {string} messageType - Optional message type for routing
     * @returns {Promise<Object>} - The streaming response
     */
    async sendMessageSilently(message, messageType = null) {
        if (!message || !this.isEnabled)
            return { status: "error", error: "Not enabled" };

        console.log(
            "📤 [Silent] Sending message silently:",
            message.substring(0, 100) + "...",
        );

        // Check if user is authenticated - REQUIRE authentication
        const isAuthenticated =
            window.firebaseAuth?.currentUser !== null &&
            window.firebaseAuth?.currentUser !== undefined;

        if (!isAuthenticated) {
            console.log(
                "[Silent] User not authenticated - showing sign-in modal",
            );
            if (window.authController) {
                window.authController.showAuthModal();
            } else if (typeof window.showNewUiAuthModal === "function") {
                // authController not ready yet — open the new-UI login modal.
                window.showNewUiAuthModal();
            } else {
                const modal = document.getElementById("modal-auth");
                if (modal) modal.classList.add("is-open");
            }
            return { status: "error", error: "Not authenticated" };
        }

        const userId = window.firebaseAuth.currentUser.uid;

        // Initialize question counter if needed
        if (this.questionCounter.cachedCount === null) {
            try {
                await this.questionCounter.initializeForUser(userId);
            } catch (initError) {
                console.error(
                    "[Silent] Failed to initialize question counter:",
                    initError,
                );
                return { status: "error", error: "Counter init failed" };
            }
        }

        // Fast local quota check
        let hasQuota = this.questionCounter.fastLocalCheck();
        if (!hasQuota) {
            // Same subscriber-safe guard as the normal send path: confirm
            // authoritative subscription status before blocking a possible
            // subscriber whose status hasn't resolved yet.
            const subscribed =
                await this.questionCounter.confirmSubscriptionBeforeBlock();
            if (subscribed) {
                hasQuota = true;
            }
        }
        if (!hasQuota) {
            alert("No questions remaining. Your question count is 0.");
            return { status: "error", error: "No quota" };
        }

        // Update UI with new count
        window.dispatchEvent(
            new CustomEvent("questionCountChanged", {
                detail: { count: this.questionCounter.cachedCount },
            }),
        );

        // Handle any active audio/interruption
        if (
            this.realtimeTTS?.isSpeaking?.() ||
            this.isAudioPlaying ||
            this.isProcessing
        ) {
            this.handleUserInterruption();
            await this.prepTTSForNewTurn();
        } else if (this.modelViewer && this.modelViewer.greetingService) {
            this.modelViewer.greetingService.stop();
        }

        // Show chat container
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }

        // Set processing flag
        this.isProcessing = true;

        // Add to conversation history (user message for context, but NOT displayed)
        this.addToConversationHistory("user", message);

        // Show loading indicator (direct DOM manipulation since showLoadingIndicator doesn't exist)
        if (this.loadingIndicator) {
            this.loadingIndicator.style.display = "flex";
            setTimeout(() => this.loadingIndicator.classList.add("show"), 10);
        }

        try {
            // Use the hidden streaming message method (doesn't show user message)
            const streamingResponse = await this.sendHiddenStreamingMessage(
                message,
                { enableAudio: true },
            );

            if (streamingResponse.status === "success") {
                this.currentBotMessage = this.currentStreamingMessage;
                this.lastAudioResponse = streamingResponse.response;

                // Add assistant response to conversation history
                this.addToConversationHistory(
                    "assistant",
                    streamingResponse.response,
                );

                // Firebase sync in background
                this.questionCounter
                    .syncDecrementToFirebase(userId)
                    .then(() => console.log("[Silent] Firebase sync completed"))
                    .catch((error) =>
                        console.error("[Silent] Firebase sync failed:", error),
                    );

                return streamingResponse;
            } else {
                throw new Error(streamingResponse.error || "Unknown error");
            }
        } catch (error) {
            // Task #530 — hidden sends (greeting, idle check-in, timeline/
            // story rides) must NEVER paint red bubbles into the visible
            // chat. The user didn't do anything; log it and move on.
            console.error("[Silent] Failed to send message:", error);
            this._micLedger?.("hidden-send-failed", {
                msg: String(error?.message || error).slice(0, 120),
            });
            return { status: "error", error: error.message };
        } finally {
            this.hideLoadingIndicator();
            if (!this.isAudioPlaying) {
                this.isProcessing = false;
            }
        }
    }

    sendDescriptionToChat(description) {
        // Close the bio panel properly (like the bio button does)
        const bioPanel = document.getElementById("bio-panel");
        const bioButton = document.getElementById("bio-button");

        if (bioPanel) {
            // Remove active class from bio button if it exists
            if (bioButton) {
                bioButton.classList.remove("bio-button-active");
            }

            // Add smooth sliding animation when closing
            bioPanel.style.transition =
                "transform 0.9s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.7s ease, width 0.5s ease";

            // Apply sliding effect
            bioPanel.style.transform = "translateX(110%)";
            bioPanel.style.opacity = "0.2";

            // Remove active class after animation finishes
            setTimeout(() => {
                bioPanel.classList.remove("active");
                bioPanel.style.transform = "translateX(100%)";
            }, 900);
        }

        // Show the chat container
        const chatContainer = document.querySelector(".chat-container");
        if (chatContainer) {
            chatContainer.classList.add("visible");
        }

        console.log("Sending to chat directly:", description);

        // Trigger listen animation when description is sent to chat
        if (window.animationController) {
            console.log("Triggering listen animation for image description");
            window.animationController.playListenAnimation();
        }

        // Find the chatController to send the message directly
        if (window.chatController) {
            // Use the ChatController to send the message directly
            window.chatController.sendMessage(description);
        } else {
            // Fallback to the old method if ChatController is not available
            const chatInput = document.getElementById("chat-input");
            if (chatInput) {
                // Set the text and focus
                chatInput.value = description;
                chatInput.focus();

                // Trigger input event for height adjustment
                const inputEvent = new Event("input", { bubbles: true });
                chatInput.dispatchEvent(inputEvent);

                // Trigger a focus event to ensure any event listeners for chat UI are triggered
                const focusEvent = new Event("focus", { bubbles: true });
                chatInput.dispatchEvent(focusEvent);

                // Simulate clicking the send button
                const sendButton = document.getElementById("send-button");
                if (sendButton) {
                    sendButton.click();
                }
            }
        }
    }

    /**
     * Speak text directly through ElevenLabs TTS pipeline without AI response generation.
     * Used for bio image descriptions to have the character narrate the content directly.
     * Simulates natural streaming by feeding text word-by-word with delays.
     * @param {string} text - The text to speak directly
     * @returns {Promise<void>}
     */
    async speakDirectly(text) {
        if (!text || !text.trim()) {
            console.warn("[speakDirectly] No text provided");
            return;
        }

        console.log(
            "🎤 [speakDirectly] Speaking text directly:",
            text.substring(0, 100) + "...",
        );

        // Ensure DOM elements are initialized (lazy init if needed)
        if (!this.chatHistory) {
            this.chatHistory = document.getElementById("chat-history");
        }
        if (!this.chatContainer) {
            this.chatContainer = document.querySelector(".chat-container");
        }

        // Validate chat infrastructure exists
        if (!this.chatHistory) {
            console.error(
                "[speakDirectly] Chat history element not found - cannot display message",
            );
            return;
        }

        // Create bot message bubble with the description text
        // Use "Assistant" as sender for proper metadata/routing
        const messageElement = this.addMessageToChat("", text, "bot-message");
        if (!messageElement) {
            console.error("[speakDirectly] Failed to create message element");
            return;
        }
        this.currentBotMessage = messageElement;
        this.currentStreamingMessage = messageElement;

        // Add to conversation history for context
        this.addToConversationHistory("assistant", text);

        console.log("📝 [speakDirectly] Bot message bubble created");

        // Get the realtime TTS instance
        const rtts = this.realtimeTTS;
        if (!rtts) {
            console.warn(
                "[speakDirectly] RealtimeTTS not available - text shown but no audio",
            );
            // Trigger speak animation even without audio for visual feedback
            if (this.animationController) {
                this.animationController.onTTSStart();
                setTimeout(() => {
                    this.animationController.onTTSEnd();
                }, 2000);
            }
            return;
        }

        // Track animation state for proper cleanup
        let animationStarted = false;

        try {
            // 1. FIRST: Start the TTS connection (ensures WebSocket is connected and audio prewarms)
            // This MUST be first as all other TTS operations require an active connection
            console.log("🔌 [speakDirectly] Starting TTS connection...");
            await rtts.start();

            // 2. Verify WebSocket is fully connected and warm
            // Wait a brief moment for connection to stabilize
            if (!rtts.wsOpen) {
                console.log(
                    "⏳ [speakDirectly] Waiting for WebSocket to be ready...",
                );
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            // 3. Resume audio context if needed (especially for Safari)
            if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                console.log("🔊 [speakDirectly] Resuming audio context...");
                await rtts.audioCtx.resume();
            }

            // 4. Prepare TTS for new response (now that connection is established)
            if (typeof rtts.ensureReadyForNextResponse === "function") {
                await rtts.ensureReadyForNextResponse(120);
            }

            // 5. Begin new response epoch (clears queues, resets flags)
            if (typeof rtts.beginNewResponse === "function") {
                console.log(
                    "🆕 [speakDirectly] Beginning new response epoch...",
                );
                await rtts.beginNewResponse();
            }

            // 5.5. Clear stop guard for intentional speech (no turn ID)
            if (typeof rtts.clearStopGuard === "function") {
                rtts.clearStopGuard();
            }

            // 6. Trigger speak animation now that TTS is ready
            if (this.animationController) {
                console.log("🎬 [speakDirectly] Starting speak animation");
                this.animationController.onTTSStart();
                animationStarted = true;
            }

            // 7. Feed text word-by-word with delays to simulate natural streaming
            // This allows ElevenLabs to start synthesizing audio incrementally
            const words = text.split(/\s+/).filter((w) => w.length > 0);
            const delayMs = 25; // Delay between words (simulates OpenAI streaming pace)

            console.log(
                `📝 [speakDirectly] Streaming ${words.length} words with ${delayMs}ms delays...`,
            );

            for (let i = 0; i < words.length; i++) {
                const word = words[i];
                // Add space after word (except for last word which gets handled by flushAndClose)
                rtts.handleLLMDelta(word + " ");

                // Add async delay between words to simulate streaming
                // This gives ElevenLabs time to process and start synthesizing
                if (i < words.length - 1) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, delayMs),
                    );
                }
            }

            console.log(
                "✅ [speakDirectly] All words streamed to TTS pipeline",
            );

            // 8. Delay flushAndClose to allow synthesis to begin before signaling end
            // This prevents ElevenLabs from waiting for all text before starting audio
            await new Promise((resolve) => setTimeout(resolve, 150));

            if (typeof rtts.flushAndClose === "function") {
                rtts.flushAndClose();
                console.log("🔚 [speakDirectly] Flush and close called");
            }
        } catch (error) {
            console.error("[speakDirectly] Error sending text to TTS:", error);
        } finally {
            // Ensure animation is properly cleaned up on any exit path
            // Note: For successful TTS, the audio-end event will handle cleanup
            // This finally block handles error cases where audio never played
            if (animationStarted && !rtts?.playing) {
                // Only end animation if TTS isn't actively playing
                // (if it is playing, the audio-end event will handle cleanup)
                setTimeout(() => {
                    if (this.animationController && !rtts?.playing) {
                        this.animationController.onTTSEnd();
                    }
                }, 500);
            }
        }
    }

    /**
     * Send a dynamic greeting message when model loads.
     * This sends a hidden user message that triggers an AI-generated greeting.
     * The user message is NOT displayed in the UI, only the AI response appears.
     * Uses whatever user data is available (name, gender, location, date).
     * Task #519: the greeting always attempts VOICE on every browser — the
     * Safari permission popup is retired (see GreetingAudioUnlock.js).
     */
    async sendDynamicGreeting() {
        console.log(
            "🎉 [DynamicGreeting] Starting dynamic greeting generation...",
        );

        const characterName = this.getCurrentCharacterName() || "Character";
        console.log("🎉 [DynamicGreeting] Character name:", characterName);

        // Task #519 — the Safari "May <legend> read their first words to
        // you?" permission card (Task #199) is GONE. Opening a character
        // just speaks, on every browser, via the same three paths as the
        // /learn player (Task #515):
        //   auto          — the bounded probe says audible autoplay is
        //                   allowed (real-silent-clip contract) → greet with
        //                   voice immediately. Chrome lands here unchanged.
        //   early-gesture — any tap/click/keypress since first paint was
        //                   harvested by GreetingAudioUnlock (installed at
        //                   boot in main.js) and already resumed the audio
        //                   context inside that gesture → greet with voice.
        //   tap           — refused with ZERO interaction (truly cold Safari
        //                   page): HOLD the spoken greeting — never burn it
        //                   as text (the old silent-degrade regression) —
        //                   show the minimal pill, and the first tap
        //                   anywhere starts the voice.
        // Audio is always requested; a genuine playback failure downstream
        // still degrades to the text greeting exactly as before.
        const enableAudio = true;

        const rtts = this.realtimeTTS || this.tts || null;
        if (rtts) {
            const probe = await probeGreetingAutoplay(rtts);
            if (probe.audible) {
                console.log(
                    "🎉 [DynamicGreeting] Autoplay probe OK — greeting with voice immediately",
                );
            } else if (hasUnlockGesture()) {
                // A real gesture already ran the in-stack unlock; re-nudge
                // the context in case it was created after that gesture.
                try {
                    if (rtts.audioCtx) await rtts.audioCtx.resume();
                } catch (e) {}
                console.log(
                    "🎉 [DynamicGreeting] Unlock gesture already harvested — audio context state:",
                    rtts.audioCtx?.state,
                );
            } else {
                // Blocked with zero interaction. The old flow silently
                // degraded to text here; instead HOLD the spoken greeting
                // and let the first tap anywhere start it (minimal pill —
                // a beat in the flow, not a permission card).
                console.log(
                    "🎉 [DynamicGreeting] Autoplay blocked with zero interaction — holding spoken greeting for the first tap",
                );
                await new Promise((resolve) => {
                    const armed = armGreetingStart(resolve, {
                        showPill: true,
                        characterName,
                    });
                    if (!armed) resolve(); // kit already committed — proceed
                });
                try {
                    if (rtts.audioCtx) await rtts.audioCtx.resume();
                } catch (e) {}
            }
            if (rtts.audioCtx && rtts.audioCtx.state === "running") {
                // Hint-only flag (never a gate): lets Safari idle check-ins
                // know this device has produced audible greeting audio.
                setGreetingAudioGranted();
            } else {
                console.warn(
                    "🎉 [DynamicGreeting] Audio DEGRADED: audio context state is",
                    rtts.audioCtx ? rtts.audioCtx.state : "(no audioCtx)",
                    "— greeting will still stream as text",
                );
            }
        }

        // The greeting commits now — tear down the page-wide gesture harvest
        // (and pill, if any) so mid-speech clicks can never re-bless the
        // element or touch the audio context.
        commitGreetingAudioUnlock();

        // Gather available user information
        const userInfo = await this.gatherUserInfoForGreeting();
        console.log("🎉 [DynamicGreeting] User info gathered:", userInfo);

        // Build the hidden greeting prompt with available user data
        const greetingPrompt = this.buildGreetingPrompt(userInfo);
        console.log("🎉 [DynamicGreeting] Greeting prompt:", greetingPrompt);

        // Show chat container for the greeting
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }

        // Set processing flag
        this.isProcessing = true;

        try {
            // Send the hidden message via streaming API
            // Note: We do NOT call addMessageToChat for the user message - it stays hidden
            // Pass enableAudio flag to control TTS behavior (false for Safari text-only)
            const streamingResponse = await this.sendHiddenStreamingMessage(
                greetingPrompt,
                { enableAudio },
            );

            if (streamingResponse.status === "success") {
                this.currentBotMessage = this.currentStreamingMessage;
                this.lastAudioResponse = streamingResponse.response;

                // Add assistant's greeting to conversation history
                this.addToConversationHistory(
                    "assistant",
                    streamingResponse.response,
                );

                console.log(
                    "🎉 [DynamicGreeting] Greeting generated successfully",
                );
            } else if (streamingResponse.status === "aborted") {
                // The visitor barged in (typed/spoke) while the greeting was
                // still streaming — interruption handling already captured
                // any partial line into history. Stay quiet: no static
                // fallback on top of the user's new exchange.
                this._dropEmptyStreamingBubble(streamingResponse.bubbleEl);
                console.log(
                    "🎉 [DynamicGreeting] Greeting aborted by user barge-in — staying quiet",
                );
            } else if (streamingResponse.status === "blocked") {
                // 403 "blocked" stays deliberately silent for ANONYMOUS
                // visitors (greeting-allowance abuse guard). But a signed-in
                // user must NEVER get a silent character — show the static
                // text greeting and log why.
                const user = window.firebaseAuth?.currentUser;
                if (user && user.isAnonymous !== true) {
                    this._showStaticTextGreetingFallback(
                        "greeting stream returned 403 (blocked) for an AUTHENTICATED user — this should not happen; check server-side greeting allowance/auth sync",
                    );
                } else {
                    console.warn(
                        "🎉 [DynamicGreeting] Greeting blocked (403) for anonymous visitor — staying silent by design",
                    );
                }
            } else {
                // any other non-success outcome must still show
                // SOMETHING — no silent no-greeting path.
                this._showStaticTextGreetingFallback(
                    "stream returned status " + streamingResponse.status,
                );
            }
        } catch (error) {
            console.error(
                "🎉 [DynamicGreeting] Error generating greeting:",
                error,
            );
            // the dynamic greeting failed (network error, stream
            // error, timeout) — fall back to a static TEXT greeting so the
            // character never loads silently.
            this._showStaticTextGreetingFallback(error?.message || error);
        } finally {
            this.isProcessing = false;
            // Task #462 — the idle check-in quiet clock starts only after the
            // greeting fully resolved (success, fallback, blocked or abort).
            this._greetingCompletedAt = Date.now();
            this.idleCheckIns?.noteActivity("greeting-complete");
        }
    }

    /**
     * Task #462 — remove a hidden-stream bot bubble if nothing ever streamed
     * into it (e.g. a barge-in or failure before the first chunk). A stray
     * empty bubble reads as a glitch; a PARTIAL bubble is left alone — it
     * matches what interruption capture put into conversation history.
     * @param {HTMLElement} [bubbleEl] — explicit bubble (barge-in nulls
     *        this.currentStreamingMessage, so callers pass the envelope's
     *        bubbleEl when they have it).
     */
    _dropEmptyStreamingBubble(bubbleEl) {
        try {
            const el = bubbleEl || this.currentStreamingMessage;
            if (el && el.parentNode && !(el.textContent || "").trim()) {
                el.parentNode.removeChild(el);
                if (el === this.currentStreamingMessage) {
                    this.currentStreamingMessage = null;
                }
            }
        } catch (e) {}
    }

    /**
     * Task #462 — build the hidden prompt for a spontaneous idle check-in.
     * Same pattern as buildGreetingPrompt(): sent as a hidden user message
     * through the normal persona pipeline, so whatever character is loaded
     * answers in its own voice (time of day comes from client_timezone
     * server-side; the last topic comes from conversation_history, which the
     * model already sees). Angles rotate within the session so consecutive
     * check-ins never repeat their flavor.
     */
    buildIdleCheckInPrompt(checkinIndex = 0) {
        const hasUserExchange = (this.conversationHistory || []).some(
            (m) => m && m.role === "user",
        );
        const angles = [
            {
                id: "timeofday",
                text: "If the general time of day is known from context, let it gently color your line (a morning/evening mood — NEVER an exact clock time).",
            },
            {
                id: "muse",
                text: "Offer ONE brief, intriguing thought or memory from your own life or work — something that invites curiosity.",
            },
            {
                id: "invite",
                text: "Ask ONE gentle, open question about what they might like to explore or ask you.",
            },
        ];
        if (hasUserExchange) {
            // Real exchange exists — picking the thread back up is the most
            // natural angle, so it goes to the front of the rotation.
            angles.unshift({
                id: "thread",
                text: "Gently pick the thread of what you two were last discussing back up — add one small NEW thought or question that moves it a step forward (no summarizing).",
            });
        }
        if (!Array.isArray(this._checkinUsedAngles)) {
            this._checkinUsedAngles = [];
        }
        let pool = angles.filter(
            (a) => !this._checkinUsedAngles.includes(a.id),
        );
        if (pool.length === 0) {
            this._checkinUsedAngles = [];
            pool = angles;
        }
        const angle = pool[Math.floor(Math.random() * pool.length)];
        this._checkinUsedAngles.push(angle.id);

        const brevity =
            checkinIndex >= 1
                ? "ONE short spoken sentence — this is not your first gentle nudge this visit, so be even briefer and lighter than before."
                : "ONE or TWO short spoken sentences maximum.";

        return (
            "The visitor has gone quiet for a little while — still here with you, just idle. " +
            "Softly re-engage them, entirely in character as yourself.\n\n" +
            angle.text +
            "\n\nRules:\n" +
            "- " +
            brevity +
            "\n" +
            '- Warm and unhurried. Never scold or guilt them for the silence, and never ask "are you still there?".\n' +
            "- Do not repeat or rephrase your greeting or anything you have already said this visit — say something NEW.\n" +
            "- Never mention timers, inactivity, being an AI, or an exact clock time.\n" +
            "- End in a way that makes replying feel easy and inviting.\n\n" +
            "Start directly with your spoken line."
        );
    }

    /**
     * Task #462 — speak one idle check-in via the SAME hidden-streaming path
     * as the greeting, tagged message_type "idle_checkin" (the server meters
     * it under its own bounded anonymous scope; a signed-in user's question
     * counter is never touched — that counter only moves on the typed path).
     * The reply streams into the chat thread and joins conversation history,
     * so replying to it flows like any other exchange.
     *
     * Returns the hidden-stream envelope ({status: "success"|"blocked"|
     * "aborted"|"skipped"|"error", ...}). A check-in NEVER surfaces an error
     * bubble and NEVER pops a permission card — every failure path is silent.
     */
    async sendIdleCheckIn(checkinIndex = 0) {
        // Belt-and-braces re-checks: IdleCheckInController gates these too,
        // but state can change between its tick and this call.
        if (this.isProcessing || this.isAudioPlaying) {
            return { status: "skipped" };
        }
        if (
            typeof this._isSpeechActive === "function" &&
            this._isSpeechActive()
        ) {
            return { status: "skipped" };
        }
        if (this.isListening) {
            return { status: "skipped" };
        }
        // Safari hard rule: an idle nudge must NEVER show the "Tap to hear"
        // permission card. Only speak when audio was already granted.
        if (this.isSafari && !isGreetingAudioGranted()) {
            return { status: "skipped" };
        }
        // Spoken-or-nothing: a check-in that cannot make sound would be a
        // ghostly text bubble, not a character "speaking up". Resume the
        // context quietly (no UI); skip when audio genuinely can't run.
        const rtts = this.realtimeTTS || this.tts || null;
        if (!rtts || !rtts.audioCtx) {
            return { status: "skipped" };
        }
        if (rtts.audioCtx.state !== "running") {
            try {
                await rtts.audioCtx.resume();
            } catch (e) {}
            if (rtts.audioCtx.state !== "running") {
                return { status: "skipped" };
            }
        }

        const prompt = this.buildIdleCheckInPrompt(checkinIndex);
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }
        this.isProcessing = true;
        try {
            const res = await this.sendHiddenStreamingMessage(prompt, {
                enableAudio: true,
                messageType: "idle_checkin",
            });
            if (res.status === "success") {
                this.currentBotMessage = this.currentStreamingMessage;
                this.lastAudioResponse = res.response;
                // The check-in joins conversation history so replying to it
                // flows naturally.
                this.addToConversationHistory("assistant", res.response);
            } else if (res.status === "aborted") {
                // Barge-in mid-check-in: interruption capture already put any
                // partial line into history; just tidy an empty bubble.
                this._dropEmptyStreamingBubble(res.bubbleEl);
            }
            return res;
        } catch (error) {
            // Silent by contract — tidy the empty bubble, log, move on.
            this._dropEmptyStreamingBubble();
            console.warn(
                "[IdleCheckIn] check-in failed silently:",
                error?.message || error,
            );
            return { status: "error" };
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Last-resort TEXT greeting. Used when the dynamic streaming
     * greeting fails for any reason so the character never appears silently.
     * Uses the character's canned greeting when it is real prose (most
     * characterData.greetings entries are audio URLs, never show those),
     * else a generic in-character line.
     */
    _showStaticTextGreetingFallback(reason) {
        try {
            console.warn(
                "🎉 [DynamicGreeting] Falling back to static TEXT greeting. Reason:",
                reason,
            );
            const charId = this.getCurrentCharacterId?.();
            const char =
                (charId && window.characterData?.[charId]) || null;
            // Only accept human-readable prose: a string that is not a URL/path
            // and looks like a sentence (contains a space).
            const proseGreetings = (char?.greetings || []).filter(
                (g) =>
                    typeof g === "string" &&
                    !/^(https?:)?\/\//i.test(g.trim()) &&
                    !/^\/?static\//i.test(g.trim()) &&
                    !/\.(mp3|wav|ogg|m4a|webm)(\?|$)/i.test(g.trim()) &&
                    g.trim().includes(" "),
            );
            const canned =
                proseGreetings.length > 0
                    ? proseGreetings[
                          Math.floor(Math.random() * proseGreetings.length)
                      ]
                    : null;
            const name =
                char?.name || this.getCurrentCharacterName?.() || "";
            const text =
                canned ||
                (name
                    ? `Greetings — I am ${name}. What shall we explore together?`
                    : "Greetings — what shall we explore together?");

            // Reuse the empty streaming bubble if it exists, else add one.
            const existing = this.currentStreamingMessage;
            const contentEl = existing?.querySelector?.(".message-content");
            if (contentEl && !contentEl.textContent?.trim()) {
                contentEl.textContent = text;
            } else if (!existing) {
                this.addMessageToChat("", text, "bot-message");
            } else {
                // Bubble already has text (partial stream) — keep it.
                return;
            }
            this.chatContainer?.classList?.add?.("visible");
            this.addToConversationHistory?.("assistant", text);
        } catch (e) {
            console.warn(
                "🎉 [DynamicGreeting] Static text fallback failed:",
                e,
            );
        }
    }

    /**
     * Gather user information for the dynamic greeting.
     * Collects whatever data is available: name, gender, location, date.
     * @returns {Object} Object containing available user data
     */
    async gatherUserInfoForGreeting() {
        const now = new Date();
        // Safari-safe: only the IANA timezone is collected from the browser.
        // The server uses its own UTC clock to compute the actual hour in
        // that zone — we never trust `new Date().getHours()`. See
        // ClientTimeOfDay.js for rationale.
        const clientTime = getClientTimeInfo();
        const userInfo = {
            name: null,
            gender: null,
            location: null,
            date: now.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            }),
            timezone: clientTime ? clientTime.timeZone : null,
            clientTime, // { timeZone, locale } or null
            isAuthenticated: false,
            isAnonymous: false,
        };

        try {
            // Try to get user info from Firebase
            if (window.firebaseAuth && window.firebaseModules) {
                const user = window.firebaseAuth.currentUser;
                // Cold visitors are signed in ANONYMOUSLY for the free taste
                // (see _ensureAnonymousAuth) — a Firebase user exists but there
                // is no real account. Treat anonymous as NOT authenticated so
                // the greeting branches to the welcome + sign-up invite instead
                // of a personalized "welcome back".
                const isAnon = !!(user && user.isAnonymous === true);
                userInfo.isAnonymous = isAnon;
                userInfo.isAuthenticated = !!user && !isAnon;
                if (user && !isAnon) {

                    // Try to get user profile from Firestore
                    try {
                        const userProfileRef = window.firebaseModules.doc(
                            window.firebaseDB,
                            "userProfiles",
                            user.uid,
                        );
                        const userProfileDoc =
                            await window.firebaseModules.getDoc(userProfileRef);

                        if (userProfileDoc.exists()) {
                            const profile = userProfileDoc.data();
                            userInfo.name =
                                profile.username ||
                                profile.displayName ||
                                user.displayName ||
                                user.email?.split("@")[0];
                            userInfo.gender = profile.gender || null;
                            userInfo.location =
                                profile.location ||
                                profile.city ||
                                profile.country ||
                                null;
                        } else {
                            // Fallback to basic user info
                            userInfo.name =
                                user.displayName || user.email?.split("@")[0];
                        }
                    } catch (profileError) {
                        console.warn(
                            "[DynamicGreeting] Could not fetch user profile:",
                            profileError,
                        );
                        userInfo.name =
                            user.displayName || user.email?.split("@")[0];
                    }
                }
            }

            // Try to get location from browser if not available from profile
            if (!userInfo.location) {
                try {
                    // Check if we have cached location
                    const cachedLocation = localStorage.getItem("userLocation");
                    if (cachedLocation) {
                        userInfo.location = cachedLocation;
                    }
                } catch (e) {
                    // Silently ignore localStorage errors
                }
            }
        } catch (error) {
            console.warn("[DynamicGreeting] Error gathering user info:", error);
        }

        return userInfo;
    }

    /**
     * Build the greeting prompt with available user information.
     * @param {Object} userInfo - Object containing available user data
     * @returns {string} The greeting prompt to send
     */
    buildGreetingPrompt(userInfo) {
        // Cold / anonymous visitors (no real account yet) hear an in-character
        // welcome that doubles as a warm first taste of LegendLabs and a gentle
        // nudge to sign up — NOT the personalized "welcome back" greeting.
        // Signed-in users fall through to the normal personal greeting below.
        const isColdVisitor =
            !!userInfo &&
            (userInfo.isAnonymous === true ||
                userInfo.isAuthenticated === false);
        if (isColdVisitor) {
            return (
                "This person just arrived and is talking with you for the very " +
                "first time — they have not signed up yet. Greet them warmly and " +
                "entirely in character as yourself. Keep it to 2-3 short, natural " +
                "spoken sentences: first welcome them in your own historical " +
                "voice; then mention, naturally, that here on LegendLabs they can " +
                "actually talk with you — ask you anything and hear your " +
                "perspective in your own words; then warmly invite them to create " +
                "a free account so they can come back and keep the conversation " +
                "going. Make it feel like a genuine personal invitation, never a " +
                "sales pitch or a list. Do NOT mention exact clock time. Stay " +
                "fully in character throughout."
            );
        }

        let contextParts = [];

        if (userInfo.name) {
            contextParts.push(`User's name: ${userInfo.name}`);
        }
        if (userInfo.gender) {
            contextParts.push(`User's gender: ${userInfo.gender}`);
        }
        if (userInfo.location) {
            contextParts.push(`User's location: ${userInfo.location}`);
        }

        // Time-of-day is resolved server-side from the client's IANA timezone
        // and the server's own UTC clock (see prompt_builder._resolve_local_time
        // and main._time_of_day_from_timezone). We deliberately do NOT inject
        // it from the browser here — the device clock is untrusted.

        let prompt =
            "Greet the user in a warm, conversational, and historically consistent manner.";

        if (contextParts.length > 0) {
            prompt +=
                " Use the following available information naturally if appropriate: " +
                contextParts.join(", ") +
                ".";
        }

        prompt +=
            " Do NOT mention exact clock time. Keep the greeting short, natural, and conversational.";

        return prompt;
    }

    /**
     * Send a hidden streaming message (user message not shown in UI).
     * The AI response appears normally as a bot message.
     * @param {string} message - The hidden message to send
     * @param {Object} options - Optional settings
     * @param {boolean} options.enableAudio - Whether to enable TTS audio (default: true)
     * @returns {Promise<Object>} - Promise resolving to response data
     */
    async sendHiddenStreamingMessage(message, options = {}) {
        // Cancel any existing stream
        if (this.streamingController) {
            this.streamingController.abort();
        }

        // Check if audio should be enabled (defaults to true)
        const enableAudio = options.enableAudio !== false;

        // Which server-side message_type this hidden send rides as. The
        // greeting is the default; Task #462 idle check-ins pass
        // "idle_checkin" so the server meters them under their own bounded
        // anonymous scope (never the typed-message allowance).
        const messageType = options.messageType || "greeting";

        // Prep TTS only if audio is enabled
        const rtts = this.realtimeTTS || this.tts || null;
        if (rtts && enableAudio) {
            try {
                if (
                    rtts &&
                    typeof rtts.ensureReadyForNextResponse === "function"
                ) {
                    await rtts.ensureReadyForNextResponse(120);
                }
                if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                    await rtts.audioCtx.resume();
                }
            } catch (e) {
                console.warn(
                    "[sendHiddenStreamingMessage] TTS prep failed (continuing)",
                    e,
                );
            }
        } else if (!enableAudio) {
            console.log(
                "[sendHiddenStreamingMessage] Audio disabled - text-only greeting",
            );
        }

        this.streamingController = new AbortController();

        return new Promise((rawResolve, rawReject) => {
            // settle-once wrappers so the readyState-4 finalizer
            // below can detect a stream that ended WITHOUT a "complete" event
            // (previously the promise hung forever = silent no-greeting).
            let settled = false;
            const resolve = (v) => {
                if (settled) return;
                settled = true;
                rawResolve(v);
            };
            const reject = (e) => {
                if (settled) return;
                settled = true;
                rawReject(e);
            };
            // Create bot message element (but NO user message - that's the key difference)
            this.currentStreamingMessage = this.addMessageToChat(
                "",
                "",
                "bot-message",
            );
            // Task #462 — local handle on the bubble: handleUserInterruption()
            // nulls this.currentStreamingMessage during barge-in, so the
            // "aborted" resolution passes the element back for cleanup when
            // nothing had streamed into it yet.
            const streamBubbleEl = this.currentStreamingMessage;
            let streamingContent = "";
            let requestId = null;
            let modelInfo = null;

            console.log(
                "🚀 [HiddenStreaming] Sending hidden message for greeting",
            );
            const xhr = new XMLHttpRequest();
            xhr.open("POST", "/api/chat/stream", true);
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.setRequestHeader("Cache-Control", "no-cache");

            let lastProcessedLength = 0;
            let processingBuffer = "";
            let streamingStarted = false;
            let streamingContentStarted = false;
            let abortedByUser = false; // Task #462 — barge-in aborted this stream

            let greetingFreeLimitHandled = false;
            xhr.onreadystatechange = () => {
                // Greeting graceful degradation: the greeting is normally exempt
                // from the anonymous free-taste cap (server meters it under a
                // generous greeting-only allowance), so a brand-new visitor
                // hears it. In the rare event the server still returns a 403
                // (e.g. greeting allowance exhausted by abuse), do NOT show a
                // scary error or hang in a "thinking" state — silently drop the
                // empty bot bubble and resolve so the rest of the Lab stays
                // usable. The sign-up wall is reserved for typed messages.
                if (
                    !greetingFreeLimitHandled &&
                    xhr.readyState >= 2 &&
                    xhr.status === 403
                ) {
                    greetingFreeLimitHandled = true;
                    try {
                        const el = this.currentStreamingMessage;
                        if (el && el.parentNode) {
                            el.parentNode.removeChild(el);
                        }
                        this.currentStreamingMessage = null;
                    } catch (e) {}
                    try {
                        xhr.abort();
                    } catch (e) {}
                    console.warn(
                        "🎉 [HiddenStreaming] Greeting blocked (403) — degrading silently",
                    );
                    resolve({ status: "blocked", response: "" });
                    return;
                }

                if (xhr.readyState >= 3) {
                    if (greetingFreeLimitHandled || xhr.status === 403) return;
                    const fullResponse = xhr.responseText;
                    const newData = fullResponse.substring(lastProcessedLength);
                    lastProcessedLength = fullResponse.length;

                    if (newData) {
                        processingBuffer += newData;

                        let startPos = 0;
                        let newlinePos = processingBuffer.indexOf(
                            "\n",
                            startPos,
                        );

                        while (newlinePos !== -1) {
                            const line = processingBuffer.substring(
                                startPos,
                                newlinePos,
                            );

                            if (line.startsWith("data: ")) {
                                try {
                                    const data = JSON.parse(line.slice(6));

                                    if (data.type === "metadata") {
                                        requestId = data.request_id;
                                        modelInfo = data.model;

                                        // Only start TTS if audio is enabled
                                        if (
                                            this.realtimeTTS &&
                                            !streamingStarted &&
                                            enableAudio
                                        ) {
                                            try {
                                                this.realtimeTTS.start();
                                                // Signal that new turn is starting and get turn ID for delta verification
                                                if (
                                                    typeof this.realtimeTTS
                                                        .expectNewTurn ===
                                                    "function"
                                                ) {
                                                    this._currentStreamingTurnId =
                                                        this.realtimeTTS.expectNewTurn();
                                                }
                                                streamingStarted = true;
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] Failed to start:",
                                                    err,
                                                );
                                            }
                                        }
                                    } else if (data.type === "chunk") {
                                        streamingContent += data.content;
                                        console.log(
                                            "🎉 [HiddenStreaming] Received chunk:",
                                            data.content,
                                        );

                                        // Only send to TTS if audio is enabled
                                        if (
                                            this.realtimeTTS &&
                                            streamingStarted &&
                                            enableAudio
                                        ) {
                                            try {
                                                // Pass turn ID to verify delta belongs to this turn
                                                this.realtimeTTS.handleLLMDelta(
                                                    data.content,
                                                    this
                                                        ._currentStreamingTurnId,
                                                );
                                                if (!streamingContentStarted) {
                                                    streamingContentStarted = true;
                                                }
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] Delta error:",
                                                    err,
                                                );
                                            }
                                        }

                                        // Update DOM with streamed content
                                        if (this.currentStreamingMessage) {
                                            const messageContent =
                                                this.currentStreamingMessage.querySelector(
                                                    ".message-content",
                                                );
                                            if (messageContent) {
                                                const html =
                                                    renderMarkdownToHtml(
                                                        streamingContent,
                                                    );
                                                messageContent.innerHTML = html;
                                                // Option 1: the reply is anchored near the top at
                                                // creation; do NOT auto-scroll to the bottom while
                                                // streaming so the user reads from the start.
                                            } else {
                                                console.warn(
                                                    "🎉 [HiddenStreaming] message-content element not found",
                                                );
                                            }
                                        } else {
                                            console.warn(
                                                "🎉 [HiddenStreaming] currentStreamingMessage is null",
                                            );
                                        }
                                    } else if (data.type === "complete") {
                                        console.log(
                                            "🎉 [HiddenStreaming] Streaming complete, final response:",
                                            data.complete_response,
                                        );

                                        // Only close TTS if audio was enabled and started
                                        if (
                                            this.realtimeTTS &&
                                            streamingStarted &&
                                            enableAudio
                                        ) {
                                            try {
                                                this.realtimeTTS.flushAndClose();
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] End turn error:",
                                                    err,
                                                );
                                            }
                                        }

                                        // Ensure final content is properly rendered in DOM
                                        if (
                                            this.currentStreamingMessage &&
                                            data.complete_response
                                        ) {
                                            const messageContent =
                                                this.currentStreamingMessage.querySelector(
                                                    ".message-content",
                                                );
                                            if (messageContent) {
                                                const html =
                                                    renderMarkdownToHtml(
                                                        data.complete_response,
                                                    );
                                                messageContent.innerHTML = html;
                                            }
                                        }

                                        this.processStreamingCompletion(data);
                                        resolve({
                                            status: "success",
                                            response: data.complete_response,
                                            tone: data.tone,
                                            request_id: data.request_id,
                                            model: modelInfo,
                                        });
                                    } else if (data.type === "error") {
                                        reject(
                                            new Error(
                                                data.error || "Streaming error",
                                            ),
                                        );
                                    }
                                } catch (parseError) {
                                    console.warn("Parse error:", parseError);
                                }
                            }

                            startPos = newlinePos + 1;
                            newlinePos = processingBuffer.indexOf(
                                "\n",
                                startPos,
                            );
                        }

                        processingBuffer = processingBuffer.substring(startPos);
                    }
                }

                // finalizer — the stream is DONE (readyState 4)
                // but no "complete"/"error" event ever settled the promise.
                // Previously this hung forever = a silent no-greeting. Settle
                // it now with whatever text we streamed (or reject if none).
                if (xhr.readyState === 4 && !settled) {
                    if (abortedByUser) {
                        // Task #462 — user barge-in: never treat the remnant
                        // as a successful reply and never flush TTS (the
                        // interruption path already hard-stopped the speaker).
                        resolve({
                            status: "aborted",
                            response: streamingContent,
                            bubbleEl: streamBubbleEl,
                            request_id: requestId,
                            model: modelInfo,
                        });
                    } else if (streamingContent && streamingContent.trim()) {
                        console.warn(
                            "🎉 [HiddenStreaming] Stream ended without a 'complete' event — finalizing with partial content (HTTP",
                            xhr.status + ")",
                        );
                        if (
                            this.realtimeTTS &&
                            streamingStarted &&
                            enableAudio
                        ) {
                            try {
                                this.realtimeTTS.flushAndClose();
                            } catch (err) {}
                        }
                        resolve({
                            status: "success",
                            response: streamingContent,
                            request_id: requestId,
                            model: modelInfo,
                        });
                    } else {
                        reject(
                            new Error(
                                "Hidden stream (" +
                                    messageType +
                                    ") ended with no content (HTTP " +
                                    xhr.status +
                                    ")",
                            ),
                        );
                    }
                }
            };

            xhr.onerror = () => {
                reject(new Error("Network error during hidden streaming"));
            };

            xhr.ontimeout = () => {
                reject(new Error("Timeout during hidden streaming"));
            };

            // Task #462 — honor barge-in: handleUserInterruption()/stopAudio()
            // abort this.streamingController; wire that signal to the XHR
            // (same pattern as the typed-message path) so a hidden stream
            // actually stops instead of painting on. Settle the promise HERE,
            // directly — never rely on abort-time readystatechange quirks
            // (the spec's transient DONE→UNSENT reset varies by browser), or
            // an interrupted check-in/greeting could hang its caller forever.
            // resolve() is settled-guarded, so at most one settlement wins.
            // Callers stay silent on "aborted" (no fallback, no history add:
            // interruption capture already preserved any partial line).
            const settleAborted = () => {
                abortedByUser = true;
                resolve({
                    status: "aborted",
                    response: streamingContent,
                    bubbleEl: streamBubbleEl,
                    request_id: requestId,
                    model: modelInfo,
                });
            };
            try {
                this.streamingController.signal.addEventListener(
                    "abort",
                    () => {
                        // Flag FIRST: xhr.abort() synchronously fires the
                        // transient readyState-4 in most browsers, and that
                        // finalizer must classify it as aborted (not as an
                        // HTTP-0 "no content" failure).
                        abortedByUser = true;
                        try {
                            xhr.abort();
                        } catch (e) {}
                        // Explicit settlement for browsers where abort fires
                        // no readystatechange at all (settled-guarded no-op
                        // when the finalizer above already resolved).
                        settleAborted();
                    },
                );
            } catch (e) {}
            // Belt-and-braces: any other xhr.abort() caller lands here too.
            // (The 403 "blocked" path aborts AFTER resolving blocked, so the
            // settled guard keeps this a no-op there.)
            xhr.onabort = () => settleAborted();

            // Get character info for the API
            const characterName = this.getCurrentCharacterName();
            const characterId = this.getCurrentCharacterId();

            // Safari-safe: send ONLY the IANA timezone. The server computes
            // the actual hour from its own UTC clock projected into that zone.
            const clientTime = getClientTimeInfo();
            const clientTimezone = clientTime ? clientTime.timeZone : null;

            // Build request body — include book_context when the EPUB
            // reader is open for the current character, mirroring the
            // main user-message XHR site so the avatar can answer
            // page-grounded greetings (e.g. "I just opened your book").
            const greetingBody = {
                message: message,
                character_name: characterName,
                character_id: characterId,
                conversation_history: this.conversationHistory,
                message_type: messageType,
                client_timezone: clientTimezone,
            };
            if (window.ActiveBookContext) {
                const bc = window.ActiveBookContext;
                if (
                    bc.character_id &&
                    characterId &&
                    bc.character_id === characterId
                ) {
                    greetingBody.book_context = {
                        character_id: bc.character_id,
                        title: bc.title,
                        author: bc.author,
                        chapter: bc.chapter,
                        location: bc.location,
                        excerpt: bc.excerpt,
                        prev_tail: bc.prev_tail || "",
                        next_head: bc.next_head || "",
                        selection: bc.selection || "",
                        now_reading: bc.now_reading || "",
                        progress_pct: bc.progress_pct || 0,
                        page_hash: bc.page_hash || "",
                        page_unchanged: !!bc.page_unchanged,
                    };
                } else if (bc.character_id && characterId && bc.character_id !== characterId) {
                    window.ActiveBookContext = null;
                }
            }
            xhr.send(JSON.stringify(greetingBody));
        });
    }

    // Prep realtime TTS for a brand-new assistant turn
    async prepTTSForNewTurn() {
        console.log("🎤 Preparing TTS for new turn");
        const rtts = this.realtimeTTS || this.tts || null;
        if (!rtts) return;

        try {
            // For RealtimeLLMToElevenLabs: explicitly reset TTS to accept new audio
            if (rtts && typeof rtts.stop === "function") {
                // Call the new stop() method which properly resets all flags
                await rtts.stop();
                console.log("✅ RealtimeLLMToElevenLabs stopped and reset");
            }

            // NOTE: We do NOT call allowUpcoming() here anymore.
            // The drop shield will be automatically reset by handleLLMDelta()
            // when the first text chunk of the NEW response arrives.
            // This prevents any lingering audio chunks from the previous
            // (stopped) response from sneaking through.

            // Check if ensureReadyForNextResponse method exists (RealtimeLLMToElevenLabs has it, ElevenLabsTTS doesn't)
            if (rtts && typeof rtts.ensureReadyForNextResponse === "function") {
                // Standard TTS preparation for RealtimeLLMToElevenLabs
                await rtts.ensureReadyForNextResponse(120);
                console.log("[prepTTSForNewTurn] TTS ready for new turn");
            } else if (rtts && rtts.audioCtx) {
                // Fallback for ElevenLabsTTS which doesn't have ensureReadyForNextResponse
                // but may still need audio context resume
                if (rtts.audioCtx.state !== "running") {
                    console.log(
                        `[prepTTSForNewTurn] Audio context is ${rtts.audioCtx.state}, resuming...`,
                    );
                    await rtts.audioCtx.resume();
                    console.log(
                        "[prepTTSForNewTurn] AudioContext resumed successfully",
                    );
                }
            }

            // Only do Safari-specific recovery if the stop button was pressed
            // This avoids interfering with normal audio playback
            if (this.isSafari && this.stopButtonPressed) {
                console.log(
                    "🔧 Safari: Stop button recovery - ensuring audio system is fully reset",
                );

                // Force recreation of audio elements for Safari to ensure clean state
                if (
                    this.activeAudioChunks &&
                    this.activeAudioChunks.length > 0
                ) {
                    console.log("🔧 Safari: Clearing lingering audio elements");
                    this.activeAudioChunks = [];
                }

                // Force audio context recreation for Safari if it was suspended
                if (rtts && rtts.audioCtx) {
                    if (rtts.audioCtx.state === "suspended") {
                        console.log(
                            "🔧 Safari: Audio context is suspended, resuming...",
                        );
                        await rtts.audioCtx.resume();
                        console.log(
                            "🔧 Safari: Audio context resumed successfully",
                        );
                    }

                    // For RealtimeLLMToElevenLabs, force a full reset if available
                    if (typeof rtts.resetAudioSystem === "function") {
                        console.log(
                            "🔧 Safari: Forcing full audio system reset",
                        );
                        await rtts.resetAudioSystem();
                    }
                }

                // Clear the flag AFTER successful recovery
                this.stopButtonPressed = false;
                console.log(
                    "🔧 Safari: Stop button recovery complete, flag cleared",
                );
            }
        } catch (e) {
            console.warn("[prepTTSForNewTurn] TTS prep failed (continuing)", e);

            // Only attempt to recreate audio context if it's truly closed (for any browser)
            if (rtts && rtts.audioCtx && rtts.audioCtx.state === "closed") {
                console.log(
                    "[prepTTSForNewTurn] Attempting to create new AudioContext as fallback",
                );
                try {
                    rtts.audioCtx = new (window.AudioContext ||
                        window.webkitAudioContext)();
                    await rtts.audioCtx.resume();
                    console.log(
                        "[prepTTSForNewTurn] New AudioContext created and resumed",
                    );
                } catch (fallbackErr) {
                    console.error(
                        "Failed to create new AudioContext",
                        fallbackErr,
                    );
                }
            }
        }
    }

    async handleChunkedAudioResponse(data) {
        console.log("Handling chunked audio response:", data);

        try {
            // Track the current request for interruption capabilities
            this.currentRequestId = data.request_id;
            this.isChunkedAudioActive = true;
            this.isAudioPlaying = true;
            this.isProcessing = true;

            // AEC (Acoustic Echo Cancellation) is now handling echo prevention
            console.log(
                "🎵 Chunked AI audio starting - AEC will prevent echo automatically",
            );

            // Emotion controller removed in Task #38 — A2F renderer
            // drives facial expression on its own.

            // Show stop button and hide microphone videos (but keep container visible)
            if (this.stopAudioButton) {
                this.stopAudioButton.classList.remove("d-none");
                // Hide send button when stop button is shown
            }
            if (this.sendButton) {
                this.sendButton.classList.remove("d-none");
            }

            // Keep microphone button container accessible during chunked audio

            // Ensure microphone button container remains visible and accessible during audio
            if (this.micButtonContainer) {
                this.micButtonContainer.style.visibility = "visible";
                this.micButtonContainer.style.pointerEvents = "auto";
            }

            if (this.animationController) {
                this.animationController.onAudioPlaybackStart();
            }

            // Poll for chunked audio status
            await this.pollChunkedAudioStatus(
                data.request_id,
                data.total_chunks,
            );
        } catch (error) {
            console.error("Error handling chunked audio:", error);
            this.stopAudio();
        }
    }

    async pollChunkedAudioStatus(requestId, totalChunks) {
        console.log(`Starting chunked audio polling for request ${requestId}`);

        let currentChunk = -1;
        let isPlaying = false;
        let isPollingActive = true;
        let consecutiveErrors = 0;
        let maxConsecutiveErrors = 10; // Increased error tolerance
        let playbackStarted = false;

        const pollChunks = async () => {
            // Check if chunked audio has been interrupted or audio stopped
            if (
                !isPollingActive ||
                !this.isChunkedAudioActive ||
                !this.isAudioPlaying
            ) {
                console.log(
                    "🛑 Polling stopped - interrupted, completed, or audio stopped",
                );
                return;
            }

            try {
                // First check if playback is ready to start
                if (!playbackStarted) {
                    // Additional safety check before making API call
                    if (!this.isAudioPlaying || !this.isChunkedAudioActive) {
                        console.log(
                            "🛑 Audio stopped during status check - aborting",
                        );
                        return;
                    }

                    const statusResponse = await fetch(
                        `/api/chunked-audio/status/${requestId}`,
                    );
                    const statusResult = await statusResponse.json();

                    if (statusResult.ready_for_playback) {
                        console.log(
                            `✅ Ready for playback: ${statusResult.processed_chunks}/${statusResult.total_chunks} chunks ready (threshold: ${statusResult.min_chunks_threshold})`,
                        );
                        playbackStarted = true;
                    } else {
                        console.log(
                            `⏳ Waiting for minimum chunks: ${statusResult.processed_chunks}/${statusResult.min_chunks_threshold} chunks ready`,
                        );
                        this.pollingTimeoutId = setTimeout(pollChunks, 500);
                        return;
                    }
                }

                // Safety check before fetching next chunk
                if (!this.isAudioPlaying || !this.isChunkedAudioActive) {
                    console.log(
                        "🛑 Audio stopped before chunk fetch - aborting",
                    );
                    return;
                }

                // Check for next available chunk
                const response = await fetch(
                    `/api/chunked-audio/next-chunk/${requestId}?last_played_chunk=${currentChunk}`,
                );
                const result = await response.json();

                if (result.status === "available") {
                    const chunkData = result.chunk_data;

                    // Final safety check before playing chunk
                    if (!this.isAudioPlaying || !this.isChunkedAudioActive) {
                        console.log(
                            "🛑 Audio stopped before chunk playback - aborting chunk:",
                            chunkData.chunk_index,
                        );
                        return;
                    }

                    console.log(
                        `Playing chunk ${chunkData.chunk_index}: "${chunkData.chunk_text.substring(0, 50)}..."`,
                    );

                    // Reset error counter on successful chunk
                    consecutiveErrors = 0;

                    // Play this chunk
                    await this.playAudioChunk(chunkData);
                    currentChunk = chunkData.chunk_index;

                    // If this was the last chunk, we're done
                    if (
                        chunkData.is_complete ||
                        currentChunk >= totalChunks - 1
                    ) {
                        console.log("All chunks completed");
                        isPollingActive = false;
                        this.isChunkedAudioActive = false;
                        this.currentRequestId = null;
                        this.stopAudio();

                        // Clean up server resources
                        fetch(`/api/chunked-audio/cleanup/${requestId}`, {
                            method: "POST",
                        }).catch((error) =>
                            console.error(
                                "Error cleaning up chunked audio:",
                                error,
                            ),
                        );
                        return;
                    }

                    // Wait briefly before checking for next chunk to allow animation transition
                    this.pollingTimeoutId = setTimeout(pollChunks, 800);
                } else {
                    // Chunk not ready yet, check again but reset error counter
                    consecutiveErrors = 0;
                    console.log(
                        `Chunk ${currentChunk + 1} not ready yet, waiting...`,
                    );
                    this.pollingTimeoutId = setTimeout(pollChunks, 1000);
                }
            } catch (error) {
                console.error("Error polling for chunks:", error);
                consecutiveErrors++;

                if (consecutiveErrors >= maxConsecutiveErrors) {
                    console.error(
                        `Too many consecutive errors (${consecutiveErrors}), stopping polling`,
                    );
                    isPollingActive = false;
                    this.stopAudio();
                    return;
                }

                this.pollingTimeoutId = setTimeout(pollChunks, 2000);
            }
        };

        pollChunks();
    }

    async playAudioChunk(chunkData) {
        try {
            // Safety check: Don't process chunk if audio has been stopped
            if (!this.isAudioPlaying || !this.isChunkedAudioActive) {
                console.log(
                    `🛑 Chunk ${chunkData.chunk_index} skipped - audio stopped`,
                );
                return Promise.resolve();
            }

            // Convert base64 to audio blob
            const audioBlob = this.base64ToBlob(
                chunkData.audio_base64,
                "audio/mpeg",
            );
            const audioUrl = URL.createObjectURL(audioBlob);

            // Create audio element - same approach for all browsers
            const audio = new Audio(audioUrl);
            audio.preload = "auto";

            // Apply current volume level if volume controller is available
            if (window.volumeController) {
                audio.volume = window.volumeController.getCurrentVolume();
                console.log(
                    `Set volume for chunk ${chunkData.chunk_index}: ${Math.round(audio.volume * 100)}%`,
                );
            }

            // IMPORTANT: Make audio globally accessible for volume control
            window.currentlyPlayingAudio = audio;

            // Track this audio element for immediate stopping if needed
            this.activeAudioChunks.push(audio);

            return new Promise((resolve, reject) => {
                audio.addEventListener("loadeddata", () => {
                    // Check if audio was stopped while loading
                    if (!this.isAudioPlaying || !this.isChunkedAudioActive) {
                        console.log(
                            `🛑 Chunk ${chunkData.chunk_index} cancelled during load`,
                        );
                        URL.revokeObjectURL(audioUrl);
                        resolve();
                        return;
                    }
                    console.log(
                        `Chunk ${chunkData.chunk_index} loaded and ready to play`,
                    );
                });

                audio.addEventListener("canplaythrough", async () => {
                    try {
                        // Final safety check before playing
                        if (
                            !this.isAudioPlaying ||
                            !this.isChunkedAudioActive
                        ) {
                            console.log(
                                `🛑 Chunk ${chunkData.chunk_index} cancelled before playback`,
                            );
                            URL.revokeObjectURL(audioUrl);
                            resolve();
                            return;
                        }
                        // Debug: Log the chunk data structure
                        console.log(
                            `Chunk ${chunkData.chunk_index} data keys:`,
                            Object.keys(chunkData),
                        );
                        console.log(
                            `Chunk ${chunkData.chunk_index} phoneme_sequence available:`,
                            !!chunkData.phoneme_sequence,
                        );
                        console.log(
                            `Chunk ${chunkData.chunk_index} duration_sequence available:`,
                            !!chunkData.duration_sequence,
                        );

                        if (chunkData.phoneme_sequence) {
                            console.log(
                                `Chunk ${chunkData.chunk_index} phoneme_sequence length:`,
                                chunkData.phoneme_sequence.length,
                            );
                            console.log(
                                `Chunk ${chunkData.chunk_index} first 5 phonemes:`,
                                chunkData.phoneme_sequence.slice(0, 5),
                            );
                        }

                        if (chunkData.duration_sequence) {
                            console.log(
                                `Chunk ${chunkData.chunk_index} duration_sequence length:`,
                                chunkData.duration_sequence.length,
                            );
                            console.log(
                                `Chunk ${chunkData.chunk_index} first 5 durations:`,
                                chunkData.duration_sequence.slice(0, 5),
                            );
                        }

                        // Start speak animation for each chunk
                        if (this.animationController) {
                            if (chunkData.chunk_index === 0) {
                                console.log(
                                    "🎬 Starting speak animation for first chunk",
                                );
                            } else {
                                console.log(
                                    `🎬 Restarting speak animation for chunk ${chunkData.chunk_index}`,
                                );
                            }
                            await this.animationController.playSpeakAnimation();
                        }

                        // Start lip sync for this chunk using the correct LipSyncController method
                        if (
                            this.lipSyncController &&
                            chunkData.phoneme_sequence &&
                            chunkData.duration_sequence
                        ) {
                            console.log(
                                `🎬 Starting lip sync for chunk ${chunkData.chunk_index} with ${chunkData.phoneme_sequence.length} phonemes`,
                            );
                            await this.lipSyncController.processElevenLabsSequence(
                                chunkData.phoneme_sequence,
                                chunkData.duration_sequence,
                                audio,
                            );
                        } else {
                            console.warn(
                                `❌ No lip sync data available for chunk ${chunkData.chunk_index}:`,
                                {
                                    hasLipSyncController:
                                        !!this.lipSyncController,
                                    hasPhonemeSequence:
                                        !!chunkData.phoneme_sequence,
                                    hasDurationSequence:
                                        !!chunkData.duration_sequence,
                                },
                            );
                        }

                        // Play audio - same approach for all browsers
                        try {
                            await audio.play();
                            console.log(
                                `✅ Playing chunk ${chunkData.chunk_index}`,
                            );
                        } catch (playError) {
                            // Only do Safari-specific retry if we get a specific NotAllowedError
                            if (
                                this.isSafari &&
                                playError.name === "NotAllowedError"
                            ) {
                                console.log(
                                    "🔧 Safari: NotAllowedError - attempting context resume",
                                );
                                if (
                                    this.realtimeTTS &&
                                    this.realtimeTTS.audioCtx
                                ) {
                                    await this.realtimeTTS.audioCtx.resume();
                                    await audio.play();
                                    console.log(
                                        "🔧 Safari: Retry successful after context resume",
                                    );
                                } else {
                                    throw playError;
                                }
                            } else {
                                throw playError;
                            }
                        }
                    } catch (playError) {
                        console.error(
                            `Error playing chunk ${chunkData.chunk_index}:`,
                            playError,
                        );
                        reject(playError);
                    }
                });

                audio.addEventListener("ended", () => {
                    console.log(
                        `Chunk ${chunkData.chunk_index} playback ended`,
                    );

                    // Clear global audio reference if this is the current one
                    if (window.currentlyPlayingAudio === audio) {
                        window.currentlyPlayingAudio = null;
                    }

                    // Remove this audio element from active chunks tracking
                    const index = this.activeAudioChunks.indexOf(audio);
                    if (index > -1) {
                        this.activeAudioChunks.splice(index, 1);
                    }

                    // Briefly transition to idle between chunks for smooth animation
                    if (this.animationController && !chunkData.is_complete) {
                        console.log("🎬 Transitioning to idle between chunks");
                        this.animationController.playIdleAnimation();
                    }

                    URL.revokeObjectURL(audioUrl);
                    resolve();
                });

                audio.addEventListener("error", (error) => {
                    console.error(
                        `Audio error for chunk ${chunkData.chunk_index}:`,
                        error,
                    );
                    URL.revokeObjectURL(audioUrl);
                    reject(error);
                });
            });
        } catch (error) {
            console.error(
                `Error playing audio chunk ${chunkData.chunk_index}:`,
                error,
            );
            throw error;
        }
    }

    base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: mimeType });
    }

    /**
     * Comprehensive interruption handler for when user interrupts AI responses
     * This is called both from voice detection and manual stop buttons
     */
    handleUserInterruption() {
        if (this._handlingInterruption) return; // guard: one interruption at a time
        this._handlingInterruption = true;

        console.log("🚨 USER BARGE-IN — hard-stopping all AI audio/streams");

        const cooldownMs = 250;

        try {
            // 1) Tell realtime TTS to stop FIRST (so late frames get dropped)
            //    - flush + block frames, fade/stop current nodes, zero lips, reset stop-gate, flip idle
            const rtts = this.realtimeTTS || this.tts || null; // tolerate older field name
            if (rtts && typeof rtts.stopNow === "function") {
                // Task #274 (iOS jetsam): on touch devices, ALSO hard-close
                // the ElevenLabs WS. Without it the interrupted reply keeps
                // streaming its remaining audio, which gets shield-dropped
                // chunk by chunk — pointless network/decode/GC churn on a
                // phone already under memory pressure (confirmed
                // [PAGE-KILL] beacon during a mic barge-in). Desktop keeps
                // the cheaper soft stop (persistent WS stays open).
                const hardClose =
                    "ontouchstart" in window ||
                    (navigator.maxTouchPoints || 0) > 0;
                rtts.stopNow({ fadeMs: 120, hard: hardClose }).catch(() => {});
            } else if (this.tts && typeof this.tts.interrupt === "function") {
                // legacy compatibility
                this.tts.interrupt({ fadeMs: 120 });
            }

            // 2) Stop any local/non-Realtime audio (your existing helper)
            try {
                this.stopAudio?.();
            } catch {}

            // 3) Stop any greeting subsystem quickly
            try {
                const gs = this.modelViewer?.greetingService;
                if (gs && typeof gs.stop === "function") {
                    gs.stop();
                    console.log("🛑 Greeting audio/lipsync stopped");
                }
            } catch {}

            // 4) Notify new TTS manager (if you keep that layer)
            if (this.realtimeTTSEnabled && this.ttsManager) {
                try {
                    this.ttsManager.onUserBargeIn?.();
                } catch (e) {
                    console.warn("🎵 [TTS Manager] onUserBargeIn failed:", e);
                }
            }

            // 5) CRITICAL: Prepare TTS for next response after stopping
            // This ensures _droppingIncomingAudio flag is reset to false
            setTimeout(async () => {
                await this.prepTTSForNewTurn();
                console.log("✅ TTS prepared for new turn after interruption");
            }, cooldownMs);

            // 5) Cancel backend LLM streaming; optionally keep partial text
            try {
                if (this.streamingController && this.currentStreamingMessage) {
                    const partial = this.getPartialStreamingContent?.();
                    if (partial && partial.trim()) {
                        this.addToConversationHistory?.(
                            "assistant",
                            `[Interrupted mid-response] ${partial}`,
                        );
                    }
                    try {
                        this.streamingController.abort();
                    } catch {}
                    this.streamingController = null;
                    this.currentStreamingMessage = null;
                    console.log(
                        "🛑 LLM stream aborted due to user interruption",
                    );
                }
            } catch (e) {
                console.warn("LLM abort error:", e);
            }

            // 6) UI tidy: loaders, typing dots, etc.
            try {
                if (this.loadingIndicator) {
                    this.loadingIndicator.style.display = "none";
                    this.loadingIndicator.classList.remove("show");
                }
                this.stopLoadingTextAnimation?.();
            } catch {}

            // 7) Transition avatar/UI to “listening” state (no mouth movement)
            try {
                // If you have a dedicated “listen” anim:
                this.animationController?.playListenAnimation?.();
                // Emotion controller removed in Task #38.
            } catch {}

            // 8) Flip controller state flags
            this.isAudioPlaying = false;
            this.isProcessing = false;

            // 9) Optional: briefly force the mic UI into “ready to listen”
            try {
                this.transitionToListeningAfterInterruption?.();
            } catch {}

            console.log(
                "✅ Interruption handled: system is ready for new input",
            );
        } finally {
            // tiny cooldown so rapid double taps don’t re-enter
            setTimeout(() => {
                this._handlingInterruption = false;
            }, cooldownMs);
        }
    }

    /**
     * Guarantee transition to listening animation after any interruption occurs
     * This ensures the model ALWAYS shows listening animation regardless of current state
     */
    async transitionToListeningAfterInterruption() {
        if (!this.animationController) {
            console.log(
                "⚠️ No animation controller available for listening transition",
            );
            return;
        }

        const currentState = this.animationController.animationState;
        console.log(
            `🚨 GUARANTEED LISTENING: Forcing transition from ${currentState} to LISTEN`,
        );

        try {
            // Force listening animation regardless of current state
            await this.animationController.handleInterruptionTransition();
            console.log(
                "👂 GUARANTEED listening animation activated after interruption",
            );

            // CRITICAL: Only update UI to listening state if speech recognition is actually active
            // This prevents phantom listening state from interruption handler
            if (this.isListening) {
                this.updateListeningUI();
                console.log(
                    "🎯 UI updated to show listening state after interruption",
                );
            } else {
                console.log(
                    "🎯 Skipping listening UI update - no active speech recognition",
                );
            }
        } catch (error) {
            console.error(
                "❌ Error with optimized interruption transition:",
                error,
            );

            // Emergency fallback 1: Direct listening animation call
            try {
                console.log(
                    "🚨 EMERGENCY FALLBACK 1: Direct listening animation",
                );
                await this.animationController.playListenAnimation();
                console.log("✅ Emergency listening animation activated");

                // Update UI only if speech recognition is actually active
                if (this.isListening) {
                    this.updateListeningUI();
                    console.log("🎯 UI updated via emergency fallback");
                } else {
                    console.log(
                        "🎯 Skipping UI update in fallback - no active recognition",
                    );
                }
            } catch (fallbackError) {
                console.error(
                    "❌ Emergency listening failed, trying direct force:",
                    fallbackError,
                );

                // Emergency fallback 2: Force listening animation directly
                try {
                    console.log(
                        "🚨 EMERGENCY FALLBACK 2: Force direct listening animation",
                    );
                    this.forceListeningAnimationDirect();
                    console.log(
                        "✅ Direct force listening animation activated",
                    );

                    // Update UI only if speech recognition is actually active
                    if (this.isListening) {
                        this.updateListeningUI();
                        console.log("🎯 UI updated via direct force fallback");
                    } else {
                        console.log(
                            "🎯 Skipping UI update in direct force - no active recognition",
                        );
                    }
                } catch (directError) {
                    console.error(
                        "❌ Direct force failed, using idle as last resort:",
                        directError,
                    );

                    // Last resort: idle animation
                    try {
                        await this.animationController.playIdleAnimation();
                        console.log("🔄 Last resort: idle animation activated");
                        this.updateStoppedListeningUI();
                    } catch (idleError) {
                        console.error(
                            "❌ CRITICAL: All animation fallbacks failed:",
                            idleError,
                        );
                    }
                }
            }
        }
    }

    /**
     * Emergency method to force listening animation directly when all other methods fail
     * This bypasses all normal animation transition logic
     */
    forceListeningAnimationDirect() {
        if (
            !this.animationController ||
            !this.animationController.trimmedAnimations
        ) {
            throw new Error("Animation controller or animations not available");
        }

        console.log(
            "🚨 FORCING listening animation directly - bypassing all transition logic",
        );

        const listenClip =
            this.animationController.trimmedAnimations.get("listen");
        if (!listenClip) {
            throw new Error("No listening animation clip available");
        }

        // Stop current animation immediately if it exists
        if (this.animationController.currentAction) {
            this.animationController.currentAction.stop();
        }

        // Force listening animation directly
        const listenAction =
            this.animationController.mixer.clipAction(listenClip);
        listenAction.setLoop(THREE.LoopRepeat);
        listenAction.reset();
        listenAction.setEffectiveWeight(1);
        listenAction.play();

        // Update controller state
        this.animationController.currentAction = listenAction;
        this.animationController.animationState = "LISTEN";
        this.animationController.isTTSActive = false;

        console.log(
            "🚨 DIRECT FORCE: Listening animation activated successfully",
        );
    }

    async stopAudioOnly() {
        console.log(
            "🛑 Stopping audio playback only - microphone remains unaffected",
        );

        // FIRST: Immediately mark as stopped to prevent new chunk processing
        this.isAudioPlaying = false;
        this.isProcessing = false;

        // Cancel any ongoing streaming requests immediately
        if (this.streamingController) {
            console.log("🛑 Aborting ongoing streaming request");
            this.streamingController.abort();
            this.streamingController = null;
            this.currentStreamingMessage = null;
        }

        // Hide loading indicator immediately
        if (this.loadingIndicator) {
            this.loadingIndicator.style.display = "none";
            this.loadingIndicator.classList.remove("show");
        }

        // Stop loading text animation
        this.stopLoadingTextAnimation();

        // Cancel any pending TTS synthesis requests via ElevenLabsTTS abort controller
        if (this.tts && this.tts.abortController) {
            console.log("🛑 Aborting ongoing TTS synthesis request");
            this.tts.abortController.abort();
        }

        // Stop chunked audio processing if active
        if (this.isChunkedAudioActive && this.currentRequestId) {
            console.log(
                "🛑 Stopping chunked audio processing for request:",
                this.currentRequestId,
            );
            this.isChunkedAudioActive = false;

            // Cancel any pending polling timeout
            if (this.pollingTimeoutId) {
                clearTimeout(this.pollingTimeoutId);
                this.pollingTimeoutId = null;
                console.log("⏹️ Cancelled pending polling timeout");
            }

            // Clean up server resources for interrupted request
            fetch(`/api/chunked-audio/cleanup/${this.currentRequestId}`, {
                method: "POST",
            })
                .then(() =>
                    console.log("✅ Chunked audio resources cleaned up"),
                )
                .catch((error) =>
                    console.error("Error cleaning up chunked audio:", error),
                );

            this.currentRequestId = null;
        }

        // Stop all active audio chunks immediately
        if (this.activeAudioChunks.length > 0) {
            console.log(
                `🛑 Force stopping ${this.activeAudioChunks.length} active audio chunks`,
            );
            this.activeAudioChunks.forEach((audio, index) => {
                try {
                    // Force immediate stop by setting currentTime to end
                    audio.pause();
                    audio.currentTime = audio.duration || 0;

                    if (this.isSafari) {
                        // Safari-specific: Create a new silent audio element to replace the old one
                        // This ensures Safari properly resets its internal audio state
                        console.log(
                            `🔧 Safari: Recreating audio element ${index} for proper reset`,
                        );

                        // Remove all event listeners first
                        audio.removeEventListener("ended", () => {});
                        audio.removeEventListener("error", () => {});

                        // For Safari, set a data URL with silence instead of empty string
                        // This prevents Safari from getting stuck in a bad state
                        const silentAudioData =
                            "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
                        audio.src = silentAudioData;
                        audio.load();

                        // Give Safari a moment to process the change
                        setTimeout(() => {
                            audio.src = "";
                            audio.removeAttribute("src");
                        }, 10);
                    } else {
                        // Clear the source and load empty data to force stop (non-Safari browsers)
                        audio.src = "";
                        audio.load(); // Force reload to clear buffer
                    }

                    console.log(`✅ Stopped audio chunk ${index}`);
                } catch (error) {
                    console.error(
                        `Error stopping audio chunk ${index}:`,
                        error,
                    );
                }
            });
            this.activeAudioChunks = [];
        }

        // Safari-specific: Only suspend audio context if we're truly stopping and it's Safari
        // This should ONLY happen when the stop button was explicitly pressed by the user
        // DO NOT suspend the audio context for normal audio transitions
        if (
            this.isSafari &&
            this.realtimeTTS &&
            this.realtimeTTS.audioCtx &&
            this.stopButtonPressed
        ) {
            console.log(
                "🔧 Safari: Stop button pressed - suspending audio context for reset",
            );
            const audioCtx = this.realtimeTTS.audioCtx;
            if (audioCtx.state === "running") {
                // Safari sometimes needs a suspend/resume cycle to properly reset ONLY after stop button
                audioCtx
                    .suspend()
                    .then(() => {
                        console.log(
                            "🔧 Safari: Audio context suspended after stop button press",
                        );
                        // Will be resumed in prepTTSForNewTurn() when needed
                    })
                    .catch((err) => {
                        console.warn(
                            "Safari audio context suspend warning:",
                            err,
                        );
                    });
            }
            // DO NOT clear the flag here - it needs to persist until the next audio request
            // so prepTTSForNewTurn() knows Safari needs special recovery
            // this.stopButtonPressed = false; // Commented out to fix Safari audio issue
        }

        // Stop TTS if it exists
        if (this.tts) {
            try {
                this.tts.stop();
                console.log("🛑 TTS stopped");
            } catch (error) {
                console.warn("TTS stop warning:", error);
            }
        }

        // Stop lip sync animation immediately
        if (this.lipSyncController) {
            this.lipSyncController.stopAnimation();
            console.log("🛑 Lip sync animation stopped");
        }

        // Immediately transition to idle animation from speak animation
        if (this.animationController) {
            console.log("🎬 Forcing transition from SPEAK to IDLE animation");
            this.animationController.onTTSEnd();
            // Force play idle animation to ensure smooth transition
            await this.animationController.playIdleAnimation();
            console.log("🎬 Idle anim�ation activated");
        }

        // Emotion controller removed in Task #38 — A2F renderer handles
        // facial expression and decays back to neutral on its own.

        // Ensure stop button is hidden (already handled in click event, but double-check)
        if (
            this.stopAudioButton &&
            !this.stopAudioButton.classList.contains("d-none")
        ) {
            this.stopAudioButton.classList.add("d-none");
            console.log("🛑 Stop button hidden (backup check)");
            // Show send button when stop button is hidden
            if (this.sendButton) {
                this.sendButton.classList.remove("d-none");
            }
        }

        // Note: DON'T reset microphone listening state - microphone should remain active
        // The microphone may still be listening and should maintain its current state

        // Ensure microphone button container is visible and interactive (without changing listening state)
        if (this.micButtonContainer) {
            this.micButtonContainer.classList.remove("d-none");
            this.micButtonContainer.style.display = ""; // Remove any inline display:none
            console.log("🎤 Microphone button container kept visible");
        }

        // Ensure microphone button itself is visible and clickable (without changing listening state)
        if (this.micButton) {
            this.micButton.classList.remove("d-none", "disabled");
            this.micButton.style.display = ""; // Remove any inline display:none
            this.micButton.disabled = false; // Ensure it's not disabled
            console.log("🎤 Microphone button kept clickable");
        }

        // Reset any audio-related states that might affect UI visibility
        this.lastAudioResponse = null;
        this.currentAudioUrl = null;

        console.log("🎵 Audio stopped - microphone state preserved");


        // Note: Don't call handleQAModeOnAIComplete() to avoid microphone restart logic

        console.log("✅ Audio-only stop complete - microphone unaffected");
    }

    async stopAudio() {
        console.log(
            "🛑 Stopping audio playback - comprehensive stop initiated",
        );

        // FIRST: Immediately mark as stopped to prevent new chunk processing
        this.isAudioPlaying = false;
        this.isProcessing = false;

        // Cancel any ongoing streaming requests immediately
        if (this.streamingController) {
            console.log("🛑 Aborting ongoing streaming request");
            this.streamingController.abort();
            this.streamingController = null;
            this.currentStreamingMessage = null;
        }

        // Hide loading indicator immediately
        if (this.loadingIndicator) {
            this.loadingIndicator.style.display = "none";
            this.loadingIndicator.classList.remove("show");
        }

        // Stop loading text animation
        this.stopLoadingTextAnimation();

        // Cancel any pending TTS synthesis requests via ElevenLabsTTS abort controller
        if (this.tts && this.tts.abortController) {
            console.log("🛑 Aborting ongoing TTS synthesis request");
            this.tts.abortController.abort();
        }

        // Stop chunked audio processing if active
        if (this.isChunkedAudioActive && this.currentRequestId) {
            console.log(
                "🛑 Stopping chunked audio processing for request:",
                this.currentRequestId,
            );
            this.isChunkedAudioActive = false;

            // Cancel any pending polling timeout
            if (this.pollingTimeoutId) {
                clearTimeout(this.pollingTimeoutId);
                this.pollingTimeoutId = null;
                console.log("⏹️ Cancelled pending polling timeout");
            }

            // Clean up server resources for interrupted request
            fetch(`/api/chunked-audio/cleanup/${this.currentRequestId}`, {
                method: "POST",
            })
                .then(() =>
                    console.log("✅ Chunked audio resources cleaned up"),
                )
                .catch((error) =>
                    console.error("Error cleaning up chunked audio:", error),
                );

            this.currentRequestId = null;
        }

        // Stop all active audio chunks immediately
        if (this.activeAudioChunks.length > 0) {
            console.log(
                `🛑 Force stopping ${this.activeAudioChunks.length} active audio chunks`,
            );
            this.activeAudioChunks.forEach((audio, index) => {
                try {
                    // Force immediate stop by setting currentTime to end
                    audio.pause();
                    audio.currentTime = audio.duration || 0;

                    // Clear the source and load empty data to force stop
                    audio.src = "";
                    audio.load(); // Force reload to clear buffer

                    console.log(`✅ Stopped audio chunk ${index}`);
                } catch (error) {
                    console.error(
                        `Error stopping audio chunk ${index}:`,
                        error,
                    );
                }
            });

            // Clear the tracking array
            this.activeAudioChunks = [];
        }

        // Stop any active greeting audio and lipsync
        if (this.modelViewer && this.modelViewer.greetingService) {
            this.modelViewer.greetingService.stop();
            console.log(
                "🛑 Stopped greeting audio and lipsync via stop button",
            );
        }

        // Stop TTS
        if (this.tts) {
            this.tts.stop();
            console.log("🛑 TTS stopped");
        }

        // Stop lip sync animation immediately
        if (this.lipSyncController) {
            this.lipSyncController.stopAnimation();
            console.log("🛑 Lip sync animation stopped");
        }

        // Immediately transition to idle animation from speak animation
        if (this.animationController) {
            //console.log("🎬 Forcing transition from SPEAK to IDLE animation");
            // this.animationController.onTTSEnd();
            // Force play idle animation to ensure smooth transition
            // await this.animationController.playIdleAnimation();
            // console.log("🎬 Idle animation activated");
        }

        // Emotion controller removed in Task #38 — A2F renderer handles
        // facial expression and decays back to neutral on its own.

        // Ensure stop button is hidden (already handled in click event, but double-check)
        if (
            this.stopAudioButton &&
            !this.stopAudioButton.classList.contains("d-none")
        ) {
            this.stopAudioButton.classList.add("d-none");
            console.log("🛑 Stop button hidden (backup check)");
            // Show send button when stop button is hidden
            if (this.sendButton) {
                this.sendButton.classList.remove("d-none");
            }
        }

        // Note: Don't reset microphone listening state here - stop button should only stop audio playback
        // The microphone may still be actively listening and should maintain its state

        // Ensure microphone button container is visible and interactive
        if (this.micButtonContainer) {
            this.micButtonContainer.classList.remove("d-none");
            this.micButtonContainer.style.display = ""; // Remove any inline display:none
            console.log(
                "🎤 Microphone button container restored to visible state",
            );
        }

        // Ensure microphone button itself is visible and clickable
        if (this.micButton) {
            this.micButton.classList.remove("d-none", "disabled");
            this.micButton.style.display = ""; // Remove any inline display:none
            this.micButton.disabled = false; // Ensure it's not disabled
            console.log("🎤 Microphone button restored to clickable state");
        }

        // Reset any audio-related states that might affect UI visibility
        this.lastAudioResponse = null;
        this.currentAudioUrl = null;

        // AEC (Acoustic Echo Cancellation) handles echo prevention - no need to pause/resume
        console.log(
            "🎵 AI audio ended - AEC maintains continuous microphone operation",
        );


        // Handle Q&A mode when AI completes
        this.handleQAModeOnAIComplete();

        console.log("✅ Audio stop complete - all systems reset to idle state");
    }

    setupEventListeners() {
        // Handle clicks outside/inside to mark interaction & keep chat visible
        document.addEventListener("click", (e) => {
            // Only process if chat is visible
            if (
                this.chatContainer &&
                this.chatContainer.classList.contains("visible")
            ) {
                // Check if the click is inside interactive elements
                const chatContainerClicked = this.chatContainer.contains(
                    e.target,
                );
                const micContainerClicked = document
                    .querySelector(".floating-mic-container")
                    ?.contains(e.target);
                const characterPanelClicked = document
                    .getElementById("character-panel")
                    ?.contains(e.target);
                const characterPanelToggleClicked = document
                    .getElementById("character-panel-toggle")
                    ?.contains(e.target);
                const topCharacterPanelToggleClicked = document
                    .getElementById("top-character-panel-toggle")
                    ?.contains(e.target);
                const bioButtonClicked = document
                    .getElementById("bio-button")
                    ?.contains(e.target);

                // Any of these = user interacting with the avatar UI
                const interactedWithCharacterOrMic =
                    chatContainerClicked ||
                    micContainerClicked ||
                    characterPanelClicked ||
                    characterPanelToggleClicked ||
                    topCharacterPanelToggleClicked ||
                    bioButtonClicked;

                // 🔁 Reset Spawn idle timer when user interacts with these
                if (
                    interactedWithCharacterOrMic &&
                    this.realtimeTTS &&
                    typeof this.realtimeTTS.bumpIdleForUserInteraction ===
                        "function"
                ) {
                    this.realtimeTTS.bumpIdleForUserInteraction("ui-click");
                }

                // If click is outside all interactive elements (kept for logic clarity)
                const isOutsideInteractiveElements =
                    !chatContainerClicked &&
                    !micContainerClicked &&
                    !characterPanelClicked &&
                    !characterPanelToggleClicked &&
                    !topCharacterPanelToggleClicked &&
                    !bioButtonClicked;

                // Never hide chat window - keep it visible always for continuous Q&A mode
                console.log(
                    "Chat window remains visible - no auto-hiding in continuous Q&A mode",
                );
            }
        });

        // Handle exit chat button
        if (this.exitChatBtn) {
            this.exitChatBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log("🚪 Exit chat button clicked");

                // 🔁 User interaction → bump idle
                if (this.realtimeTTS?.bumpIdleForUserInteraction) {
                    this.realtimeTTS.bumpIdleForUserInteraction(
                        "exit-chat-click",
                    );
                }

                this.exitChatMode();
            });
        }

        // Handle stop button
        if (this.stopAudioButton) {
            this.stopAudioButton.addEventListener("click", async (e) => {
                e.preventDefault();
                e.stopPropagation();

                // 🔁 User interaction → bump idle
                if (this.realtimeTTS?.bumpIdleForUserInteraction) {
                    this.realtimeTTS.bumpIdleForUserInteraction("stop-click");
                }

                // Immediately hide the stop button to prevent double-click issues
                this.stopAudioButton.classList.add("d-none");
                console.log("🛑 Stop button clicked - stopping audio playback");

                // Show send button when stop button is hidden
                if (this.sendButton) {
                    this.sendButton.classList.remove("d-none");
                }

                // Set flag to indicate stop button was pressed (for Safari-specific handling)
                this.stopButtonPressed = true;

                // Stop only audio playback, don't affect microphone
                this.stopAudioOnly();

                // Prepare TTS for next response
                await this.prepTTSForNewTurn();
                console.log("✅ TTS prepared for new turn after stop button");

                // Stop greeting playback if available
                if (this.modelViewer && this.modelViewer.greetingService) {
                    this.modelViewer.greetingService.stop();
                    console.log("🛑 Greeting playback stopped via stop button");
                }

                // DO NOT send any message here - wait for user input
            });
        }

        // Handle send button
        if (this.sendButton && this.chatInput) {
            this.sendButton.addEventListener("click", async () => {
                const message = this.chatInput.value.trim();
                if (!message) return;

                // 🔁 User sending message = strong interaction
                if (this.realtimeTTS?.bumpIdleForUserInteraction) {
                    this.realtimeTTS.bumpIdleForUserInteraction("send-click");
                }

                // ✅ User gesture → prime Safari audio + new epoch
                await this.prepTTSForNewTurn();

                this.sendMessage(message);
            });
        }

        // Handle chat input events
        if (this.chatInput) {
            this.chatInput.addEventListener("focus", () => {
                console.log(
                    "Chat input focused - playing listen animation and showing chat window",
                );
                // Show chat container
                if (this.chatContainer) {
                    this.chatContainer.classList.add("visible");
                }
                if (
                    this.animationController &&
                    !this.isAudioPlaying &&
                    !TTSActive
                ) {
                    this.animationController.playListenAnimation();
                }

                // 🔁 User is clearly active → bump idle
                if (this.realtimeTTS?.bumpIdleForUserInteraction) {
                    this.realtimeTTS.bumpIdleForUserInteraction(
                        "chat-input-focus",
                    );
                }
            });

            this.chatInput.addEventListener("blur", () => {
                console.log(
                    "Chat input blurred - maintaining camera position until chat window is closed",
                );
                // Only play idle animation but keep camera and chat positions
                if (this.animationController && !this.isAudioPlaying) {
                    // this.animationController.playIdleAnimation();
                }
            });

            this.chatInput.addEventListener("keypress", (e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    const message = this.chatInput.value.trim();
                    if (!message) return;

                    // 🔁 Enter-send = user interaction
                    if (this.realtimeTTS?.bumpIdleForUserInteraction) {
                        this.realtimeTTS.bumpIdleForUserInteraction(
                            "chat-enter-send",
                        );
                    }

                    this.sendMessage(message);
                }
            });
        }

        // Task #530 — the circular button is the CALL control. A tap is NEVER
        // silently eaten: in-call → hang up; character speaking → stop the
        // speech AND open the call; otherwise → open the call. (The old
        // handler early-returned whenever `TTSActive` looked true, so a wedged
        // speech flag permanently swallowed every click.)
        if (this.micButton) {
            this._setupMicStopState();

            this.micButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handleCallButtonClick();
            });
        }

        // Task #530 — Dictate controls (speech → editable text, never auto-sends)
        if (this.dictateButton) {
            this.dictateButton.addEventListener("click", (e) => {
                e.preventDefault();
                this.startDictation();
            });
        }
        if (this.dictateCancelBtn) {
            this.dictateCancelBtn.addEventListener("click", (e) => {
                e.preventDefault();
                this.stopDictation({
                    keepText: false,
                    refocus: true,
                    reason: "cancel-tap",
                });
            });
        }
        if (this.dictateAcceptBtn) {
            this.dictateAcceptBtn.addEventListener("click", (e) => {
                e.preventDefault();
                this.stopDictation({
                    keepText: true,
                    refocus: true,
                    reason: "accept-tap",
                });
            });
        }
        // Escape inside the field discards an active dictation
        if (this.chatInput) {
            this.chatInput.addEventListener("keydown", (e) => {
                if (e.key === "Escape" && this.micMode === "dictate") {
                    e.preventDefault();
                    this.stopDictation({
                        keepText: false,
                        refocus: true,
                        reason: "escape-key",
                    });
                }
            });
        }
    }

    /**
     * Task #530 — single entry point for every circular-button tap.
     * Order matters and there is NO silent early-return:
     *   1. in a call            → end the call (also silences the character)
     *   2. character speaking   → STOP the speech. Full stop — the next tap
     *                             opens the call. (Founder feedback: a stop
     *                             tap that surprise-opened a call caused
     *                             constant misclicks. Stop and Call are now
     *                             visually AND behaviorally distinct.)
     *   3. idle                 → open the call
     */
    handleCallButtonClick() {
        const speechActive = this._isSpeechActive();
        this._micLedger("click", {
            inCall: this.micMode === "call",
            dictating: this.micMode === "dictate",
            speechActive,
            listening: !!this.isListening,
        });

        // Any tap = user interaction → bump idle
        if (this.realtimeTTS?.bumpIdleForUserInteraction) {
            this.realtimeTTS.bumpIdleForUserInteraction("call-button");
        }

        if (this.micMode === "call") {
            this._micLedger("branch", { taken: "end-call" });
            if (speechActive) this.stopSpeechFromMicButton();
            this.endCall("user-tap");
            return;
        }

        if (speechActive) {
            // STOP ONLY — never open a call from a stop tap. The wedge
            // guard still runs so a stuck speech flag can never eat the
            // NEXT tap (which then cleanly reaches the start-call branch).
            this._micLedger("branch", { taken: "stop-speech-only" });
            this.stopSpeechFromMicButton();
            this._clearWedgedSpeechFlag();
            return;
        }

        this._micLedger("branch", { taken: "start-call" });
        this.startCall();
    }

    /**
     * Task #530 — guard against a wedged speech-active flag. `TTSActive` is
     * cleared in exactly one place in the TTS pipeline; if that pipeline
     * wedges mid-stream the flag stays true forever. After we have already
     * run the full stop routine, a still-true flag with no actually-speaking
     * engine is a wedge — clear it so it cannot keep lying to the UI.
     */
    _clearWedgedSpeechFlag() {
        try {
            const engineSpeaking = !!this.realtimeTTS?.isSpeaking?.();
            const gs = this.modelViewer?.greetingService;
            const greetingPlaying = !!(gs && (gs.isPlaying || gs.currentAudio));
            if (window.TTSActive && !engineSpeaking && !greetingPlaying) {
                window.TTSActive = false;
                this._micLedger("wedge-cleared", {});
            }
        } catch (_) {}
    }

    /**
     * Task #530 — open the live Call session: one awaited activation flow,
     * one permission/capture path, continuous listen↔reply until ended.
     */
    async startCall() {
        if (this.micMode === "call") return;
        if (this._callStarting) {
            this._micLedger("start-call-ignored", { reason: "in-flight" });
            return;
        }

        // Starting one mode cleanly ends the other. A switch away from
        // dictation keeps whatever was already transcribed (non-destructive).
        if (this.micMode === "dictate") {
            this.stopDictation({
                keepText: true,
                refocus: false,
                reason: "switch-to-call",
            });
        }

        // Persistently unavailable mic → open the helper, not a dead prompt.
        if (
            window.micAssistant &&
            window.micAssistant.micStatus === "unavailable"
        ) {
            this._micLedger("start-call-blocked", { reason: "mic-assistant" });
            window.micAssistant.forceShowOnMicClick();
            return;
        }

        this._callStarting = true;
        this.micMode = "call";
        this.manualStop = false;
        this.continuousListening = true;
        this.userActivatedMicrophone = true;

        // Show chat container when the call opens
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }

        // Reveal confidence slider when Lab page Diagnostic is active with Answer via Chat mode
        if (
            this.diagnosticChatManager?.isActive &&
            window.TutorState?.isChatModeActive
        ) {
            const container = document.getElementById(
                "diagnostic-confidence-container",
            );
            if (
                container &&
                container.style.display !== "none" &&
                !container.classList.contains("visible")
            ) {
                container.classList.add("visible");
            }
        }

        // Immediate visual feedback: the button is a call control now
        this._updateCallUI(true);

        // Clear any phantom listening state from a prior interruption
        this.clearPhantomListeningState();

        try {
            const ok = await this.startListening();
            if (!ok) {
                this._micLedger("start-call-failed", {});
                this.endCall("start-failed");
                return;
            }

            // Fresh silence tracking for the new call
            if (this.silenceTimer) clearTimeout(this.silenceTimer);
            this.lastSpeechTime = Date.now();
            this.startLongSilenceDetection();
            this._micLedger("call-open", {});
        } finally {
            this._callStarting = false;
        }
    }

    /**
     * Task #530 — end the Call session and tear everything down.
     */
    endCall(reason = "user") {
        if (this.micMode !== "call" && !this.isListening) {
            // Still make sure UI is coherent
            this._updateCallUI(false);
        }
        this._micLedger("end-call", { reason });

        this.micMode = null;
        this.manualStop = true;
        this.continuousListening = false;
        this.userActivatedMicrophone = false;

        this.stopListening();
        this._updateCallUI(false);
    }

    /**
     * Task #530 — apply/remove the in-call look on the circular button.
     * In-call state visually overrides the speech-stop morph.
     */
    _updateCallUI(inCall) {
        const btn = this.micButton;
        if (!btn) return;
        if (inCall) {
            // The stop morph must never mask the in-call state
            this._updateMicStopState(false);
        }
        btn.classList.toggle("in-call", !!inCall);
        if (this.micButtonContainer) {
            this.micButtonContainer.classList.toggle("in-call", !!inCall);
        }
        if (inCall) {
            btn.setAttribute("aria-label", "End the call");
            btn.setAttribute("title", "End call");
        } else {
            btn.setAttribute("aria-label", "Start a voice call");
            btn.setAttribute("title", "Voice call");
        }
    }

    /**
     * True while the character is audibly speaking — covers every speech path:
     * the realtime engine (window.TTSActive, set on first scheduled chunk,
     * cleared at real end-of-utterance) plus the GreetingService <audio>
     * fallback used on some Safari greeting paths.
     */
    _isSpeechActive() {
        try {
            if (window.TTSActive) return true;
            const gs = this.modelViewer?.greetingService;
            if (gs && (gs.isPlaying || gs.currentAudio)) return true;
        } catch (_) {}
        return false;
    }

    /**
     * Mic-as-stop: the exact stop routine the old floating Stop pill ran —
     * stop audio only (mic untouched), prep TTS for the next turn, and stop
     * any greeting playback. UI/state only; never touches the audio pipeline
     * beyond the existing stop calls.
     */
    async stopSpeechFromMicButton() {
        console.log("🛑 Mic-as-stop tapped - stopping speech playback");

        // Snap the button back to mic immediately (don't wait for the poll)
        this._updateMicStopState(false);

        // Flag for Safari-specific handling (same as the old stop button)
        this.stopButtonPressed = true;

        // Stop only audio playback, don't affect microphone
        this.stopAudioOnly();

        // Stop greeting playback if available
        if (this.modelViewer && this.modelViewer.greetingService) {
            this.modelViewer.greetingService.stop();
            console.log("🛑 Greeting playback stopped via mic-as-stop");
        }

        // Prepare TTS for next response
        try {
            await this.prepTTSForNewTurn();
            console.log("✅ TTS prepared for new turn after mic-as-stop");
        } catch (_) {}
    }

    /**
     * Make the mic button state-aware: while the character speaks it renders
     * a stop-square glyph (crossfaded with the mic icon via CSS inline in
     * index.html) and a tap stops the speech. A light poll mirrors the
     * SpeakingGlyph pattern so EVERY speech path is covered by one signal.
     */
    _setupMicStopState() {
        this._micStopMode = false;

        // Inject the stop glyph element once (styled by inline CSS)
        if (
            this.micButton &&
            !this.micButton.querySelector(".mic-stop-glyph")
        ) {
            const glyph = document.createElement("span");
            glyph.className = "mic-stop-glyph";
            glyph.setAttribute("aria-hidden", "true");
            this.micButton.appendChild(glyph);
        }

        // Task #530 — inject the in-call (hang-up) glyph once: a phone-down
        // outline, inline SVG so no icon-font version can break it.
        if (
            this.micButton &&
            !this.micButton.querySelector(".call-end-glyph")
        ) {
            const end = document.createElement("span");
            end.className = "call-end-glyph";
            end.setAttribute("aria-hidden", "true");
            end.innerHTML =
                '<svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.94)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>' +
                '<line x1="22" y1="2" x2="2" y2="22"/>' +
                "</svg>";
            this.micButton.appendChild(end);
        }

        this._updateMicStopState = (speaking) => {
            speaking = !!speaking;
            // Task #530 — in-call the button is the hang-up control; the
            // speech-stop morph never overrides it.
            if (this.micMode === "call") speaking = false;
            if (speaking === this._micStopMode) return;
            this._micStopMode = speaking;
            const container =
                this.micButtonContainer ||
                this.micButton?.closest(".mic-button-container");
            container?.classList.toggle("speech-stop-mode", speaking);
            if (this.micButton) {
                this.micButton.classList.toggle(
                    "speech-stop-mode",
                    speaking,
                );
                this.micButton.setAttribute(
                    "aria-label",
                    speaking ? "Stop the voice" : "Start a voice call",
                );
                this.micButton.title = speaking
                    ? "Stop speaking"
                    : "Voice call";
            }
        };

        if (!this._micStopPoll) {
            this._micStopPoll = setInterval(() => {
                this._updateMicStopState(this._isSpeechActive());
            }, 150);
        }
    }

    /**
     * Helper method to start listening with proper animation handling
     * Ensures that listening UI and animations are correctly triggered after restart
     */
    async startListeningWithAnimationCheck() {
        const result = await this.startListening();
        if (result === false) {
            // startListening failed - ensure clean state (SR onstart never
            // fired, so no UI was touched)
            console.log("❌ startListening failed - ensuring clean state");
            this.clearPhantomListeningState();
        }
        return result;
    }

    /**
     * Clear any phantom listening state that might be left over from interruptions
     * This ensures the UI is in a clean state before starting actual speech recognition
     */
    clearPhantomListeningState() {
        console.log(
            "🧹 Clearing phantom listening state before starting actual recognition",
        );

        // Reset visual listening state without triggering speech recognition
        this.isListening = false;

        // Clear visual indicators
        if (this.micStatus) this.micStatus.classList.add("d-none");
        if (this.micButton) this.micButton.classList.remove("active");
        if (this.micButtonContainer)
            this.micButtonContainer.classList.remove("listening");

        // Reset animation to idle before starting
        if (this.animationController && !this.isAudioPlaying) {
            this.animationController.playIdleAnimation();
        }

        console.log(
            "✅ Phantom listening state cleared - ready for fresh start",
        );
    }

    /* --------- UTIL: stop all tracks safely --------- */
    _stopTracks(stream) {
        try {
            stream?.getTracks?.().forEach((t) => t.stop());
        } catch (_) {}
    }

    /* --------- ACQUIRE/DESTROY CAPTURE STREAM (Task #530) --------- */
    /**
     * The ONE getUserMedia path for both Call and Dictate. Single-flight:
     * concurrent callers share the same promise, a live stream is reused,
     * and there are no primer/probe streams. Safari serializes device
     * arbitration, so overlapping capture attempts could stall the tab —
     * this guarantees at most one is ever in flight.
     * Throws a classified error (err.name preserved) on failure.
     */
    async _acquireCaptureStream() {
        if (
            this._captureStream &&
            this._captureStream.getTracks().some((t) => t.readyState === "live")
        ) {
            this._micLedger("capture", { result: "reused" });
            return this._captureStream;
        }
        if (this._capturePromise) {
            this._micLedger("capture", { result: "joined-in-flight" });
            return this._capturePromise;
        }

        this._capturePromise = (async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                const err = new Error("Media devices not supported");
                err.name = "NoMediaDevices";
                throw err;
            }

            // Fast-fail on a known-denied permission (no prompt storm, no
            // pointless getUserMedia). Best-effort — Safari may not support
            // the query; any failure just falls through to getUserMedia.
            try {
                if (navigator.permissions?.query) {
                    const st = await navigator.permissions.query({
                        name: "microphone",
                    });
                    this._micLedger("permission", { state: st?.state });
                    if (st?.state === "denied") {
                        const err = new Error("Microphone permission denied");
                        err.name = "NotAllowedError";
                        throw err;
                    }
                }
            } catch (permErr) {
                if (permErr?.name === "NotAllowedError") throw permErr;
                // permissions.query unsupported — continue
            }

            // Prefer the remembered device, but only as `ideal` so an
            // unplugged mic can never hard-fail the whole activation.
            let savedDeviceId = null;
            try {
                savedDeviceId = localStorage.getItem("lastMicDeviceId");
            } catch (_) {}

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    ...(savedDeviceId
                        ? { deviceId: { ideal: savedDeviceId } }
                        : {}),
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            this._captureStream = stream;

            // Remember the device actually granted (no enumerateDevices pass)
            try {
                const settings = stream.getAudioTracks()[0]?.getSettings?.();
                if (settings?.deviceId) {
                    localStorage.setItem("lastMicDeviceId", settings.deviceId);
                }
                this._micLedger("capture", {
                    result: "acquired",
                    aec: settings?.echoCancellation !== false,
                });
            } catch (_) {
                this._micLedger("capture", { result: "acquired" });
            }

            if (window.micAssistant) {
                window.micAssistant.micStatus = "available";
            }
            return stream;
        })().finally(() => {
            this._capturePromise = null;
        });

        return this._capturePromise;
    }

    _destroyCaptureStream() {
        this._stopTracks(this._captureStream);
        this._captureStream = null;
    }

    /* --------- VAD START/PAUSE/STOP HELPERS --------- */
    _startVAD(stream) {
        if (!stream) {
            console.warn("[VAD] No MediaStream provided to _startVAD()");
            return;
        }

        // Create once
        if (!this.vad) {
            this.vad = new VADLite(stream, {
                onSpeechStart: () => {
                    // your existing logic
                    if (this.isListening) {
                        if (typeof TTSActive !== "undefined" && TTSActive) {
                            this.realtimeTTS?.ModifiedVloume?.(30, {
                                fadeMs: 300,
                            });
                            this._isVolumeModified = true;
                        }
                        this.animationController?.playListenAnimation?.();
                    }
                    this.onUserVoiceDetected?.();
                },
                onSpeechEnd: () => {
                    // your existing logic
                    if (this.isListening) {
                        if (
                            typeof TTSActive !== "undefined" &&
                            TTSActive &&
                            this._isVolumeModified
                        ) {
                            this.realtimeTTS?.restoreVolume?.({ fadeMs: 5000 });
                            this._isVolumeModified = false;
                            this.animationController?.playspeakAnimation?.();
                        } else {
                            this.animationController?.playIdleAnimation?.();
                        }
                    }
                    this.onUserVoiceStopped?.();
                },
                activityHz: 5,
            });
        } else {
            // If we already have an instance (e.g., restart after pause), unpause it
            this.vad.setPaused?.(false);
        }

        // Task #274: pass the CURRENT capture stream — each mic session has
        // a fresh stream, and the reused VADLite rebinds its analysis graph
        // to it (one shared AudioContext for the whole page lifetime).
        try {
            this.vad.start(stream);
            console.log("[VAD] started");
        } catch (e) {
            console.warn("[VAD] start failed:", e);
        }
    }

    _stopVAD(hard = true) {
        try {
            if (!this.vad) return;

            if (hard) {
                // Task #274: stop analysis + release the stream graph, but
                // KEEP the instance — VADLite now suspends (not closes) its
                // AudioContext so mic on/off cycles reuse one context
                // instead of churning create/close (iOS Safari has a small
                // AudioContext budget; churn under memory pressure fed the
                // jetsam kills). Next _startVAD rebinds to the new stream.
                this.vad.stop();
                console.log("[VAD] stopped (context suspended for reuse)");
            } else {
                // soft pause (keeps instance to resume quicker)
                this.vad.setPaused?.(true);
                console.log("[VAD] paused");
            }
        } catch (e) {
            console.warn("[VAD] stop failed:", e);
        }
    }

    _pauseVAD() {
        try {
            this.vad?.setPaused?.(true);
            console.log("[VAD] paused");
        } catch (_) {}
    }

    /* =========================
       START/STOP LISTENING
       ========================= */
    /**
     * Task #530 — the ONE awaited activation flow, shared by Call and
     * Dictate (and by the auto-restart paths). Exactly one permission/
     * capture request per activation; SR + VAD reuse the same stream.
     * Returns true on success, false on failure (after reporting it).
     */
    async startListening() {
        if (this.isListening) return true;
        if (this._micStartPromise) return this._micStartPromise;

        // Callers set micMode first; a bare legacy call implies Call mode.
        if (!this.micMode) this.micMode = "call";
        this.manualStop = false;

        this._micStartPromise = (async () => {
            try {
                // ONE capture path (single-flight, no primer streams)
                const stream = await this._acquireCaptureStream();

                // ONE transcription engine per session: ElevenLabs Scribe
                // realtime (works everywhere, transcribes OUR stream), with
                // webkitSpeechRecognition as a Chromium-only fallback.
                // NEVER SR on Safari — its second OS-level capture session
                // is the freeze vector this rework removes.
                this._sttEngine = await this._startSTTEngine(stream);
                this._micLedger("stt-engine", { engine: this._sttEngine });

                // VAD (barge-in + TTS ducking) belongs to the CALL loop only;
                // Dictate is a pure transcription tool.
                if (this.micMode === "call") {
                    this._startVAD(stream);
                }
                return true;
            } catch (err) {
                this._micLedger("start-failed", {
                    name: err?.name || "?",
                    msg: String(err?.message || err).slice(0, 120),
                });
                this._reportMicFailure(err);
                // Release anything half-acquired
                this._stopScribe();
                this._sttEngine = null;
                this._stopVAD(true);
                this._destroyCaptureStream();
                return false;
            } finally {
                this._micStartPromise = null;
            }
        })();

        return this._micStartPromise;
    }

    // Called by recognition.onstart event to update UI
    updateListeningUI() {
        this.isListening = true;

        // Task #530 — Dictate never touches the circular Call button or the
        // character animations; its UI is the morphed control in the bar.
        if (this.micMode === "dictate") {
            this._updateDictateUI(true);
            return;
        }

        if (this.micStatus) {
            this.micStatus.textContent = "Listening...";
            this.micStatus.classList.remove("d-none");
        }
        this.micButton?.classList?.add("active");
        this.micButtonContainer?.classList?.add("listening");

        if (this.animationController) {
            if (this.isAudioPlaying || this.isChunkedAudioActive) {
                console.log(
                    "🎤 Mic active but prioritizing speak animation - audio is playing",
                );
            } else {
                console.log("🎤 Playing listen animation - no audio detected");
                if (TTSActive) {
                    console.log(
                        "🎤 Playing Same animation - no audio detected",
                    );
                } else {
                    this.animationController.playListenAnimation?.();
                }
            }
        }
        this.chatContainer?.classList?.add("visible");
    }

    stopListening() {
        const wasScribe =
            this._sttEngine === "scribe" || !!(this.scribe && this.scribe.active);

        // Only stop if active
        if (this.isListening || this.userActivatedMicrophone) {
            this.manualStop = true; // block auto-restart
            try {
                if (this.recognition && this._sttEngine !== "scribe") {
                    console.log("🛑 Stopping speech recognition");
                    this.recognition.stop();
                }
            } catch (e) {
                console.warn("Error stopping recognition:", e);
            }
        }

        // Scribe teardown — intentional stop never fires onClose, so no
        // reconnect logic can race this.
        this._stopScribe();
        this._clearScribeSend();
        this._sttEngine = null;

        // Stop VAD HARD and release the mic
        this._stopVAD(true);
        this._destroyCaptureStream();

        // SR's onend event repaints the UI for the fallback engine; the
        // Scribe path repaints here.
        if (wasScribe && this.isListening) this.updateStoppedListeningUI();

        // Clear timers
        if (this.longSilenceTimer) {
            clearTimeout(this.longSilenceTimer);
            this.longSilenceTimer = null;
        }
        if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
        }

        // This flag says: user intentionally stopped the mic now
        this.userActivatedMicrophone = false;
        this._micLedger("teardown", {});
    }

    // Called by recognition.onend event to update UI
    updateStoppedListeningUI() {
        this.isListening = false;

        // Task #530 — dictation owns its own (bar) UI
        if (this.micMode === "dictate") {
            return;
        }

        this.micStatus?.classList?.add("d-none");
        this.micButton?.classList?.remove("active");
        this.micButtonContainer?.classList?.remove("listening");

        console.log("Chat window remains visible - continuous Q&A mode active");

        if (this.animationController && !this.isAudioPlaying) {
            setTimeout(() => {
                const stillSpeaking =
                    (typeof TTSActive !== "undefined" && TTSActive) ||
                    this.isAudioPlaying;
                if (stillSpeaking) {
                    try {
                        typeof Avatar !== "undefined" && Avatar.play?.("speak");
                    } catch {}
                } else {
                    this.animationController.playIdleAnimation?.();
                }
            }, 60); // grace tick
        }
    }

    /* =========================
       Scribe realtime — THE transcription engine (Task #530 rework)
       ElevenLabs Scribe v2 Realtime transcribes the ONE shared capture
       stream over WebSocket. No second OS capture session (the Safari
       freeze vector), no Apple dictation dependency, same behavior in
       every browser. Two routing modes on the one engine:
         'call'    → committed segments auto-send + barge-in (live loop)
         'dictate' → partial+committed stream into the text field ONLY
       ========================= */

    /**
     * Pick and start the transcription engine on the shared stream.
     * Scribe first, everywhere. webkitSpeechRecognition only as a
     * Chromium fallback — NEVER on Safari, where SR's second capture
     * session is the exact contention that froze the tab.
     * Returns the engine name; throws the Scribe error when no fallback.
     */
    async _startSTTEngine(stream) {
        try {
            await this._startScribe(stream);
            return "scribe";
        } catch (err) {
            this._micLedger("scribe-start-failed", {
                name: err?.name || "?",
                msg: String(err?.message || err).slice(0, 120),
            });
            if (this.recognition && !this.isSafari) {
                try {
                    this.recognition.start();
                    this._micLedger("sr-fallback-start", {});
                } catch (srErr) {
                    if (srErr?.name !== "InvalidStateError") throw srErr;
                    this._micLedger("sr-fallback-start", {
                        note: "already-running",
                    });
                }
                return "websr";
            }
            throw err;
        }
    }

    async _startScribe(stream) {
        if (!this.scribe) {
            this.scribe = new ScribeRealtimeSTT({
                ledger: (ev, d) => this._micLedger(ev, d),
                onReady: () => {
                    this.updateListeningUI();
                    if (this.micMode === "call") {
                        this.resetLongSilenceDetection?.();
                    }
                },
                onPartial: (t) => this._onScribePartial(t),
                onCommitted: (t) => this._onScribeCommitted(t),
                onClose: (lastErr) => this._onScribeClose(lastErr),
            });
        }
        this._scribeCommittedText = "";
        await this.scribe.start(stream);
        this._sttBackoff = 350; // healthy session → reset reconnect backoff
    }

    _stopScribe() {
        try {
            this.scribe?.stop("teardown");
        } catch (_) {}
    }

    /** Silent reconnect on the SAME capture stream (token + WS only). */
    async _restartScribeSession() {
        const stream =
            this._captureStream &&
            this._captureStream.getTracks().some((t) => t.readyState === "live")
                ? this._captureStream
                : await this._acquireCaptureStream();
        await this._startScribe(stream);
        this._sttEngine = "scribe";
        this._micLedger("stt-engine", { engine: "scribe", reconnect: true });
    }

    _canRestartSTT() {
        const now = Date.now();
        this._sttRestartWindow = (this._sttRestartWindow || []).filter(
            (t) => now - t < 30000,
        );
        return this._sttRestartWindow.length < 6;
    }

    _noteSTTRestart() {
        this._sttRestartWindow.push(Date.now());
        this._sttBackoff = Math.min(
            Math.max((this._sttBackoff || 350) * 1.6, 350),
            4000,
        );
    }

    /** Interim (and stable-uncommitted) text from Scribe. */
    _onScribePartial(text) {
        text = String(text || "");
        if (!text.trim()) return;

        if (this.micMode === "dictate") {
            const composed = [this._dictateBase, this._dictateFinal, text]
                .filter(Boolean)
                .join(" ")
                .replace(/\s+/g, " ")
                .trimStart();
            this._setDictateField(composed);
            return;
        }
        if (this.micMode !== "call") return;

        this.lastSpeechTime = Date.now();
        // Live echo of the in-flight utterance in the input (read-along)
        const live = [this._scribeCommittedText, text]
            .filter(Boolean)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
        if (this.chatInput) {
            this.chatInput.value = live;
            this.chatInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
        // Sustained speech while the character talks = barge-in
        if (text.trim().length > 3) this._maybeBargeInDuringTTS();
    }

    /** A settled segment from Scribe (server-side VAD end-of-thought). */
    _onScribeCommitted(text) {
        text = String(text || "")
            .replace(/\s+/g, " ")
            .trim();
        if (!text) return;

        if (this.micMode === "dictate") {
            this._dictateFinal = [this._dictateFinal, text]
                .filter(Boolean)
                .join(" ");
            const composed = [this._dictateBase, this._dictateFinal]
                .filter(Boolean)
                .join(" ")
                .replace(/\s+/g, " ")
                .trimStart();
            this._setDictateField(composed);
            this._micLedger("dictate-final", {
                len: this._dictateFinal.length,
            });
            return;
        }
        if (this.micMode !== "call") return;

        this.lastSpeechTime = Date.now();
        this._scribeCommittedText = [this._scribeCommittedText, text]
            .filter(Boolean)
            .join(" ");
        this._micLedger("scribe-committed", {
            len: this._scribeCommittedText.length,
        });
        // The server already waited out the end-of-thought silence; a short
        // debounce only coalesces back-to-back commits into one message.
        this._scheduleScribeSend(180);
    }

    _scheduleScribeSend(delay) {
        if (this._scribeSendTimer) clearTimeout(this._scribeSendTimer);
        this._scribeSendTimer = setTimeout(() => {
            this._scribeSendTimer = null;
            const text = (this._scribeCommittedText || "")
                .replace(/\s+/g, " ")
                .trim();
            this._scribeCommittedText = "";
            if (text.length < 2) return;
            if (text === this._lastSentText) {
                this._micLedger("call-send-dedupe", {});
                return;
            }
            this._lastSentText = text;
            if (this.chatInput) {
                this.chatInput.value = text;
                this.chatInput.dispatchEvent(
                    new Event("input", { bubbles: true }),
                );
            }
            this._micLedger("call-send", { len: text.length });
            this.sendMessage?.(text);
        }, delay);
    }

    _clearScribeSend() {
        if (this._scribeSendTimer) {
            clearTimeout(this._scribeSendTimer);
            this._scribeSendTimer = null;
        }
        this._scribeCommittedText = "";
    }

    /** Barge-in from the Scribe path — same throttle+grace as the SR path. */
    _maybeBargeInDuringTTS() {
        const speaking = () =>
            (typeof TTSActive !== "undefined" && TTSActive) ||
            !!window.TTSActive ||
            this.isAudioPlaying ||
            this.isChunkedAudioActive ||
            this.isProcessing;
        if (!speaking()) return;
        setTimeout(() => {
            if (!speaking()) return; // tiny tail just finished — let it be
            const now = Date.now();
            if (now - (this._lastInterruptAt || 0) < 400) return;
            this._lastInterruptAt = now;
            this._micLedger("barge-in", { engine: "scribe" });
            try {
                this.handleUserInterruption?.();
            } catch (_) {}
        }, 120);
    }

    _setDictateField(text) {
        if (!this.chatInput) return;
        this.chatInput.value = text;
        this.chatInput.dispatchEvent(new Event("input", { bubbles: true }));
        try {
            this.chatInput.scrollLeft = this.chatInput.scrollWidth;
            this.chatInput.scrollTop = this.chatInput.scrollHeight;
        } catch (_) {}
    }

    /**
     * UNEXPECTED Scribe close (intentional stops never land here).
     * Transient causes reconnect silently under the restart caps; fatal
     * causes end the session with an honest, specific message.
     */
    _onScribeClose(lastErr) {
        this._sttEngine = null;
        const mode = this.micMode;
        this.isListening = false;
        if (this.manualStop || !mode) return; // teardown already in charge

        const FATAL = {
            auth_error: 1,
            quota_exceeded: 1,
            unaccepted_terms: 1,
            invalid_request: 1,
            chunk_size_exceeded: 1,
        };
        const endSession = (reason) => {
            if (mode === "dictate") {
                this.stopDictation({
                    keepText: true,
                    refocus: true,
                    reason,
                });
            } else {
                this.endCall(reason);
            }
        };

        if (lastErr && FATAL[lastErr]) {
            const err = new Error("scribe: " + lastErr);
            err.name = "SttServiceError";
            this._reportMicFailure(err);
            endSession("stt-fatal-" + lastErr);
            return;
        }

        // Transient: silence timeout, session length limit, network blip,
        // transcriber hiccup → reconnect quietly (the user keeps talking).
        if (!this._canRestartSTT()) {
            this._micLedger("stt-restart-capped", {});
            const err = new Error("stt keeps disconnecting");
            err.name = "SttUnstable";
            this._reportMicFailure(err);
            endSession("stt-unstable");
            return;
        }
        this._noteSTTRestart();
        const delay = this._sttBackoff;
        this._micLedger("stt-reconnect", {
            inMs: delay,
            after: lastErr || "clean-close",
        });
        setTimeout(() => {
            if (this.micMode !== mode || this.manualStop || document.hidden) {
                return;
            }
            this._restartScribeSession().catch((err) => {
                this._micLedger("stt-reconnect-failed", {
                    name: err?.name || "?",
                });
                this._reportMicFailure(err);
                endSession("stt-reconnect-failed");
            });
        }, delay);
    }

    /* =========================
       SpeechRecognition v2 — now the Chromium-only FALLBACK (Task #530)
       Kept intact for when the Scribe socket can't open. Same two
       routing modes; never started on Safari.
       ========================= */
    setupSpeechRecognition() {
        // visibility pause — no engine can run usefully in a hidden tab, so
        // end the active session cleanly (dictation keeps its text).
        // Registered BEFORE the SR check: Scribe-only browsers need it too.
        if (!this._boundVisibilityHandler) {
            this._boundVisibilityHandler = () => {
                if (!document.hidden) return;
                if (this.micMode === "dictate") {
                    this.stopDictation({
                        keepText: true,
                        refocus: false,
                        reason: "tab-hidden",
                    });
                } else if (this.micMode === "call" || this.isListening) {
                    this.endCall("tab-hidden");
                }
                this._flushMicLedger();
            };
            document.addEventListener(
                "visibilitychange",
                this._boundVisibilityHandler,
            );
        }

        const SR = window.webkitSpeechRecognition || window.SpeechRecognition;
        if (!SR) {
            // No browser SR (e.g. Firefox, some Safari builds) — fine.
            // Scribe realtime is the PRIMARY engine and needs none of it;
            // the voice buttons stay visible and fully functional.
            this.recognition = null;
            this._micLedger("sr-unsupported", { scribeOnly: true });
            return;
        }

        // ----------- CONFIG -----------
        const COMMIT_DELAY_MS = 800; // default commit delay for interim-only batches
        const COMMIT_AFTER_FINAL_MS = 120; // faster commit when a final result is present
        const MIN_SEND_LEN = 2; // ignore tiny noise chunks
        const MAX_BUFFER_LEN = 4000; // cap for _bufferText
        const MAX_MESSAGES = 300; // DOM cap to prevent memory bloat
        const BARGE_IN_THROTTLE_MS = 400; // limit barge-in calls
        const TTS_GRACE_MS = 120; // optional: short grace so TTS finishes tiny tails
        const RESTART_BACKOFF_MIN = 350; // initial onend restart delay
        const RESTART_BACKOFF_MAX = 4000; // max onend restart delay
        const RESTART_WINDOW_MS = 30000; // consider restarts within this window
        const RESTART_WINDOW_LIMIT = 6; // max restarts allowed within window

        // ----------- STATE -----------
        this.recognition = new SR();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = this.recognition.lang || "en-US";

        // batching / commit
        this._commitTimer = null;
        this._commitDelayMs = COMMIT_DELAY_MS;
        this._bufferText = "";
        this._hasFinalInBatch = false;
        this._lastSentText = ""; // prevent duplicate sends (same text back-to-back)

        // barge-in throttle
        this._lastInterruptAt = 0;

        // restart/backoff
        this._srRestartWindow = [];
        this._srBackoff = RESTART_BACKOFF_MIN;

        // ------- HELPERS -------
        const clearCommitTimer = () => {
            if (this._commitTimer) {
                clearTimeout(this._commitTimer);
                this._commitTimer = null;
            }
        };

        const scheduleCommit = (delay) => {
            clearCommitTimer();
            this._commitTimer = setTimeout(() => {
                const text = (this._bufferText || "").trim();
                if (text.length < MIN_SEND_LEN) return;

                // prevent duplicate sends if nothing changed
                if (text === this._lastSentText) {
                    this._bufferText = "";
                    this._hasFinalInBatch = false;
                    return;
                }

                if (this.chatInput) {
                    this.chatInput.value = text;
                    this.chatInput.dispatchEvent(
                        new Event("input", { bubbles: true }),
                    );
                }

                this.sendMessage?.(text);
                this._lastSentText = text;
                this._bufferText = "";
                this._hasFinalInBatch = false;
            }, delay);
        };

        const canRestartSR = () => {
            const now = Date.now();
            this._srRestartWindow = this._srRestartWindow.filter(
                (t) => now - t < RESTART_WINDOW_MS,
            );
            return this._srRestartWindow.length < RESTART_WINDOW_LIMIT;
        };

        const noteRestart = () => {
            this._srRestartWindow.push(Date.now());
            // exponential backoff within bounds
            this._srBackoff = Math.min(
                Math.max(this._srBackoff * 1.6, RESTART_BACKOFF_MIN),
                RESTART_BACKOFF_MAX,
            );
        };

        const resetBackoffIfStable = () => {
            // after a successful onstart, reset to minimum
            this._srBackoff = RESTART_BACKOFF_MIN;
        };

        const maybeInterrupt = () => {
            const now = Date.now();
            if (now - this._lastInterruptAt > BARGE_IN_THROTTLE_MS) {
                this._lastInterruptAt = now;
                this.handleUserInterruption?.();
            }
        };

        const capBuffer = () => {
            if (this._bufferText.length > MAX_BUFFER_LEN) {
                this._bufferText = this._bufferText.slice(-MAX_BUFFER_LEN);
            }
        };

        const pruneChatDom = () => {
            if (!this.chatHistory) return;
            const msgs = this.chatHistory.children;
            while (msgs.length > MAX_MESSAGES)
                this.chatHistory.removeChild(msgs[0]);
        };

        // ----------- EVENTS -----------
        this.recognition.onstart = () => {
            resetBackoffIfStable();
            this._micLedger("sr-start", {});
            this.updateListeningUI?.();
            if (this.micMode === "call") {
                this.resetLongSilenceDetection?.();
            }
        };

        this.recognition.onend = () => {
            const mode = this.micMode;
            this.updateStoppedListeningUI?.();

            // auto-restart only while a session is meant to be active
            const callWantsRestart =
                mode === "call" &&
                !this.manualStop &&
                this.userActivatedMicrophone;
            const dictateWantsRestart = mode === "dictate" && !this.manualStop;

            if (callWantsRestart || dictateWantsRestart) {
                if (!canRestartSR()) {
                    this._micLedger("sr-end", {
                        willRestart: false,
                        reason: "restart-cap",
                    });
                    // Too many restarts — end the session honestly instead
                    // of flapping (and say so in plain words).
                    if (mode === "dictate") {
                        this.stopDictation({
                            keepText: true,
                            refocus: true,
                            reason: "restart-cap",
                        });
                    } else {
                        this.endCall("restart-cap");
                    }
                    this.addMessageToChat?.(
                        "System",
                        "The microphone keeps cutting out, so I've stopped it for now. Check that no other app is using it, then tap the button to try again.",
                        "system-message",
                    );
                    return;
                }
                noteRestart();
                this._micLedger("sr-end", {
                    willRestart: true,
                    backoffMs: this._srBackoff,
                });
                setTimeout(() => {
                    const stillWanted =
                        this.micMode === mode &&
                        !this.isListening &&
                        !this.manualStop &&
                        (mode === "dictate" || this.userActivatedMicrophone);
                    if (stillWanted) {
                        this.startListening?.();
                    }
                }, this._srBackoff);
            } else {
                this._micLedger("sr-end", { willRestart: false });
            }
        };

        this.recognition.onresult = (event) => {
            let interim = "";
            let final = "";
            this._hasFinalInBatch = false;

            // Aggregate results
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const res = event.results[i];
                const t = res[0]?.transcript || "";
                if (res.isFinal) {
                    final += t;
                    this._hasFinalInBatch = true;
                } else {
                    interim += t;
                }
            }

            // ---- DICTATE ROUTE: transcript → text field, nothing else ----
            // No barge-in, no commit timer, no auto-send. The user owns the
            // send button.
            if (this.micMode === "dictate") {
                if (final) {
                    this._dictateFinal = (
                        this._dictateFinal +
                        " " +
                        final
                    )
                        .replace(/\s+/g, " ")
                        .trim();
                    this._micLedger("dictate-final", {
                        len: this._dictateFinal.length,
                    });
                }
                const composed = (
                    this._dictateBase +
                    " " +
                    this._dictateFinal +
                    " " +
                    interim
                )
                    .replace(/\s+/g, " ")
                    .trim();
                if (this.chatInput) {
                    this.chatInput.value = composed;
                    this.chatInput.dispatchEvent(
                        new Event("input", { bubbles: true }),
                    );
                    // Keep the caret at the end so the live text is visible
                    try {
                        this.chatInput.scrollLeft = this.chatInput.scrollWidth;
                    } catch (_) {}
                }
                return;
            }

            // ---- CALL ROUTE: today's buffer/commit/auto-send + barge-in ----
            const latestChunk = (final || interim || "").trim();

            // Optional: small grace while TTS is playing to reduce false barge-ins
            const ttsActive =
                (typeof TTSActive !== "undefined" && TTSActive) ||
                this.isAudioPlaying ||
                this.isChunkedAudioActive;
            if (latestChunk.length > 3 && (ttsActive || this.isProcessing)) {
                // Give TTS a tiny window to finish before we barge-in
                setTimeout(() => {
                    const stillSpeaking =
                        (typeof TTSActive !== "undefined" && TTSActive) ||
                        this.isAudioPlaying ||
                        this.isChunkedAudioActive;
                    if (stillSpeaking) maybeInterrupt();
                }, TTS_GRACE_MS);
            }

            // Compose the current buffer (favor finalized text)
            const addText = final || interim;
            if (addText) {
                this._bufferText = (this._bufferText + " " + addText)
                    .replace(/\s+/g, " ")
                    .trim();
                capBuffer();
            }

            // Reflect live text in the input (nice for UX)
            if (this.chatInput) {
                this.chatInput.value = this._bufferText;
                this.chatInput.dispatchEvent(
                    new Event("input", { bubbles: true }),
                );
            }

            // Commit policy: commit faster on finals; otherwise debounce
            if (this._hasFinalInBatch) {
                scheduleCommit(COMMIT_AFTER_FINAL_MS);
            } else if (latestChunk) {
                scheduleCommit(COMMIT_DELAY_MS);
            }

            pruneChatDom();
        };

        this.recognition.onerror = (event) => {
            console.error("Speech recognition error:", event.error);
            this._micLedger("sr-error", { code: event.error || "?" });

            if (event.error === "no-speech" || event.error === "aborted") {
                // benign; onend handles restart/teardown
                return;
            }

            // Fatal-ish errors: report specifically, then end the session
            // cleanly. NEVER re-enter the permission flow from here — that
            // was the old prompt storm.
            const err = new Error(`Speech recognition error: ${event.error}`);
            if (event.error === "not-allowed" ||
                event.error === "service-not-allowed") {
                err.name = "NotAllowedError";
            } else if (event.error === "audio-capture") {
                err.name = "NotFoundError";
            } else if (event.error === "network") {
                err.name = "SRNetworkError";
            } else {
                err.name = "SRError";
                err.code = event.error;
            }
            this._reportMicFailure(err);

            if (this.micMode === "dictate") {
                this.stopDictation({
                    keepText: true,
                    refocus: false,
                    reason: `sr-error-${event.error}`,
                });
            } else if (this.micMode === "call") {
                this.endCall(`sr-error-${event.error}`);
            } else {
                this.updateStoppedListeningUI?.();
            }
            this.animationController?.playIdleAnimation?.();
        };

        // Show the voice controls
        if (this.micButton) this.micButton.style.display = "block";
        if (this.dictateWrap) this.dictateWrap.style.display = "";
    }

    /* =========================
       DICTATE MODE (Task #530)
       Speech → editable text. The control morphs into ✕ / ✓ while the
       transcript streams into the field. NOTHING auto-sends.
       ========================= */
    async startDictation() {
        if (this.micMode === "dictate") return;
        if (this._dictateStarting) return;

        this._micLedger("dictate-click", {
            inCall: this.micMode === "call",
            speechActive: this._isSpeechActive(),
        });

        // Starting one mode cleanly ends the other
        if (this.micMode === "call") {
            this.endCall("switch-to-dictate");
        }

        if (
            window.micAssistant &&
            window.micAssistant.micStatus === "unavailable"
        ) {
            this._micLedger("dictate-blocked", { reason: "mic-assistant" });
            window.micAssistant.forceShowOnMicClick();
            return;
        }

        this._dictateStarting = true;

        // Snapshot the field so ✕ can restore it exactly; new speech appends
        // after whatever was already typed.
        const prior = this.chatInput ? this.chatInput.value : "";
        this._dictatePrevText = prior;
        this._dictateBase = prior.trim();
        this._dictateFinal = "";

        this.micMode = "dictate";
        this.manualStop = false;
        this.continuousListening = false;
        this.userActivatedMicrophone = false; // call-loop paths stay dormant

        this._updateDictateUI(true);

        try {
            const ok = await this.startListening();
            if (!ok) {
                this._micLedger("dictate-start-failed", {});
                this.micMode = null;
                this.manualStop = true;
                this._updateDictateUI(false);
                // Field untouched — nothing streamed yet
                return;
            }
            this._micLedger("dictate-open", {});
        } finally {
            this._dictateStarting = false;
        }
    }

    /**
     * End dictation. keepText=true (✓ or mode switch) leaves the composed
     * text in the field for the user to edit and send themselves;
     * keepText=false (✕ / Escape) restores the exact prior field content.
     */
    stopDictation({ keepText = true, refocus = false, reason = "" } = {}) {
        if (this.micMode !== "dictate") return;

        this._micLedger("dictate-end", {
            keepText,
            reason,
            finalLen: this._dictateFinal.length,
        });

        this.micMode = null;
        this.manualStop = true;
        this.stopListening();

        if (this.chatInput) {
            if (keepText) {
                // Drop any half-word interim tail: keep base + finalized text
                const composed = (
                    this._dictateBase +
                    " " +
                    this._dictateFinal
                )
                    .replace(/\s+/g, " ")
                    .trim();
                // If nothing was ever transcribed, restore the exact prior
                // text (preserves leading/trailing whitespace the user had)
                this.chatInput.value = this._dictateFinal
                    ? composed
                    : this._dictatePrevText;
            } else {
                this.chatInput.value = this._dictatePrevText;
            }
            this.chatInput.dispatchEvent(new Event("input", { bubbles: true }));
            if (refocus) {
                try {
                    this.chatInput.focus();
                    const end = this.chatInput.value.length;
                    this.chatInput.setSelectionRange?.(end, end);
                } catch (_) {}
            }
        }

        this._dictateBase = "";
        this._dictateFinal = "";
        this._updateDictateUI(false);
    }

    /** Morph the bar control: mic ⇄ (✕ ✓), plus a listening cue on the row. */
    _updateDictateUI(active) {
        active = !!active;
        this.dictateWrap?.classList.toggle("dictating", active);
        this.chatInput
            ?.closest(".chat-input")
            ?.classList.toggle("is-dictating", active);
        if (this.chatInput) {
            if (active) {
                if (!this._dictatePlaceholderSaved) {
                    this._dictatePlaceholderSaved =
                        this.chatInput.placeholder || "";
                }
                this.chatInput.placeholder = "Listening…";
            } else if (this._dictatePlaceholderSaved !== undefined) {
                this.chatInput.placeholder =
                    this._dictatePlaceholderSaved || "";
            }
        }
        if (this.dictateButton) {
            this.dictateButton.setAttribute("aria-expanded", String(active));
        }
    }

    /* =========================
       MIC FAILURE MESSAGING (Task #530)
       Specific, human, actionable — never a generic red bubble.
       ========================= */
    _reportMicFailure(err) {
        const name = err?.name || "";
        let text;
        let assistant = false;

        switch (name) {
            case "NotAllowedError":
            case "SecurityError":
            case "PermissionDeniedError":
                text =
                    "Microphone access is blocked for this site. Click the lock icon in the address bar, allow the microphone, then try again.";
                assistant = true;
                break;
            case "NotFoundError":
            case "DevicesNotFoundError":
            case "OverconstrainedError":
                text =
                    "No microphone was found. Plug one in (or enable it in your system sound settings), then try again.";
                assistant = true;
                break;
            case "NotReadableError":
            case "TrackStartError":
            case "AbortError":
                text =
                    "Your microphone is busy in another app or tab. Close whatever is using it, then try again.";
                break;
            case "NoMediaDevices":
            case "NoSpeechRecognition":
                text =
                    "This browser can't capture voice here. Try Chrome, Edge, or Safari — or just type your message.";
                assistant = true;
                break;
            case "SRNetworkError":
                text =
                    "Speech recognition needs a network connection right now. Check your connection and try again.";
                break;
            case "SttUnconfigured":
            case "SttTokenError":
            case "SttServiceError":
                text =
                    "Live voice transcription is temporarily unavailable on our side. Give it a moment and try again — typing still works.";
                break;
            case "SttBusy":
                text =
                    "Voice sessions are starting too quickly. Wait a few seconds, then try again.";
                break;
            case "SttUnstable":
                text =
                    "Voice keeps disconnecting right now. Give it a moment and try again — typing still works.";
                break;
            default:
                text = `The microphone couldn't start${
                    name || err?.code ? ` (${name || err.code})` : ""
                }. Try again, or type your message instead.`;
                break;
        }

        if (assistant && window.micAssistant) {
            window.micAssistant.micStatus = "unavailable";
            window.micAssistant.forceShowOnMicClick?.();
        }

        // De-dupe: the same failure within 30s doesn't repaint the thread
        const key = name + "|" + text;
        const now = Date.now();
        if (
            this._lastMicFailureKey === key &&
            now - (this._lastMicFailureAt || 0) < 30000
        ) {
            return;
        }
        this._lastMicFailureKey = key;
        this._lastMicFailureAt = now;

        this.addMessageToChat?.("System", text, "system-message");
    }

    /* =========================
       MIC-LEDGER (Task #530)
       Breadcrumbs for every mic activation, POSTed to the server log the
       way AUDIO-LEDGER does (the production bundle strips console.*).
       ========================= */
    _micLedger(ev, detail = {}) {
        try {
            const entry = {
                seq: ++this._micLedgerSeq,
                t: Date.now(),
                ev,
                mode: this.micMode || "-",
                ...detail,
            };
            console.log("[MIC-LEDGER]", ev, entry);
            if (!MIC_LEDGER_SERVER_LOG) return;
            this._micLedgerQueue.push(entry);
            if (this._micLedgerQueue.length >= 12) {
                this._flushMicLedger();
                return;
            }
            clearTimeout(this._micLedgerTimer);
            this._micLedgerTimer = setTimeout(
                () => this._flushMicLedger(),
                600,
            );
        } catch (_) {}
    }

    _flushMicLedger() {
        try {
            clearTimeout(this._micLedgerTimer);
            this._micLedgerTimer = null;
            const events = this._micLedgerQueue.splice(0);
            if (!events.length) return;
            fetch("/api/debug/mic-ledger", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    events,
                    ua: (navigator.userAgent || "").slice(0, 140),
                }),
                keepalive: true,
            }).catch(() => {});
        } catch (_) {}
    }

    addMessageToChat(
        sender,
        message,
        className = "",
        keywordImages = null,
        videoData = null,
    ) {
        const messageDiv = document.createElement("div");
        messageDiv.className = `chat-message ${className}`;

        if (sender) {
            const senderSpan = document.createElement("span");
            senderSpan.className = "sender";
            senderSpan.textContent = sender;
            messageDiv.appendChild(senderSpan);
        }

        const messageContent = document.createElement("span");
        messageContent.className = "message-content";

        // Check if message contains HTML (for buttons, etc.)
        if (
            message &&
            (message.includes("<button") || message.includes("<a"))
        ) {
            // Sanitize HTML content to prevent XSS
            const dangerousAttributes = /(on\w+|javascript:|data:)/i;

            // Only allow <button> and <a> tags - escape everything else
            const safeHtmlPattern =
                /^[^<]*(<(button|a)\s+[^>]*>[^<]*<\/(button|a)>)[^<]*$/;

            if (
                safeHtmlPattern.test(message) &&
                !dangerousAttributes.test(message)
            ) {
                // We trust this limited pattern
                messageContent.innerHTML = message;
            } else {
                // Fallback to safe text if pattern is not safe
                messageContent.textContent = message;
            }
        } else {
            // ✅ Use Markdown → HTML rendering for normal messages
            const html = renderMarkdownToHtml(message);
            messageContent.innerHTML = html;
        }

        messageDiv.appendChild(messageContent);

        // Add keyword images if provided and this is a bot message
        if (
            keywordImages &&
            keywordImages.length > 0 &&
            className.includes("bot-message")
        ) {
            const imagesContainer =
                this.createKeywordImagesContainer(keywordImages);
            if (imagesContainer) {
                messageDiv.appendChild(imagesContainer);
            }
        }

        // Add keyword videos if provided and this is a bot message (add after the images block)
        if (
            videoData &&
            videoData.has_videos &&
            videoData.videos &&
            videoData.videos.length > 0 &&
            className.includes("bot-message")
        ) {
            const videosContainer =
                this.createKeywordVideosContainer(videoData);
            if (videosContainer) {
                messageDiv.appendChild(videosContainer);
            }
        }

        this.chatHistory.appendChild(messageDiv);

        // Smart scrolling: position to show upper part of bot responses
        if (className.includes("bot-message")) {
            const messagePosition = messageDiv.offsetTop;
            const chatHistoryHeight = this.chatHistory.clientHeight;
            const targetScrollTop = Math.max(
                0,
                messagePosition - chatHistoryHeight * 0.2,
            );
            this.chatHistory.scrollTop = targetScrollTop;
        } else {
            this.chatHistory.scrollTop = this.chatHistory.scrollHeight;
        }

        // Dispatch event to notify image viewer modal about chat updates
        if (window.imageViewerModal) {
            window.imageViewerModal.updateMirroredChat();
        }

        // Task #274: prune old messages to keep the DOM (and iOS memory)
        // bounded. On touch devices every retained bot message keeps its
        // decoded image thumbnails alive — over a long conversation that
        // stacks hundreds of MB of bitmaps on top of the WebGL scene and
        // is exactly the profile that makes iOS silently kill+reload the
        // tab. Full history is persisted server-side; only the visible
        // thread is trimmed. Desktop keeps a generous cap.
        const isTouch =
            "ontouchstart" in window ||
            (navigator.maxTouchPoints || 0) > 0;
        const MAX_MESSAGES = isTouch ? 40 : 300;
        const msgs = this.chatHistory.children;
        while (msgs.length > MAX_MESSAGES) {
            const victim = msgs[0];
            // Release media resources before detaching so Safari can
            // actually free decoders/bitmaps, not just DOM nodes.
            try {
                victim.querySelectorAll("video").forEach((v) => {
                    try {
                        v.pause();
                        v.removeAttribute("src");
                        v.load();
                    } catch (_) {}
                });
                victim.querySelectorAll("iframe").forEach((f) => {
                    try {
                        f.src = "about:blank";
                    } catch (_) {}
                });
                victim.querySelectorAll("img").forEach((im) => {
                    try {
                        im.removeAttribute("src");
                        im.removeAttribute("srcset");
                    } catch (_) {}
                });
            } catch (_) {}
            this.chatHistory.removeChild(victim);
        }

        return messageDiv;
    }

    /**
     * Create a container for keyword images to be embedded in bot messages
     * @param {Array} keywordImages - Array of keyword image results from API
     * @returns {HTMLElement|null} - Container element or null if no images
     */
    createKeywordImagesContainer(keywordImages) {
        if (!keywordImages || keywordImages.length === 0) {
            return null;
        }

        console.log(
            `Creating embedded keyword images for ${keywordImages.length} keyword results`,
        );

        // Collect all unique images from all keyword results
        const allImages = [];
        keywordImages.forEach((result) => {
            const images = result.images || [];
            images.forEach((image) => {
                // Only add if not already in our collection
                if (!allImages.find((existing) => existing.url === image.url)) {
                    allImages.push({
                        ...image,
                        keyword: result.fetched_keyword,
                    });
                }
            });
        });

        if (allImages.length === 0) {
            return null;
        }

        // === INNER GRID (same as before) ===
        const imagesGrid = document.createElement("div");
        imagesGrid.className = "embedded-keyword-images";
        imagesGrid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 8px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
        `;

        // Add each image to the grid
        allImages.forEach((image, index) => {
            const imageElement = this.createEmbeddedImage(image, index);
            if (imageElement) {
                imagesGrid.appendChild(imageElement);
            }
        });

        // === OUTER CONTAINER WRAPPER (your new part) ===
        const container = document.createElement("div");
        container.className = "keyword-images-container";
        container.style.cssText = `
            margin-top: 10px;
            padding: 10px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 8px;
        `;

        // Put the grid inside the wrapper
        container.appendChild(imagesGrid);

        // Return the wrapper instead of the raw grid
        return container;
    }

    /**
     * Create a container for keyword videos to be embedded in bot messages
     * @param {Object} videoData - Video data from API with has_videos and videos array
     * @returns {HTMLElement|null} - Container element or null if no videos
     */
    createKeywordVideosContainer(videoData) {
        if (
            !videoData ||
            !videoData.has_videos ||
            !videoData.videos ||
            videoData.videos.length === 0
        ) {
            return null;
        }

        console.log(
            `Creating embedded keyword videos for ${videoData.videos.length} videos`,
        );

        // === INNER GRID ===
        const videosGrid = document.createElement("div");
        const videoCount = videoData.videos.length;
        // Add modifier class based on video count for responsive layout
        let layoutClass = "";
        if (videoCount === 1) {
            layoutClass = "videos-single";
        } else if (videoCount === 2) {
            layoutClass = "videos-two-up";
        } else {
            layoutClass = "videos-three-up";
        }
        videosGrid.className = `embedded-keyword-videos ${layoutClass}`;

        // Add each video to the grid
        videoData.videos.forEach((video, index) => {
            const videoElement = this.createEmbeddedVideo(video, index);
            if (videoElement) {
                videosGrid.appendChild(videoElement);
            }
        });

        // === OUTER CONTAINER WRAPPER ===
        const container = document.createElement("div");
        container.className = "keyword-videos-container";
        container.style.cssText = `
            margin-top: 10px;
            padding: 10px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 8px;
        `;

        // Put the grid inside the wrapper
        container.appendChild(videosGrid);

        return container;
    }

    /**
     * Create an embedded image element for display inside bot messages
     * @param {Object} image - Image data from API
     * @param {number} index - Image index
     * @returns {HTMLElement|null} - Created image element or null
     */
    /**
     * Create an embedded image element for display inside bot messages
     * Uses SimpleImagePanel for 3D preview (same as bio image viewer)
     * @param {Object} image - Image data from API
     * @param {number} index - Image index
     * @returns {HTMLElement|null} - Created image element or null
     */
    createEmbeddedImage(image, index) {
        const imageUrl = image.url;
        const filename = image.filename || "Unknown";
        const keyword = image.keyword || "Unknown";

        // Create image container
        const imageContainer = document.createElement("div");
        imageContainer.className = "embedded-keyword-image";
        imageContainer.style.cssText = `
            position: relative;
            border-radius: 6px;
            overflow: hidden;
            background-color: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            cursor: pointer;
        `;

        // Add hover effects
        imageContainer.addEventListener("mouseenter", () => {
            imageContainer.style.transform = "scale(1.02)";
            imageContainer.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
        });

        imageContainer.addEventListener("mouseleave", () => {
            imageContainer.style.transform = "scale(1)";
            imageContainer.style.boxShadow = "none";
        });

        // Create image element
        const img = document.createElement("img");
        // Task #274: decode lazily — offscreen thumbnails in a long
        // conversation otherwise pin decoded bitmaps (iOS memory kills).
        img.loading = "lazy";
        img.decoding = "async";
        img.src = imageUrl;
        img.alt = `${keyword} - ${filename}`;
        img.style.cssText = `
            width: 100%;
            height: 100px;
            object-fit: cover;
            display: block;
            border-radius: 6px;
        `;

        // Add error handling
        img.onerror = () => {
            imageContainer.style.display = "none";
            console.warn(`Failed to load embedded image: ${imageUrl}`);
        };

        // Add click handler to float the image behind the character (3D preview).
        // Prefer modelViewer.showImage3D — it lazily creates SimpleImagePanel if it
        // doesn't exist yet, so the effect works even before the first show.
        imageContainer.addEventListener("click", () => {
            if (window.modelViewer && typeof window.modelViewer.showImage3D === "function") {
                console.log("Floating chat image behind character (3D):", imageUrl);
                window.modelViewer.showImage3D(imageUrl, keyword || filename || "");
            } else if (window.simpleImagePanel && typeof window.simpleImagePanel.showImage === "function") {
                window.simpleImagePanel.showImage(imageUrl);
            } else if (window.imageViewerModal) {
                // Fallback to modal if the 3D panel isn't available
                window.imageViewerModal.openModal(imageUrl, filename, keyword);
            } else {
                // Last fallback to new tab
                window.open(imageUrl, "_blank");
            }
        });

        imageContainer.appendChild(img);
        return imageContainer;
    }

    /**
     * Create an embedded video element for display inside bot messages
     * @param {Object} video - Video data from API (contains embed_html, keyword, title, etc.)
     * @param {number} index - Video index
     * @returns {HTMLElement|null} - Created video element or null
     */
    createEmbeddedVideo(video, index) {
        if (!video || !video.embed_html) {
            console.warn(`No embed_html found for video ${index}`);
            return null;
        }

        const keyword = video.keyword || "Video";
        const title = video.title || "Related Video";

        // Create video wrapper (styles handled by CSS for responsive layout)
        const videoWrapper = document.createElement("div");
        videoWrapper.className = "embedded-keyword-video-wrapper";

        // Create keyword header
        const keywordHeader = document.createElement("div");
        keywordHeader.className = "video-keyword-header";
        keywordHeader.textContent = keyword;
        keywordHeader.style.cssText = `
            padding: 8px 12px;
            font-size: 12px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.8);
            background: rgba(255, 255, 255, 0.1);
            text-transform: capitalize;
        `;

        // Create video container with 16:9 aspect ratio
        const videoContainer = document.createElement("div");
        videoContainer.className = "embedded-video-container";
        videoContainer.style.cssText = `
            position: relative;
            width: 100%;
            padding-bottom: 56.25%; /* 16:9 aspect ratio */
            height: 0;
            background: #000;
        `;

        // Task #274: click-to-load facade instead of an eager YouTube
        // iframe. Each live embed is a full YouTube player — several of
        // them stacked per reply is a major memory load on iPhone Safari
        // (contributes to the tab force-reload). The facade shows the
        // thumbnail + play button (visually the same as an unplayed
        // embed); on tap: mobile opens the video on YouTube, desktop
        // swaps in the real player with autoplay.
        const facade = createLiteYouTubeEmbed(video, {
            borderRadius: "0 0 8px 8px",
        });
        if (facade) {
            videoContainer.appendChild(facade);
        } else {
            // Fallback (no resolvable video id): legacy eager iframe.
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = video.embed_html;
            const iframe = tempDiv.querySelector("iframe");

            if (iframe) {
                // Remove existing width/height attributes
                iframe.removeAttribute("width");
                iframe.removeAttribute("height");
                iframe.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border: none;
                    border-radius: 0 0 8px 8px;
                `;
                videoContainer.appendChild(iframe);
            } else {
                console.error(
                    `No iframe found in embed HTML for video ${index}`,
                );
                return null;
            }
        }

        videoWrapper.appendChild(keywordHeader);
        videoWrapper.appendChild(videoContainer);

        return videoWrapper;
    }

    addToneIndicator(messageElement, tone) {
        if (!messageElement || !tone || tone === "analyzing") return;

        // Remove existing tone indicator
        const existingIndicator =
            messageElement.querySelector(".tone-indicator");
        if (existingIndicator) {
            existingIndicator.remove();
        }

        // Create tone indicator
        const toneIndicator = document.createElement("span");
        toneIndicator.className = "tone-indicator";
        toneIndicator.style.cssText = `
            display: inline-block;
            margin-left: 8px;
            padding: 2px 6px;
            font-size: 0.8em;
            border-radius: 12px;
            font-weight: bold;
            text-transform: capitalize;
        `;

        // Set color and emoji based on tone
        const toneStyles = {
            happy: { bg: "#e8f5e8", color: "#2e7d32", emoji: "😊" },
            sad: { bg: "#e3f2fd", color: "#1565c0", emoji: "😢" },
            angry: { bg: "#ffebee", color: "#c62828", emoji: "😠" },
            fear: { bg: "#f3e5f5", color: "#7b1fa2", emoji: "😨" },
            surprise: { bg: "#fff3e0", color: "#ef6c00", emoji: "😲" },
            neutral: { bg: "#f5f5f5", color: "#616161", emoji: "😐" },
        };

        const style = toneStyles[tone] || toneStyles.neutral;
        toneIndicator.style.backgroundColor = style.bg;
        toneIndicator.style.color = style.color;
        toneIndicator.textContent = `${style.emoji} ${tone}`;

        messageElement.appendChild(toneIndicator);
    }

    setupSideButtonEventListeners() {
        // Share button
        const shareButton = document.getElementById("share-button");
        if (shareButton) {
            shareButton.addEventListener("click", () => this.handleShare());
        }

        // History button is now handled independently in main.js

        // Replay button
        const replayButton = document.getElementById("replay-button");
        if (replayButton) {
            replayButton.addEventListener("click", () => this.handleReplay());
        }

        // Fact button
        const factButton = document.getElementById("fact-button");
        if (factButton) {
            factButton.addEventListener("click", () => this.requestFact());
        }

        // Mode switching buttons
        if (this.answerModeBtn) {
            this.answerModeBtn.addEventListener("click", () =>
                this.switchUIMode("answer"),
            );
        }
        if (this.imageModeBtn) {
            this.imageModeBtn.addEventListener("click", () =>
                this.switchUIMode("image"),
            );
        }
        if (this.videoModeBtn) {
            this.videoModeBtn.addEventListener("click", () =>
                this.switchUIMode("video"),
            );
        }
    }

    async handleShare() {
        // Share a clean per-character link (/lab/<slug>) so the pasted preview
        // shows THIS legend's card — not whatever query string the tab is on.
        let shareUrl = window.location.href;
        try {
            const modelId = this.getCurrentModelId();
            if (modelId) {
                shareUrl = window.location.origin + getLabUrlForModel(modelId);
            }
        } catch (e) {
            /* fall back to current URL */
        }

        const shareData = {
            title: "AI Character Interaction",
            text: "Check out this amazing AI character interaction!",
            url: shareUrl,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareUrl);
                // Show a temporary toast message
                const toast = document.createElement("div");
                toast.className =
                    "position-fixed top-50 start-50 translate-middle bg-dark text-white p-3 rounded";
                toast.style.zIndex = "9999";
                toast.textContent = "Link copied to clipboard!";
                document.body.appendChild(toast);
                setTimeout(() => toast.remove(), 2000);
            }
        } catch (err) {
            console.error("Error sharing:", err);
        }
    }

    async handleReplay() {
        if (!this.lastAudioResponse) {
            console.log("No previous response to replay");
            return;
        }

        // Stop any existing audio
        if (this.isAudioPlaying) {
            this.stopAudio();
        }

        try {
            // Show stop button
            if (this.stopAudioButton) {
                this.stopAudioButton.classList.remove("d-none");
                // Hide send button when stop button is shown
            }
            if (this.sendButton) {
                this.sendButton.classList.remove("d-none");
            }

            // Keep microphone button visible during replay

            this.isAudioPlaying = true;

            // AEC (Acoustic Echo Cancellation) handles echo prevention - no need to pause microphone
            console.log(
                "🎵 Audio replay starting - WebRTC AEC prevents echo while allowing interruptions",
            );

            if (this.animationController) {
                this.animationController.onTTSStart();
            }

            // Connect animation controller directly to TTS if needed
            if (this.animationController && !this.tts.animationController) {
                this.tts.setAnimationController(this.animationController);
                console.log(
                    "Connected animation controller directly to TTS service in replay",
                );
            }

            // Check if we have a stored audio blob and URL
            if (this.lastAudioBlob && this.lastAudioUrl) {
                console.log("Replaying using stored audio blob");

                // Set up tts onPlayStart handler for animations with timing data
                this.tts.onPlayStart = async (audioElement) => {
                    if (this.lipSyncController) {
                        // Use character-based fallback for replay (timing data not stored)
                        this.lipSyncController.speak(this.lastAudioResponse);
                    }
                    if (this.animationController) {
                        this.animationController.onAudioPlaybackStart();
                    }
                };

                // Legacy TTS replay disabled - new TTS system handles audio
                console.log(
                    "Legacy TTS replay disabled - new TTS system will handle audio",
                );
            } else {
                // Legacy TTS disabled
                console.log(
                    "Legacy TTS disabled - new TTS system will handle audio",
                );
            }

            // Natural completion
            this.stopAudio();

            // Explicitly notify animation controller of TTS end
            if (this.animationController) {
                this.animationController.onTTSEnd();
                console.log(
                    "Animation controller explicitly notified of TTS end after replay",
                );
            }
        } catch (error) {
            console.error("Replay error:", error);
            this.stopAudio();
        }
    }

    async requestFact() {
        const message = "Tell me an interesting fact about yourself";
        await this.sendMessage(message);
    }

    // Clean up resources when the page is unloaded
    cleanUp() {
        console.log("Cleaning up resources before page unload");

        // Stop any playing audio
        if (this.isAudioPlaying) {
            this.stopAudio();
        }

        // Clean up any stored audio URL
        if (this.lastAudioUrl) {
            URL.revokeObjectURL(this.lastAudioUrl);
            this.lastAudioUrl = null;
            console.log("Cleaned up stored audio URL");
        }

        // Clean up TTS resources
        if (this.tts) {
            this.tts.stop();
        }
    }

    async checkAllComponentsReady(requestId, audioElement) {
        /**
         * Check if all components are ready for synchronized playback:
         * 1. Phoneme sequence ready
         * 2. Duration sequence ready
         * 3. Audio element ready
         */
        console.log("Starting component readiness verification...");

        const readinessStatus = {
            allReady: false,
            missingComponents: [],
            phonemeSequenceReady: false,
            durationSequenceReady: false,
            audioElementReady: false,
        };

        try {
            // Check 1: Audio element readiness
            if (
                audioElement &&
                audioElement.duration &&
                audioElement.readyState >= 3
            ) {
                readinessStatus.audioElementReady = true;
                console.log(
                    "✓ Audio element ready - duration:",
                    audioElement.duration.toFixed(2),
                    "readyState:",
                    audioElement.readyState,
                );
            } else {
                readinessStatus.missingComponents.push(
                    "Audio element not ready or missing duration",
                );
                console.log(
                    "✗ Audio element not ready:",
                    audioElement
                        ? `readyState: ${audioElement.readyState}`
                        : "missing",
                );
            }

            // Check 2 & 3: Phoneme and duration sequences from ElevenLabs data
            const sequenceData =
                await this.lipSyncController.getElevenLabsSequence(requestId);

            if (
                sequenceData &&
                sequenceData.phonemeSequence &&
                sequenceData.phonemeSequence.length > 0
            ) {
                readinessStatus.phonemeSequenceReady = true;
                console.log(
                    "✓ Phoneme sequence ready -",
                    sequenceData.phonemeSequence.length,
                    "phonemes",
                );
            } else {
                readinessStatus.missingComponents.push(
                    "Phoneme sequence missing or empty",
                );
                console.log("✗ Phoneme sequence not ready");
            }

            if (
                sequenceData &&
                sequenceData.durationSequence &&
                sequenceData.durationSequence.length > 0
            ) {
                readinessStatus.durationSequenceReady = true;
                console.log(
                    "✓ Duration sequence ready -",
                    sequenceData.durationSequence.length,
                    "durations",
                );
            } else {
                readinessStatus.missingComponents.push(
                    "Duration sequence missing or empty",
                );
                console.log("✗ Duration sequence not ready");
            }

            // Validate sequences match
            if (
                readinessStatus.phonemeSequenceReady &&
                readinessStatus.durationSequenceReady
            ) {
                if (
                    sequenceData.phonemeSequence.length ===
                    sequenceData.durationSequence.length
                ) {
                    console.log(
                        "✓ Phoneme and duration sequences match in length",
                    );
                } else {
                    readinessStatus.missingComponents.push(
                        "Phoneme and duration sequence length mismatch",
                    );
                    console.log(
                        "✗ Sequence length mismatch - phonemes:",
                        sequenceData.phonemeSequence.length,
                        "durations:",
                        sequenceData.durationSequence.length,
                    );
                }
            }

            // Final readiness determination
            readinessStatus.allReady =
                readinessStatus.phonemeSequenceReady &&
                readinessStatus.durationSequenceReady &&
                readinessStatus.audioElementReady &&
                readinessStatus.missingComponents.length === 0;

            if (readinessStatus.allReady) {
                console.log(
                    "🎯 ALL COMPONENTS READY FOR SYNCHRONIZED PLAYBACK",
                );
                // Pre-prepare the lip sync data now that everything is confirmed ready
                await this.lipSyncController.prepareElevenLabsData(
                    requestId,
                    audioElement,
                );
            } else {
                console.log(
                    "❌ Components not ready:",
                    readinessStatus.missingComponents,
                );
            }

            return readinessStatus;
        } catch (error) {
            console.error("Error during readiness check:", error);
            readinessStatus.missingComponents.push(
                `Readiness check error: ${error.message}`,
            );
            return readinessStatus;
        }
    }

    // pollToneAnalysis removed in Task #38: GPT-4o-mini text tone analyzer
    // is gone — facial expression is now driven by A2F's per-utterance
    // emotion vector (mapped to ARKit blendshapes by A2FEmotionRenderer).

    async waitForElevenLabsData(requestId) {
        // Legacy ElevenLabs polling - now disabled since we use the new TTS system
        console.log(
            "Legacy ElevenLabs polling disabled - using new TTS system",
        );
        return Promise.resolve({ status: "disabled", sequence: [] });
    }

    /**
     * Get the current character name for chat storage
     * @returns {string|null} Current character name or null if not available
     */
    getCurrentCharacterName() {
        try {
            // Method 1: Try to get from ModelManager (most reliable)
            if (
                window.ModelManager &&
                typeof window.ModelManager.getCurrentModelId === "function"
            ) {
                const modelId = window.ModelManager.getCurrentModelId();
                // Doc-born characters resolve via getCharacterById (synthesized
                // from window.__docCharacters), never as hand-wired map keys.
                const record =
                    modelId && window.characterData
                        ? window.characterData[modelId] ||
                          (typeof window.characterData.getCharacterById ===
                          "function"
                              ? window.characterData.getCharacterById(modelId)
                              : null)
                        : null;
                if (record && record.name) {
                    const characterName = record.name;
                    console.log(
                        "ChatController: Got character name from ModelManager:",
                        characterName,
                    );
                    return characterName;
                }
            }

            // Method 2: Try to get from localStorage
            const storedModelId = localStorage.getItem("selectedModelId");
            const storedRecord =
                storedModelId && window.characterData
                    ? window.characterData[storedModelId] ||
                      (typeof window.characterData.getCharacterById ===
                      "function"
                          ? window.characterData.getCharacterById(
                                storedModelId,
                            )
                          : null)
                    : null;
            if (storedRecord && storedRecord.name) {
                const characterName = storedRecord.name;
                console.log(
                    "ChatController: Got character name from localStorage:",
                    characterName,
                );
                return characterName;
            }

            // Method 3: Try to get from BioController if available
            if (
                window.bioController &&
                window.bioController.currentCharacterName
            ) {
                console.log(
                    "ChatController: Got character name from BioController:",
                    window.bioController.currentCharacterName,
                );
                return window.bioController.currentCharacterName;
            }

            console.warn(
                "ChatController: Could not determine current character name",
            );
            return null;
        } catch (error) {
            console.error(
                "ChatController: Error getting current character name:",
                error,
            );
            return null;
        }
    }

    /**
     * Get the current character's portrait image URL from character data.
     * Returns null if it can't be resolved (the popup falls back gracefully).
     */
    getCurrentCharacterImageUrl() {
        try {
            let modelId = null;
            if (
                window.ModelManager &&
                typeof window.ModelManager.getCurrentModelId === "function"
            ) {
                modelId = window.ModelManager.getCurrentModelId();
            }
            if (!modelId) {
                modelId = localStorage.getItem("selectedModelId");
            }
            // Doc-born characters resolve via getCharacterById (synthesized
            // from window.__docCharacters), never as hand-wired map keys.
            const record =
                modelId && window.characterData
                    ? window.characterData[modelId] ||
                      (typeof window.characterData.getCharacterById ===
                      "function"
                          ? window.characterData.getCharacterById(modelId)
                          : null)
                    : null;
            if (record && (record.imageUrl || record.portrait)) {
                return record.imageUrl || record.portrait;
            }
        } catch (error) {
            console.warn(
                "ChatController: Error getting current character image URL:",
                error,
            );
        }
        return null;
    }

    /**
     * Add a message to the conversation history
     * @param {string} role - Either 'user' or 'assistant'
     * @param {string} content - The message content
     */
    addToConversationHistory(role, content) {
        // Validate role
        if (role !== "user" && role !== "assistant") {
            console.warn("Invalid role for conversation history:", role);
            return;
        }

        // Add to history
        this.conversationHistory.push({
            role: role,
            content: content,
        });

        // Limit conversation history to last 6 messages (3 exchanges) to manage API costs
        // and stay within token limits
        const maxMessages = 8;
        if (this.conversationHistory.length > maxMessages) {
            this.conversationHistory =
                this.conversationHistory.slice(-maxMessages);
        }

        console.log(
            `Added ${role} message to conversation history. Total messages: ${this.conversationHistory.length}`,
        );
    }

    /**
     * Clear conversation history (useful when switching characters)
     */
    clearConversationHistory() {
        this.conversationHistory = [];
        // Emotion controller removed in Task #38.
        console.log("Conversation history cleared");
    }

    /**
     * Get current conversation history
     * @returns {Array} Array of conversation messages
     */
    getConversationHistory() {
        return [...this.conversationHistory]; // Return a copy to prevent external modification
    }

    /**
     * Get current Tutor Hub context for sharing with main chat
     * Extracts the current learning material from TutorState when Tutor Hub is open
     * @returns {Object|null} Tutor context object or null if not available
     */
    getTutorContext() {
        try {
            const tutorState = window.TutorState;
            if (!tutorState) return null;

            const learningLoop = tutorState.learningLoop || {};
            const learningPath = learningLoop.learning_path || [];
            const currentIdx = learningLoop.currentSubtopicIndex || 0;
            const currentSubtopic = learningPath[currentIdx];
            const currentTutorial = learningLoop.currentTutorial;

            // Build context object with available information
            const context = {
                topic: learningLoop.topic || null,
                view: tutorState.currentView || null,
            };

            // Add current subtopic information
            if (currentSubtopic) {
                context.subtopic =
                    currentSubtopic.name ||
                    currentSubtopic.subtopic_name ||
                    null;
                context.subtopic_description =
                    currentSubtopic.description || null;
            }

            // Add tutorial content if currently viewing a tutorial
            if (currentTutorial) {
                let tutorialText = "";

                // Extract key concepts
                if (
                    currentTutorial.key_concepts &&
                    currentTutorial.key_concepts.length > 0
                ) {
                    tutorialText += "Key Concepts:\n";
                    currentTutorial.key_concepts.forEach((concept, i) => {
                        tutorialText += `${i + 1}. ${concept.concept}: ${concept.explanation}\n`;
                    });
                }

                // Extract step-by-step breakdown
                if (
                    currentTutorial.step_by_step &&
                    currentTutorial.step_by_step.length > 0
                ) {
                    tutorialText += "\nStep-by-Step Guide:\n";
                    currentTutorial.step_by_step.forEach((step, i) => {
                        tutorialText += `${i + 1}. ${step}\n`;
                    });
                }

                // Extract worked examples
                if (
                    currentTutorial.worked_examples &&
                    currentTutorial.worked_examples.length > 0
                ) {
                    tutorialText += "\nWorked Examples:\n";
                    currentTutorial.worked_examples.forEach((ex) => {
                        tutorialText += `- ${ex.title || "Example"}: ${ex.example}\n`;
                    });
                }

                // Extract common misconceptions
                if (
                    currentTutorial.common_misconceptions &&
                    currentTutorial.common_misconceptions.length > 0
                ) {
                    tutorialText += "\nCommon Misconceptions:\n";
                    currentTutorial.common_misconceptions.forEach((mis) => {
                        tutorialText += `- Misconception: ${mis.description}\n  Correction: ${mis.correction}\n`;
                    });
                }

                // Add checkpoint summary
                if (currentTutorial.checkpoint_summary) {
                    tutorialText += `\nCheckpoint Summary: ${currentTutorial.checkpoint_summary}`;
                }

                if (tutorialText.trim()) {
                    context.tutorial_content = tutorialText.trim();
                }
            }

            // Add learning path progress info
            if (learningPath.length > 0) {
                context.total_subtopics = learningPath.length;
                context.current_subtopic_index = currentIdx + 1;

                // List all subtopics for broader context
                context.all_subtopics = learningPath
                    .map((s) => s.name || s.subtopic_name)
                    .filter(Boolean);
            }

            // Add completed steps
            if (
                tutorState.completedSteps &&
                Object.keys(tutorState.completedSteps).length > 0
            ) {
                context.completed_subtopics = Object.keys(
                    tutorState.completedSteps,
                );
            }

            // Only return if we have meaningful content
            if (context.topic || context.subtopic || context.tutorial_content) {
                console.log("[ChatController] Tutor context extracted:", {
                    topic: context.topic,
                    subtopic: context.subtopic,
                    hasTutorial: !!context.tutorial_content,
                    progress: `${context.current_subtopic_index}/${context.total_subtopics}`,
                });
                return context;
            }

            return null;
        } catch (error) {
            console.warn(
                "[ChatController] Error extracting tutor context:",
                error,
            );
            return null;
        }
    }

    /**
     * Send a streaming message and handle real-time response
     * @param {string} message - The message to send
     * @param {string} messageType - Optional message type ('image_description' for brief responses)
     * @returns {Promise<Object>} - Promise resolving to response data
     */
    async sendStreamingMessage(message, messageType = null) {
        // 1) Cancel any existing stream
        if (this.streamingController) {
            this.streamingController.abort();
        }

        // 2) Prep TTS (Safari-safe) *before* creating the Promise
        const rtts = this.realtimeTTS || this.tts || null;
        if (rtts) {
            try {
                // Check if ensureReadyForNextResponse method exists (RealtimeLLMToElevenLabs has it, ElevenLabsTTS doesn't)
                if (
                    rtts &&
                    typeof rtts.ensureReadyForNextResponse === "function"
                ) {
                    // Reset epoch + allow audio + WS ready + resume/recreate AudioContext
                    await rtts.ensureReadyForNextResponse(120);
                }

                // Resume audio context if needed (works for both TTS implementations)
                if (rtts.audioCtx && rtts.audioCtx.state !== "running") {
                    await rtts.audioCtx.resume();
                }
            } catch (e) {
                console.warn(
                    "[sendStreamingMessage] TTS prep failed (continuing)",
                    e,
                );
            }
        }

        // 2b) Resolve a Firebase ID token so the server can identify the caller.
        // Anonymous (free-taste) users never call the session sync endpoint, so
        // the Bearer token is the only way the server learns their uid + provider
        // to apply the abuse cap. Real users send it too (server prefers it).
        let authToken = null;
        try {
            const u = window.firebaseAuth?.currentUser;
            if (u && typeof u.getIdToken === "function") {
                authToken = await u.getIdToken();
            }
        } catch (e) {
            console.warn("[sendStreamingMessage] getIdToken failed (continuing)", e);
        }

        // 3) Now create controller and do XHR streaming
        this.streamingController = new AbortController();

        return new Promise((resolve, reject) => {
            // Initialize streaming message element
            this.currentStreamingMessage = this.addMessageToChat(
                "",
                "",
                "bot-message",
            );
            let streamingContent = "";
            let requestId = null;
            let modelInfo = null;

            console.log("🚀 Using XMLHttpRequest for real-time streaming");
            const xhr = new XMLHttpRequest();
            xhr.open("POST", "/api/chat/stream", true);
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.setRequestHeader("Cache-Control", "no-cache");
            if (authToken) {
                xhr.setRequestHeader("Authorization", "Bearer " + authToken);
            }

            let lastProcessedLength = 0;
            let processingBuffer = "";
            let streamingStarted = false;
            let streamingContentStarted = false;

            let freeLimitHandled = false;
            xhr.onreadystatechange = () => {
                // Free-taste / abuse cap: the server returns 403 with
                // {code:"FREE_LIMIT_REACHED"} (not SSE) when an anonymous caller
                // is out of free exchanges for the day. Raise the continuation
                // wall and stop instead of trying to parse it as a stream.
                if (
                    !freeLimitHandled &&
                    xhr.readyState >= 2 &&
                    xhr.status === 403
                ) {
                    let isFreeLimit = false;
                    try {
                        const body = JSON.parse(xhr.responseText || "{}");
                        isFreeLimit = body && body.code === "FREE_LIMIT_REACHED";
                    } catch (e) {}
                    if (isFreeLimit) {
                        freeLimitHandled = true;
                        this.pendingMessage = message;
                        try {
                            window.trackEvent &&
                                window.trackEvent("wall_shown", {
                                    source: "server",
                                });
                        } catch (e) {}
                        try {
                            const el = this.currentStreamingMessage;
                            if (el && el.parentNode) {
                                el.parentNode.removeChild(el);
                            }
                            this.currentStreamingMessage = null;
                        } catch (e) {}
                        if (
                            typeof window.showContinuationWall === "function"
                        ) {
                            window.showContinuationWall();
                        } else if (window.authController) {
                            window.authController.showAuthModal();
                        } else if (
                            typeof window.showNewUiAuthModal === "function"
                        ) {
                            window.showNewUiAuthModal();
                        }
                        try {
                            xhr.abort();
                        } catch (e) {}
                        reject(new Error("FREE_LIMIT_REACHED"));
                        return;
                    }
                }

                if (xhr.readyState >= 3) {
                    if (freeLimitHandled || xhr.status === 403) return;
                    const fullResponse = xhr.responseText;
                    const newData = fullResponse.substring(lastProcessedLength);
                    lastProcessedLength = fullResponse.length;

                    if (newData) {
                        processingBuffer += newData;

                        let startPos = 0;
                        let newlinePos = processingBuffer.indexOf(
                            "\n",
                            startPos,
                        );

                        while (newlinePos !== -1) {
                            const line = processingBuffer.substring(
                                startPos,
                                newlinePos,
                            );

                            if (line.startsWith("data: ")) {
                                try {
                                    const data = JSON.parse(line.slice(6));

                                    if (data.type === "metadata") {
                                        requestId = data.request_id;
                                        modelInfo = data.model;

                                        // Start realtime TTS stream (non-blocking here)
                                        if (
                                            this.realtimeTTS &&
                                            !streamingStarted
                                        ) {
                                            try {
                                                // don't await inside this handler
                                                this.realtimeTTS.start();
                                                // Signal that new turn is starting and get turn ID for delta verification
                                                if (
                                                    typeof this.realtimeTTS
                                                        .expectNewTurn ===
                                                    "function"
                                                ) {
                                                    this._currentStreamingTurnId =
                                                        this.realtimeTTS.expectNewTurn();
                                                }
                                                streamingStarted = true;
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] Failed to start:",
                                                    err,
                                                );
                                            }
                                        }

                                        if (
                                            this.realtimeTTSEnabled &&
                                            this.ttsManager
                                        ) {
                                            try {
                                                this.ttsManager.onAssistantTurnStart();
                                            } catch (err) {
                                                console.warn(
                                                    "🎵 [New TTS Manager] Failed to start:",
                                                    err,
                                                );
                                            }
                                        }
                                    } else if (data.type === "chunk") {
                                        streamingContent += data.content;

                                        // Feed to realtime TTS
                                        if (
                                            this.realtimeTTS &&
                                            streamingStarted
                                        ) {
                                            try {
                                                // Pass turn ID to verify delta belongs to this turn
                                                this.realtimeTTS.handleLLMDelta(
                                                    data.content,
                                                    this
                                                        ._currentStreamingTurnId,
                                                );
                                                if (!streamingContentStarted) {
                                                    streamingContentStarted = true;
                                                }
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] Delta error:",
                                                    err,
                                                );
                                            }
                                        }

                                        // Feed to new TTS Manager
                                        if (
                                            this.realtimeTTSEnabled &&
                                            this.ttsManager
                                        ) {
                                            try {
                                                // Pass turn ID for ownership verification
                                                this.ttsManager.onOpenAIDelta(
                                                    data.content,
                                                    this
                                                        ._currentStreamingTurnId,
                                                );
                                            } catch (err) {
                                                console.warn(
                                                    "🎵 [New TTS Manager] Delta error:",
                                                    err,
                                                );
                                            }
                                        }

                                        // Update DOM
                                        if (this.currentStreamingMessage) {
                                            const messageContent =
                                                this.currentStreamingMessage.querySelector(
                                                    ".message-content",
                                                );
                                            if (messageContent) {
                                                const html =
                                                    renderMarkdownToHtml(
                                                        streamingContent,
                                                    );
                                                messageContent.innerHTML = html;
                                                // Option 1: keep the reply anchored near its top; no
                                                // auto-scroll-to-bottom while streaming.
                                            }
                                        }
                                    } else if (data.type === "complete") {
                                        // Close realtime TTS turn
                                        if (
                                            this.realtimeTTS &&
                                            streamingStarted
                                        ) {
                                            try {
                                                this.realtimeTTS.flushAndClose();
                                            } catch (err) {
                                                console.warn(
                                                    "🎤 [RealtimeTTS] End turn error:",
                                                    err,
                                                );
                                            }
                                        }

                                        if (
                                            this.realtimeTTSEnabled &&
                                            this.ttsManager
                                        ) {
                                            try {
                                                this.ttsManager.onAssistantTurnEnd();
                                            } catch (err) {
                                                console.warn(
                                                    "🎵 [New TTS Manager] End turn error:",
                                                    err,
                                                );
                                            }
                                        }

                                        this.processStreamingCompletion(data);
                                        resolve({
                                            status: "success",
                                            response: data.complete_response,
                                            tone: data.tone,
                                            keyword_images: data.keyword_images,
                                            request_id: data.request_id,
                                            model: modelInfo,
                                            audio_status: data.audio_status,
                                            processing_mode:
                                                data.processing_mode,
                                            realtime_tts_used:
                                                this.useRealtimeTTS &&
                                                streamingStarted,
                                        });
                                        return;
                                    }
                                } catch (parseError) {
                                    console.warn(
                                        "Parse error in real-time stream:",
                                        parseError,
                                    );
                                }
                            }

                            startPos = newlinePos + 1;
                            newlinePos = processingBuffer.indexOf(
                                "\n",
                                startPos,
                            );
                        }

                        // keep the tail (incomplete line)
                        processingBuffer = processingBuffer.substring(startPos);
                    }
                }

                if (xhr.readyState === 4) {
                    console.log("🎯 XMLHttpRequest streaming complete");
                }
            };

            xhr.onerror = () => {
                console.error("❌ XMLHttpRequest streaming error");
                reject(new Error("Real-time streaming failed"));
            };

            // Handle abortion
            this.streamingController.signal.addEventListener("abort", () =>
                xhr.abort(),
            );

            // Start the request
            const characterId = this.getCurrentCharacterId();
            // Safari-safe: send ONLY the IANA timezone. The server computes
            // the actual hour from its own UTC clock projected into that zone.
            const clientTime = getClientTimeInfo();
            const clientTimezone = clientTime ? clientTime.timeZone : null;

            const requestBody = {
                message,
                conversation_history: this.conversationHistory,
                character_id: characterId, // null when no character is loaded (triggers Assistant mode)
                client_timezone: clientTimezone,
                conversation_style: (window.LLConversationStyle || 'natural'),
            };

            // Log if we're in assistant mode (no character selected)
            if (!characterId) {
                console.log(
                    "[ChatController] No character selected - using Legend Lab AI Assistant mode",
                );
            }

            // Add message type if specified (for brief responses to image descriptions)
            if (messageType) {
                requestBody.message_type = messageType;
            }

            // Include active book context if the EPUB reader is open or
            // if the user has read a page recently. Lets the avatar answer
            // questions about "this page", quoted lines, etc.
            if (window.ActiveBookContext) {
                const bc = window.ActiveBookContext;
                const activeCharId = this.getCurrentCharacterId &&
                    this.getCurrentCharacterId();
                if (
                    bc.character_id &&
                    activeCharId &&
                    bc.character_id === activeCharId
                ) {
                    requestBody.book_context = {
                        character_id: bc.character_id,
                        title: bc.title,
                        author: bc.author,
                        chapter: bc.chapter,
                        location: bc.location,
                        excerpt: bc.excerpt,
                        prev_tail: bc.prev_tail || "",
                        next_head: bc.next_head || "",
                        selection: bc.selection || "",
                        now_reading: bc.now_reading || "",
                        progress_pct: bc.progress_pct || 0,
                        page_hash: bc.page_hash || "",
                        page_unchanged: !!bc.page_unchanged,
                    };
                    console.log(
                        "[ChatController] Including book context:",
                        bc.title,
                        "-",
                        bc.location,
                    );
                } else if (bc.character_id && activeCharId && bc.character_id !== activeCharId) {
                    console.log(
                        "[ChatController] Dropping stale book context (char mismatch)",
                    );
                    window.ActiveBookContext = null;
                }
            }

            // Include active lesson context if available (from Lesson Hub)
            if (window.activeLessonContext) {
                requestBody.lesson_context = {
                    character: window.activeLessonContext.character,
                    topic: window.activeLessonContext.topicTitle,
                    subtopic: window.activeLessonContext.subtopicTitle,
                    content: window.activeLessonContext.lessonContent,
                };
                console.log(
                    "[ChatController] Including lesson context:",
                    requestBody.lesson_context.subtopic,
                );
            }

            // Include tutor context if Tutor Hub is open and has learning material
            if (
                document.body.classList.contains("tutor-hub-active") &&
                window.TutorState
            ) {
                const tutorContext = this.getTutorContext();
                if (tutorContext) {
                    requestBody.tutor_context = tutorContext;
                    console.log(
                        "[ChatController] Including tutor context:",
                        tutorContext.subtopic || tutorContext.topic,
                    );
                }
            }

            xhr.send(JSON.stringify(requestBody));
        });
    }

    /**
     * Update the streaming message content in real-time
     * @param {string} content - The current streaming content
     */
    updateStreamingMessage(content) {
        if (this.currentStreamingMessage) {
            const messageContent =
                this.currentStreamingMessage.querySelector(".message-content");
            if (messageContent) {
                console.log(
                    "🔄 Updating message content:",
                    content.length + " chars",
                );
                // OLD:
                // messageContent.textContent = content;

                // NEW:
                const html = renderMarkdownToHtml(content);
                messageContent.innerHTML = html;
                console.log("✅ Message content updated in DOM");
            } else {
                console.warn("❌ No .message-content element found");
            }
        } else {
            console.warn("❌ No currentStreamingMessage element");
        }
    }

    /**
     * Instant DOM update for streaming content without delay
     * @param {string} content - The content to display immediately
     */
    updateStreamingMessageInstant(content) {
        if (this.currentStreamingMessage) {
            const messageContent =
                this.currentStreamingMessage.querySelector(".message-content");
            if (messageContent) {
                console.log("⚡ INSTANT DOM update:", content.length, "chars");
                // OLD:
                // messageContent.textContent = content;

                // NEW:
                const html = renderMarkdownToHtml(content);
                messageContent.innerHTML = html;
                console.log("✅ Content displayed instantly");
            }
        }
    }

    /**
     * Process a streaming chunk immediately
     * @param {Object} data - The chunk data
     * @param {string} streamingContent - Current accumulated content
     * @param {string} requestId - Request ID
     * @param {Object} modelInfo - Model information
     * @param {Function} resolve - Promise resolve function
     */
    processStreamChunk(data, streamingContent, requestId, modelInfo, resolve) {
        console.log(
            "📝 Processing stream chunk:",
            data.type,
            data.content ? data.content.length + " chars" : "",
        );
    }

    /**
     * Get the current partial streaming content during interruption
     * @returns {string} - The partial response content or empty string
     */
    getPartialStreamingContent() {
        if (this.currentStreamingMessage) {
            const messageContent =
                this.currentStreamingMessage.querySelector(".message-content");
            if (messageContent) {
                return messageContent.textContent || "";
            }
        }
        return "";
    }

    /**
     * Process the streaming completion with tone and keyword data
     * @param {Object} data - Complete response data from streaming endpoint
     */
    processStreamingCompletion(data) {
        try {
            // DEBUG: Log raw data received from streaming completion
            console.log("🎬 === STREAMING COMPLETION RECEIVED ===");
            console.log("🎬 video_data:", data.video_data);
            console.log(
                "🎬 video_data.has_videos:",
                data.video_data?.has_videos,
            );
            console.log(
                "🎬 video_data.video_count:",
                data.video_data?.video_count,
            );
            console.log("🎬 video_data.videos:", data.video_data?.videos);
            console.log("🎬 === END STREAMING COMPLETION DEBUG ===");

            const finalText = data.complete_response || "";

            // ========== 1) KEYWORD IMAGES ==========
            if (data.keyword_images && data.keyword_images.length > 0) {
                // Process keyword images for the image mode viewer only
                this.processKeywordImages(data.keyword_images);

                // Append images to the current message bubble (DOM element)
                if (this.currentStreamingMessage) {
                    this.appendKeywordImagesToMessage(
                        this.currentStreamingMessage,
                        data.keyword_images,
                    );
                }
            }

            // ========== 2) VIDEOS (independent of images) ==========
            const videoData = data.video_data || {};
            if (
                videoData.has_videos &&
                videoData.videos &&
                videoData.videos.length > 0
            ) {
                console.log(
                    "=== KEYWORD VIDEOS FOUND ===",
                    videoData.videos.length,
                    "videos",
                );

                // Append videos to the current message bubble (DOM element)
                if (this.currentStreamingMessage) {
                    this.appendKeywordVideosToMessage(
                        this.currentStreamingMessage,
                        videoData,
                    );
                }

                // Also process for video mode viewer
                this.videoController.processVideoData(videoData);
            } else {
                console.log(
                    "No videos found in streaming response - video data:",
                    videoData,
                );
            }

            // ========== 2b) SOURCE CITATION CHIPS (Character-Cited Answers) ==========
            // Honesty rule: the server sends citations ONLY when the model
            // actually drew on retrieved passages. No citations → no chips.
            if (
                Array.isArray(data.citations) &&
                data.citations.length > 0 &&
                this.currentStreamingMessage
            ) {
                this.appendCitationChipsToMessage(
                    this.currentStreamingMessage,
                    data.citations,
                );
            }

            // GPT-4o-mini text tone analysis removed in Task #38: facial
            // expression is now driven by A2F's per-utterance emotion vector
            // (mapped to ARKit blendshapes by A2FEmotionRenderer).

            // ========== 4) FINAL MARKDOWN RENDER (IMPORTANT) ==========
            // No matter what images/videos did, make sure the final text
            // inside the existing streaming bubble is rendered as Markdown.
            if (this.currentStreamingMessage) {
                const messageContent =
                    this.currentStreamingMessage.querySelector(
                        ".message-content",
                    );
                if (messageContent) {
                    const html = renderMarkdownToHtml(finalText);
                    messageContent.innerHTML = html; // ✅ keep **bold** etc.
                }
            } else {
                // Fallback: if for some reason we lost the streaming bubble,
                // create a fresh bot message using the Markdown-aware method.
                this.addMessageToChat(
                    "",
                    finalText,
                    "bot-message",
                    data.keyword_images || null,
                );
            }

            console.log(
                "✅ Streaming completion processed with tone:",
                data.tone,
                "and",
                data.keyword_images ? data.keyword_images.length : 0,
                "keyword images",
            );
        } catch (error) {
            console.error("Error processing streaming completion:", error);
        }
    }

    /**
     * Character-Cited Answers: render up to 3 source chips under a finished
     * assistant bubble. Chip click opens the Reader on the cited book at the
     * cited passage (EpubReaderController.openCitation handles navigation +
     * highlight).
     */
    appendCitationChipsToMessage(messageElement, citations) {
        if (messageElement.querySelector(".citation-chips")) return;
        this._ensureCitationChipStyles();
        const wrap = document.createElement("div");
        wrap.className = "citation-chips";
        (citations || []).slice(0, 3).forEach((c) => {
            if (!c || !c.work) return;
            const chip = document.createElement("button");
            chip.type = "button";
            chip.className = "citation-chip";
            const label = c.chapter
                ? `${c.work} · ${c.chapter}`
                : c.work + (c.page ? ` · p. ${c.page}` : "");
            chip.textContent = `📖 ${label}`;
            chip.title = (c.snippet || "").slice(0, 160);
            chip.addEventListener("click", () => this._openCitation(c));
            wrap.appendChild(chip);
        });
        if (wrap.childElementCount > 0) messageElement.appendChild(wrap);
    }

    _openCitation(citation) {
        try {
            const characterId = this.getCurrentCharacterId();
            const reader =
                typeof window.getEpubReader === "function"
                    ? window.getEpubReader()
                    : window.epubReader;
            if (!reader || !characterId) return;
            // Mirror the real-book-card open path: put the right panel into
            // reader view before mounting the embedded reader.
            const panel = document.getElementById("right-panel");
            if (panel) {
                panel.dataset.view = "reader";
                panel.dataset.realBook = "1";
            }
            const titleEl = document.getElementById("panel-title");
            if (titleEl) titleEl.textContent = citation.work || "Reading…";
            try {
                const cw =
                    parseInt(
                        getComputedStyle(
                            document.documentElement,
                        ).getPropertyValue("--panel-w"),
                    ) || 380;
                if (cw < 520 && typeof window.setPanelWidth === "function") {
                    window.setPanelWidth(520);
                }
            } catch (_) {}
            document.body.classList.add("panel-open");
            reader.openCitation(characterId, "", citation);
        } catch (e) {
            console.warn("[ChatController] citation open failed", e);
        }
    }

    _ensureCitationChipStyles() {
        if (document.getElementById("citation-chip-styles")) return;
        const st = document.createElement("style");
        st.id = "citation-chip-styles";
        st.textContent = `
            .citation-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
            .citation-chip {
                display: inline-flex; align-items: center; gap: 4px;
                font-size: 12px; line-height: 1.2; padding: 4px 10px;
                border-radius: 999px; cursor: pointer;
                border: 1px solid rgba(180, 140, 70, 0.45);
                background: rgba(242, 201, 76, 0.12); color: inherit;
                font-family: inherit; max-width: 100%;
                white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
            }
            .citation-chip:hover { background: rgba(242, 201, 76, 0.28); }
        `;
        document.head.appendChild(st);
    }

    appendKeywordImagesToMessage(messageElement, keywordImages) {
        // Check if images already added to prevent duplicates
        if (messageElement.querySelector(".keyword-images-container")) {
            return;
        }

        // Create and append the images container
        const imagesContainer =
            this.createKeywordImagesContainer(keywordImages);
        if (imagesContainer) {
            messageElement.appendChild(imagesContainer);
            // Option 1: don't yank the view down when extra media is added;
            // the reply stays anchored near its top.
        }
    }

    /**
     * Append keyword videos to an existing message element
     * @param {HTMLElement} messageElement - The message element to append videos to
     * @param {Object} videoData - Video data from API
     */
    appendKeywordVideosToMessage(messageElement, videoData) {
        // Check if videos already added to prevent duplicates
        if (messageElement.querySelector(".keyword-videos-container")) {
            return;
        }

        // Create and append the videos container
        const videosContainer = this.createKeywordVideosContainer(videoData);
        if (videosContainer) {
            messageElement.appendChild(videosContainer);
            // Option 1: keep the reply anchored near its top; don't scroll down
            // to the recommendations automatically.
            console.log(
                `✅ Appended ${videoData.videos.length} videos to bot message`,
            );
        }
    }

    /**
     * Process any remaining buffered data when streaming completes
     * @param {string} buffer - The remaining buffered data
     */
    processBufferedData(buffer) {
        if (buffer.startsWith("data: ")) {
            try {
                const data = JSON.parse(buffer.slice(6));
                if (data.type === "complete") {
                    console.log("🎯 Processing buffered complete data:", data);
                    this.processStreamingCompletion(data);
                }
            } catch (parseError) {
                console.warn(
                    "Failed to parse buffered data:",
                    buffer.substring(0, 200) +
                        (buffer.length > 200 ? "...[Truncated]" : ""),
                );
            }
        }
    }

    /**
     * Update streaming message with keyword images
     * @param {string} content - The message content
     * @param {Array} keywordImages - Array of keyword image data
     */
    updateStreamingMessageWithImages(content, keywordImages) {
        if (this.currentStreamingMessage && keywordImages.length > 0) {
            const messageContent =
                this.currentStreamingMessage.querySelector(".message-content");
            if (messageContent) {
                // Create HTML content with text and inline images
                const htmlContent = this.createMessageWithInlineImages(
                    content,
                    keywordImages,
                );
                messageContent.innerHTML = htmlContent;

                console.log(
                    "🖼️ Displaying keyword images inline:",
                    keywordImages,
                );
            }
        }
    }

    /**
     * Create HTML content with inline images
     * @param {string} content - The message text content
     * @param {Array} keywordImages - Array of keyword image data
     * @returns {string} - HTML content with inline images
     */
    createMessageWithInlineImages(content, keywordImages) {
        let htmlContent = `<div class="message-text">${content}</div>`;

        if (keywordImages && keywordImages.length > 0) {
            // Collect all unique images from all keyword results
            const allImages = [];
            keywordImages.forEach((result) => {
                const images = result.images || [];
                images.forEach((image) => {
                    // Only add if not already in our collection
                    if (
                        !allImages.find(
                            (existing) => existing.url === image.url,
                        )
                    ) {
                        allImages.push({
                            ...image,
                            keyword: result.fetched_keyword,
                        });
                    }
                });
            });

            if (allImages.length > 0) {
                htmlContent += `
                    <div class="inline-keyword-images" style="
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 10px;
                        margin-top: 15px;
                        padding-top: 15px;
                        border-top: 1px solid rgba(255, 255, 255, 0.2);
                    ">
                `;

                allImages.forEach((image) => {
                    htmlContent += `
                        <div class="inline-image-item" style="
                            position: relative;
                            border-radius: 8px;
                            overflow: hidden;
                            background-color: rgba(255, 255, 255, 0.1);
                            border: 1px solid rgba(255, 255, 255, 0.2);
                            transition: transform 0.2s ease, box-shadow 0.2s ease;
                            cursor: pointer;
                        " onclick="if (window.modelViewer && window.modelViewer.showImage3D) { window.modelViewer.showImage3D('${image.url}', '${image.keyword || "Keyword"}'); } else if (window.simpleImagePanel) { window.simpleImagePanel.showImage('${image.url}'); } else if (window.imageViewerModal) { window.imageViewerModal.openModal('${image.url}', '${image.filename || "Image"}', '${image.keyword || "Keyword"}'); } else { window.open('${image.url}', '_blank'); }">
                            <img src="${image.url}" 
                                 alt="${image.keyword || "Keyword"} - ${image.filename || "Image"}"
                                 style="
                                     width: 100%;
                                     height: 120px;
                                     object-fit: cover;
                                     display: block;
                                     border-radius: 8px;
                                 "
                                 onerror="this.parentElement.style.display='none'">
                        </div>
                    `;
                });

                htmlContent += `</div>`;

                // Add hover effect styles
                htmlContent += `
                    <style>
                        .inline-image-item:hover {
                            transform: scale(1.02);
                            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                        }
                    </style>
                `;
            }
        }

        return htmlContent;
    }

    /**
     * Set up the mode toggle buttons for Answer, Image, and Video
     */
    setupModeButtons() {
        if (!this.answerModeBtn || !this.imageModeBtn || !this.videoModeBtn) {
            console.warn("Mode buttons not found, skipping mode setup");
            return;
        }

        // Set up click handlers for mode buttons
        this.answerModeBtn.addEventListener("click", () => {
            this.switchToMode("answer");
        });

        this.imageModeBtn.addEventListener("click", () => {
            this.switchToMode("image");
        });

        this.videoModeBtn.addEventListener("click", () => {
            this.switchToMode("video");
        });

        // Set up image navigation buttons
        if (this.prevImageBtn && this.nextImageBtn) {
            this.prevImageBtn.addEventListener("click", () => {
                this.navigateImage(-1);
            });

            this.nextImageBtn.addEventListener("click", () => {
                this.navigateImage(1);
            });
        }

        console.log("Mode buttons initialized successfully");
    }

    /**
     * Switch between different chat modes (answer, image, video)
     * @param {string} mode - The mode to switch to ('answer', 'image', or 'video')
     */
    switchToMode(mode) {
        // Remove active class from all mode buttons
        const modeButtons = [
            this.answerModeBtn,
            this.imageModeBtn,
            this.videoModeBtn,
        ];
        modeButtons.forEach((btn) => btn.classList.remove("active"));

        // Hide all mode content
        const modeContents = [
            this.answerContent,
            this.imageContent,
            this.videoContent,
        ];
        modeContents.forEach((content) => content.classList.remove("active"));

        // Activate the selected mode
        switch (mode) {
            case "answer":
                this.answerModeBtn.classList.add("active");
                this.answerContent.classList.add("active");
                break;
            case "image":
                this.imageModeBtn.classList.add("active");
                this.imageContent.classList.add("active");
                this.updateImageDisplay();
                break;
            case "video":
                this.videoModeBtn.classList.add("active");
                this.videoContent.classList.add("active");
                break;
        }

        console.log(`Switched to ${mode} mode`);

        // Update notification dots after mode switch
        this.updateNotificationDots();
    }

    /**
     * Show notification dot for image button
     */
    showImageNotification() {
        if (this.imageNotificationDot) {
            this.imageNotificationDot.style.display = "block";
            console.log("Image notification dot shown");
        }
    }

    /**
     * Hide notification dot for image button
     */
    hideImageNotification() {
        if (this.imageNotificationDot) {
            this.imageNotificationDot.style.display = "none";
            console.log("Image notification dot hidden");
        }
    }

    /**
     * Show notification dot for video button
     */
    showVideoNotification() {
        if (this.videoNotificationDot) {
            this.videoNotificationDot.style.display = "block";
            console.log("Video notification dot shown");
        }
    }

    /**
     * Hide� notification dot for video button
     */
    hideVideoNotification() {
        if (this.videoNotificationDot) {
            this.videoNotificationDot.style.display = "none";
            console.log("Video notification dot hidden");
        }
    }

    /**
     * Update notification dots when user switches to a mode
     * This hides the notification for the current active mode
     */
    updateNotificationDots() {
        // Hide notification dot for currently active mode. The mode buttons were
        // removed in the Tuscan-dusk merge, so null-guard before touching them
        // (Task #57) — media renders inline now, so these are usually absent.
        if (this.imageModeBtn && this.imageModeBtn.classList.contains("active")) {
            this.hideImageNotification();
        }
        if (this.videoModeBtn && this.videoModeBtn.classList.contains("active")) {
            this.hideVideoNotification();
        }
    }

    /**
     * Add an image to the available images collection
     * @param {Object} imageData - Image data object containing url, title, description
     */
    addImageToCollection(imageData) {
        this.availableImages.push(imageData);
        this.updateImageDisplay();
        this.updateImageControls();

        console.log(
            `Added image to collection: ${imageData.title || "Untitled"}`,
        );
    }

    /**
     * Update the image display with images in grid layout, full width
     */
    updateImageDisplay() {
        if (!this.availableImages.length) {
            // Show simple message when no images
            this.imageDisplay.innerHTML = `
                <div class="no-images-message">
                    <p>The response does not include images.</p>
                </div>
            `;
            if (this.imageTitle) this.imageTitle.textContent = "";
            if (this.imageDescription) this.imageDescription.textContent = "";
            if (this.imageCounter) this.imageCounter.textContent = "";
            return;
        }

        // Create masonry layout for all images
        let imagesHTML = '<div class="image-masonry">';

        this.availableImages.forEach((image, index) => {
            const keyword = image.keyword || "";
            imagesHTML += `
                <div class="masonry-item">
                    ${keyword ? `<div class="image-keyword">${keyword}</div>` : ""}
                    <img src="${image.url}" alt="Response Image ${index + 1}" />
                </div>
            `;
        });

        imagesHTML += "</div>";

        this.imageDisplay.innerHTML = imagesHTML;

        // Hide title, description and counter elements (with null checks)
        if (this.imageTitle) this.imageTitle.textContent = "";
        if (this.imageDescription) this.imageDescription.textContent = "";
        if (this.imageCounter) this.imageCounter.textContent = "";
    }

    /**
     * Update the image navigation controls - hide them since we're using grid layout
     */
    updateImageControls() {
        if (!this.prevImageBtn || !this.nextImageBtn) return;

        // Hide navigation buttons since we're showing all images in grid
        this.prevImageBtn.style.display = "none";
        this.nextImageBtn.style.display = "none";
    }

    /**
     * Navigate through available images
     * @param {number} direction - Direction to navigate (-1 for previous, 1 for next)
     */
    navigateImage(direction) {
        if (!this.availableImages.length) return;

        const newIndex = this.currentImageIndex + direction;
        if (newIndex >= 0 && newIndex < this.availableImages.length) {
            this.currentImageIndex = newIndex;
            this.updateImageDisplay();
            this.updateImageControls();
        }
    }

    /**
     * Extract images from keyword_images data and replace the collection
     * @param {Array} keywordImages - Array of keyword image objects
     */
    processKeywordImages(keywordImages) {
        if (!keywordImages || !Array.isArray(keywordImages)) {
            // Clear images if no keyword images
            this.availableImages = [];
            this.updateImageDisplay();
            this.updateImageControls();
            return;
        }

        // Clear existing images and start fresh for this response
        this.availableImages = [];
        const uniqueUrls = new Set(); // Track unique URLs to prevent duplicates

        keywordImages.forEach((keywordData, index) => {
            const keyword =
                keywordData.fetched_keyword || keywordData.keyword || "Unknown";

            if (keywordData.images && Array.isArray(keywordData.images)) {
                keywordData.images.forEach((imageObj, imgIndex) => {
                    // Handle both string URLs and image objects
                    const imageUrl =
                        typeof imageObj === "string" ? imageObj : imageObj.url;
                    const filename =
                        imageObj.filename ||
                        imageObj.original_filename ||
                        `Image ${imgIndex + 1}`;

                    if (imageUrl && !uniqueUrls.has(imageUrl)) {
                        uniqueUrls.add(imageUrl);
                        this.availableImages.push({
                            url: imageUrl,
                            keyword: keyword,
                            title: filename,
                            description: "",
                        });

                        console.log(
                            `Added ${keyword} image to gallery: ${imageUrl}`,
                        );
                    }
                });
            }
        });

        // Update display after adding all images
        this.updateImageDisplay();
        this.updateImageControls();

        // Show notification on Image UI button if we have new images
        if (this.availableImages.length > 0) {
            //this.showImageNotification();
        }

        // Trigger auto-slideshow in 3D space with the collected images
        if (this.availableImages.length > 0 && this.keywordImageDisplay) {
            this.keywordImageDisplay.startAutoSlideshow(this.availableImages);
        }
    }

    // Task #530 — processSpeechInput, setupWebRTCAudio, debugWebRTCState and
    // setupFallbackSpeechRecognition are retired. ONE recognition lifecycle
    // lives in setupSpeechRecognition() with 'call' / 'dictate' routing;
    // the WebRTC manager (parallel SR + auto AEC chat test) is gone.

    /**
     * Test AEC functionality directly by measuring echo levels
     */
    async testAECEffectiveness() {
        console.log("🔧 Testing AEC effectiveness directly...");

        try {
            // Get microphone with AEC constraints
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                    channelCount: 1,
                },
            };

            const testStream =
                await navigator.mediaDevices.getUserMedia(constraints);
            const track = testStream.getAudioTracks()[0];
            const settings = track.getSettings();

            console.log("🎤 Microphone settings:", settings);

            if (!settings.echoCancellation) {
                console.error("❌ ERROR: Echo cancellation is NOT enabled!");
                testStream.getTracks().forEach((track) => track.stop());
                return false;
            }

            // Set up audio analysis
            const audioContext = new (window.AudioContext ||
                window.webkitAudioContext)();
            const source = audioContext.createMediaStreamSource(testStream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);

            // Measure baseline for 2 seconds
            console.log("📊 Measuring baseline audio level...");
            const baseline = await this.measureAudioLevel(analyser, 2000);
            console.log(`📊 Baseline: ${baseline.toFixed(2)}`);

            // Play test tone for 3 seconds and measure
            console.log("🔊 Playing test audio and measuring echo...");
            const testAudioPromise = this.playTestTone(audioContext, 3000);
            const echoLevel = await this.measureAudioLevel(analyser, 3000);
            console.log(`📊 Level during audio: ${echoLevel.toFixed(2)}`);

            await testAudioPromise;

            // Clean up
            testStream.getTracks().forEach((track) => track.stop());
            audioContext.close();

            // Analyze results
            const echoIncrease = echoLevel - baseline;
            const echoPercentage =
                baseline > 0 ? (echoIncrease / baseline) * 100 : 0;

            console.log("🔊 === AEC TEST RESULTS ===");
            console.log(
                `Echo increase: ${echoIncrease.toFixed(2)} (${echoPercentage.toFixed(1)}%)`,
            );

            let aecWorking = echoPercentage < 30; // Allow up to 30% increase

            if (aecWorking) {
                console.log("✅ AEC is working! Echo increase is acceptable.");
                this.addMessageToChat(
                    "System",
                    "✅ AEC Test Passed - Echo cancellation is working properly",
                    "success-message",
                );
            } else {
                console.error(
                    "❌ AEC is NOT working properly! Significant echo detected.",
                );
                this.addMessageToChat(
                    "System",
                    "❌ AEC Test Failed - Echo cancellation needs attention",
                    "error-message",
                );
            }

            return aecWorking;
        } catch (error) {
            console.error("❌ AEC test failed:", error);
            this.addMessageToChat(
                "System",
                `❌ AEC Test Error: ${error.message}`,
                "error-message",
            );
            return false;
        }
    }

    async measureAudioLevel(analyser, duration) {
        return new Promise((resolve) => {
            let totalLevel = 0;
            let sampleCount = 0;
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const startTime = Date.now();

            const measure = () => {
                if (Date.now() - startTime < duration) {
                    analyser.getByteFrequencyData(dataArray);
                    const average =
                        dataArray.reduce((a, b) => a + b) / dataArray.length;
                    totalLevel += average;
                    sampleCount++;
                    requestAnimationFrame(measure);
                } else {
                    const averageLevel =
                        sampleCount > 0 ? totalLevel / sampleCount : 0;
                    resolve(averageLevel);
                }
            };

            measure();
        });
    }

    async playTestTone(audioContext, duration) {
        return new Promise((resolve) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);

            oscillator.start();

            setTimeout(() => {
                oscillator.stop();
                resolve();
            }, duration);
        });
    }

    /**
     * Configure and verify AEC (Acoustic Echo Cancellation) settings
     */
    async configureAndVerifyAEC() {
        console.log("🔧 Configuring and verifying AEC settings...");

        try {
            const supportedConstraints =
                navigator.mediaDevices.getSupportedConstraints();
            console.log(
                "🔍 AEC supported:",
                supportedConstraints.echoCancellation,
            );
            console.log(
                "🔍 Noise suppression supported:",
                supportedConstraints.noiseSuppression,
            );
            console.log(
                "🔍 Auto gain control supported:",
                supportedConstraints.autoGainControl,
            );

            const testConstraints = {
                audio: {
                    echoCancellation: { ideal: true },
                    noiseSuppression: { ideal: true },
                    autoGainControl: { ideal: true },
                    channelCount: { ideal: 1 },
                    sampleRate: { ideal: 48000 },
                },
            };

            const testStream =
                await navigator.mediaDevices.getUserMedia(testConstraints);
            const track = testStream.getAudioTracks()[0];
            const settings = track.getSettings();

            console.log(" c� AEC Configuration verified:");
            console.log("  - Echo cancellation:", settings.echoCancellation);
            console.log("  - Noise suppression:", settings.noiseSuppression);
            console.log("  - Auto gain control:", settings.autoGainControl);
            console.log("  - Sample rate:", settings.sampleRate);
            console.log("  - Channel count:", settings.channelCount);

            testStream.getTracks().forEach((track) => track.stop());

            /*this.addMessageToChat(
                "System",
                `🔧 AEC Configuration Active:<br>
                • Echo Cancellation: ${settings.echoCancellation ? "✅ Enabled" : "❌ Disabled"}<br>
                • Noise Suppression: ${settings.noiseSuppression ? "✅ Enabled" : "❌ Disabled"}<br>
                • Auto Gain Control: ${settings.autoGainControl ? "✅ Enabled" : "❌ Disabled"}<br>
                • Sample Rate: ${settings.sampleRate}<br>
                • Channel Count: ${settings.channelCount}`,
                "success-message"
            );*/

            return true;
        } catch (error) {
            // Dev-only diagnostic — never paints into the visible chat
            console.error("❌ AEC configuration failed:", error);
            return false;
        }
    }

    // ===== Q&A MODE SETUP =====

    /**
     * Setup Q&A mode - continuous listening behavior
     */
    setupQAMode() {
        console.log("Setting up Q&A mode with continuous listening...");

        // Q&A mode is always active - no UI selector needed
        this.qaMode = true;
        this.continuousListening = false;

        console.log("Q&A mode initialized - continuous listening ready");
    }

    /**
     * Handle Q&A mode when AI completes responding
     */
    handleQAModeOnAIComplete() {
        if (!this.qaMode) return;

        // Task #530 — the listen↔reply loop belongs to CALL mode only.
        // Dictation (or no active mode) must never restart the mic here.
        if (this.micMode !== "call") return;

        console.log(
            "Q&A Mode: AI finished responding, checking listening state...",
        );

        // Check if microphone is already listening (started immediately after message send)
        if (this.isListening) {
            console.log(
                "Q&A Mode: ✅ Microphone already listening from immediate restart - no action needed",
            );
            this.startLongSilenceDetection(); // Start monitoring for extended silence
            return;
        }

        // Only restart if user has manually activated microphone (AEC handles echo prevention)
        setTimeout(() => {
            if (
                !this.isListening &&
                !this.manualStop &&
                this.userActivatedMicrophone
            ) {
                console.log(
                    "Q&A Mode: Backup restart - microphone wasn't listening yet (AEC prevents echo)",
                );
                this.startListening();

                // Ensure the UI shows that we're listening again
                if (this.micButton) {
                    this.micButton.classList.add("active");
                }
                // Show listening animation via CSS class
                if (this.micButtonContainer) {
                    this.micButtonContainer.classList.add("listening");
                }

                console.log("Q&A Mode: ✅ Backup listening restart completed");
                this.startLongSilenceDetection(); // Start monitoring for extended silence
            } else if (!this.userActivatedMicrophone) {
                console.log(
                    "Q&A Mode: Microphone not restarted - user has not manually activated microphone",
                );
            } else {
                console.log(
                    "Q&A Mode: ✅ Listening already active - continuous flow maintained",
                );
            }
        }, 500); // Shorter delay since immediate restart should have already happened
    }

    /**
     * Start long-term silence detection that logs but doesn't show annoying messages
     */
    startLongSilenceDetection() {
        // Clear any existing long silence timer
        if (this.longSilenceTimer) {
            clearTimeout(this.longSilenceTimer);
        }

        // Set a longer timeout for logging silence but don't show messages to user
        this.longSilenceTimer = setTimeout(() => {
            if (
                this.isListening &&
                !this.isProcessing &&
                !this.isAudioPlaying
            ) {
                console.log(
                    "📢 Long silence detected - microphone still active and listening",
                );

                // Restart the long silence detection timer
                this.startLongSilenceDetection();
            }
        }, 60000); // 60 seconds of silence before logging

        console.log(
            "⏰ Long silence detection started - will log after 60 seconds",
        );
    }

    /**
     * Reset long silence detection when user speaks
     */
    resetLongSilenceDetection() {
        if (this.longSilenceTimer) {
            clearTimeout(this.longSilenceTimer);
            console.log("🔄 Long silence timer reset - user spoke");
        }
        this.startLongSilenceDetection();
    }

    /**
     * Exit chat mode functionality - stops microphone and hides chat window
     */
    exitChatMode() {
        console.log("🚪 Exiting chat mode - stopping all chat activities");

        // 1. Stop microphone and prevent recorded text from being sent
        this.stopMicrophoneAndClearInput();

        // 2. Stop any playing audio
        if (this.isAudioPlaying) {
            this.stopAudio();
            console.log("🛑 Stopped audio playback during chat exit");
        }

        // 3. Hide chat container
        if (this.chatContainer) {
            this.chatContainer.classList.remove("visible");
            console.log("📦 Chat container hidden");
        }

        // 4. Reset processing flags
        this.isProcessing = false;
        this.userActivatedMicrophone = false;

        // 5. Clear any pending messages
        this.pendingMessage = null;

        // 6. Reset camera position if available
        if (this.modelViewer && this.modelViewer.getCameraAnimator) {
            const cameraAnimator = this.modelViewer.getCameraAnimator();
            if (cameraAnimator) {
                cameraAnimator.chatCameraAnimation(false, {
                    duration: 1000,
                });
                console.log("📸 Camera position reset");
            }
        }

        // 7. Exit diagnostic mode if active (resets TutorState and syncs buttons)
        if (this.diagnosticChatManager && this.diagnosticChatManager.isActive) {
            this.exitDiagnosticMode();
            console.log("📊 Diagnostic mode exited");
        } else {
            // Reset TutorState flags for non-diagnostic interactive modes (e.g., subtopic quiz)
            if (window.TutorState) {
                window.TutorState.answerViaChatTriggered = false;
                window.TutorState.isChatModeActive = false;
                window.TutorState.isSubtopicChatModeActive = false;
                window.TutorState.activeQuizQuestionId = null;
                console.log("🔄 TutorState flags reset");
            }
            if (window.appState) {
                window.appState.answerViaChatTriggered = false;
            }
            // Sync button labels back to "Answer Interactively"
            if (window.TutorApp) {
                if (window.TutorApp.syncAllQuizInteractiveButtons) {
                    window.TutorApp.syncAllQuizInteractiveButtons();
                }
                if (window.TutorApp.updateAnswerViaChatButton) {
                    window.TutorApp.updateAnswerViaChatButton();
                }
                console.log("🔘 Interactive button labels synced");
            }
        }

        console.log("✅ Chat mode exit complete - all systems stopped");
    }

    /**
     * Stop microphone and clear input to prevent sending recorded text
     */
    stopMicrophoneAndClearInput() {
        // Task #530 — end whichever voice mode is active first so its UI
        // and restart logic wind down coherently. (Dictation discards its
        // text here — this path exists to prevent any recorded text from
        // being sent.)
        if (this.micMode === "dictate") {
            this.stopDictation({
                keepText: false,
                refocus: false,
                reason: "exit-chat",
            });
        } else if (this.micMode === "call") {
            this.endCall("exit-chat");
        }

        // Stop speech recognition immediately
        if (this.isListening) {
            this.manualStop = true; // Set manual stop flag to prevent auto-restart
            this.stopListening();
            console.log("🎤 Microphone stopped and auto-restart disabled");
        }

        // Clear chat input to prevent sending any recorded text
        if (this.chatInput) {
            this.chatInput.value = "";
            console.log(
                "🧹 Chat input cleared to prevent sending recorded text",
            );
        }

        // Update UI to show microphone is stopped
        this.updateStoppedListeningUI();

        // Clear any silence timers
        if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
        }

        if (this.longSilenceTimer) {
            clearTimeout(this.longSilenceTimer);
            this.longSilenceTimer = null;
        }

        // Reset microphone flags
        this.recognitionPausedForAudio = false;
        this.stoppedBySilence = false;
    }

    /**
     * Switch between Text, Image, and Video UI modes seamlessly
     * @param {string} mode - The mode to switch to ('answer', 'image', 'video')
     */
    switchUIMode(mode) {
        console.log(`🔄 Switching UI mode to: ${mode}`);

        // Remove active class from all mode buttons
        const allModeButtons = [
            this.answerModeBtn,
            this.imageModeBtn,
            this.videoModeBtn,
        ];
        allModeButtons.forEach((btn) => {
            if (btn) btn.classList.remove("active");
        });

        // Hide all content panels
        const allContentPanels = [
            this.answerContent,
            this.imageContent,
            this.videoContent,
        ];
        allContentPanels.forEach((panel) => {
            if (panel) panel.classList.remove("active");
        });

        // Reset notification for the selected mode
        if (mode === "image") {
            this.clearImageNotification();
        } else if (mode === "video") {
            this.clearVideoNotification();
        }

        // Activate the selected mode
        switch (mode) {
            case "answer":
                if (this.answerModeBtn)
                    this.answerModeBtn.classList.add("active");
                if (this.answerContent)
                    this.answerContent.classList.add("active");
                console.log("📝 Text UI mode activated");
                break;

            case "image":
                if (this.imageModeBtn)
                    this.imageModeBtn.classList.add("active");
                if (this.imageContent)
                    this.imageContent.classList.add("active");
                this.updateImageDisplay();
                console.log("🖼️ Image UI mode activated");
                break;

            case "video":
                if (this.videoModeBtn)
                    this.videoModeBtn.classList.add("active");
                if (this.videoContent)
                    this.videoContent.classList.add("active");
                this.updateVideoDisplay();
                console.log("🎥 Video UI mode activated");
                break;

            default:
                console.warn(`Unknown UI mode: ${mode}`);
                // Default to answer mode
                if (this.answerModeBtn)
                    this.answerModeBtn.classList.add("active");
                if (this.answerContent)
                    this.answerContent.classList.add("active");
        }

        // Ensure chat container stays visible during mode switching
        if (this.chatContainer) {
            this.chatContainer.classList.add("visible");
        }

        console.log(`✅ UI mode switch to ${mode} complete`);
    }

    /**
     * Display images in single column format for image mode
     */
    displayImagesInGallery() {
        if (!this.imageDisplay || this.availableImages.length === 0) return;

        this.imageDisplay.innerHTML = "";

        // Apply full-width styling directly to imageDisplay container
        this.imageDisplay.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 20px;
            padding: 15px;
            margin: 0;
            height: 100%;
            width: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
        `;

        // Hide scrollbar for webkit browsers
        const scrollbarStyle = document.createElement("style");
        scrollbarStyle.textContent = `
            .chat-content.active .image-content::-webkit-scrollbar,
            .image-display::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
            }
        `;
        if (!document.querySelector("style[data-image-scrollbar-hide]")) {
            scrollbarStyle.setAttribute("data-image-scrollbar-hide", "true");
            document.head.appendChild(scrollbarStyle);
        }

        // Add each image directly to imageDisplay (no extra containers)
        this.availableImages.forEach((image, index) => {
            // Create keyword label with Video UI styling
            const keywordLabel = document.createElement("div");
            keywordLabel.className = "image-keyword-tag";
            keywordLabel.textContent = image.keyword || "Unknown";
            keywordLabel.style.cssText = `
                background: rgba(58,65,73, 0.3);
                color: white;
                padding: 6px 12px;
                border-radius: 10px;
                font-size: 0.9rem;
                font-weight: 500;
                text-transform: capitalize;
                text-align: center;
                margin-bottom: 5px;
                width: 100%;
            `;

            // Create full-width image container
            const imageContainer = document.createElement("div");
            imageContainer.className = "gallery-image-full-width";
            imageContainer.style.cssText = `
                position: relative;
                width: 100%;
                cursor: pointer;
                transition: transform 0.2s ease;
                margin-bottom: 20px;
            `;

            const img = document.createElement("img");
            img.src = image.url;
            img.alt = image.filename || "Image";
            img.style.cssText = `
                width: 100%;
                height: auto;
                object-fit: contain;
                display: block;
                border-radius: 8px;
            `;

            img.onerror = () => {
                keywordLabel.style.display = "none";
                imageContainer.style.display = "none";
            };

            imageContainer.addEventListener("mouseenter", () => {
                imageContainer.style.transform = "scale(1.01)";
            });

            imageContainer.addEventListener("mouseleave", () => {
                imageContainer.style.transform = "scale(1)";
            });

            imageContainer.addEventListener("click", () => {
                if (window.imageViewerModal) {
                    window.imageViewerModal.openModal(
                        image.url,
                        image.filename,
                        image.keyword,
                    );
                } else {
                    window.open(image.url, "_blank");
                }
            });

            imageContainer.appendChild(img);

            // Add keyword label and image directly to imageDisplay
            this.imageDisplay.appendChild(keywordLabel);
            this.imageDisplay.appendChild(imageContainer);
        });
    }

    /**
     * Update video display when switching to video mode
 �    */
    updateVideoDisplay() {
        if (!this.videoContent) return;

        const videoDisplay = this.videoContent.querySelector("#video-display");
        if (!videoDisplay) return;

        // Check if there are any videos from recent responses
        const existingVideos = videoDisplay.querySelectorAll("iframe");

        if (existingVideos.length === 0) {
            // Show placeholder when no videos available
            videoDisplay.innerHTML = `
                <div class="video-placeholder">
                    <i class="fas fa-video"></i>
                    <p>No videos available from responses yet</p>
                </div>
            `;
        }
        // If videos exist, they're already displayed correctly
    }

    /**
     * Clear notification from Image UI button
     */
    clearImageNotification() {
        if (!this.imageModeBtn) return;

        const notification =
            this.imageModeBtn.querySelector(".mode-notification");
        if (notification) {
            notification.remove();
            console.log("✅ Image notification cleared");
        }
    }

    /**
     * Clear notification from Video UI button
     */
    clearVideoNotification() {
        if (!this.videoModeBtn) return;

        const notification =
            this.videoModeBtn.querySelector(".mode-notification");
        if (notification) {
            notification.remove();
            console.log("✅ Video notification cleared");
        }
    }

    /**
     * Set up listener for model changes to check microphone availability
     */
    setupModelChangeListener() {
        // Listen for character switch events
        document.addEventListener("characterSwitch", () => {
            this.checkMicrophoneAvailability();
        });

        // Listen for character-changed events
        document.addEventListener("character-changed", () => {
            this.checkMicrophoneAvailability();
        });

        // Listen for model-loaded events
        document.addEventListener("model-loaded", () => {
            this.checkMicrophoneAvailability();
        });

        // Also check when ModelManager notifies of model changes - with retry
        const setupModelManagerListener = () => {
            if (
                window.ModelManager &&
                window.ModelManager.addModelSelectListener
            ) {
                window.ModelManager.addModelSelectListener(() => {
                    setTimeout(() => this.checkMicrophoneAvailability(), 500);
                });
                console.log("ModelManager listener set up successfully");
            } else {
                // Retry in 1 second if ModelManager not ready yet
                setTimeout(() => {
                    if (
                        window.ModelManager &&
                        window.ModelManager.addModelSelectListener
                    ) {
                        window.ModelManager.addModelSelectListener(() => {
                            setTimeout(
                                () => this.checkMicrophoneAvailability(),
                                500,
                            );
                        });
                        console.log(
                            "ModelManager listener set up successfully (delayed)",
                        );
                    } else {
                        console.log(
                            "ModelManager still not available for listener setup",
                        );
                    }
                }, 1000);
            }
        };

        setupModelManagerListener();
    }

    /**
     * Check if microphone should be available based on current model
     */
    checkMicrophoneAvailability() {
        try {
            console.log("Checking microphone availability...");
            const currentModelId = this.getCurrentModelId();
            console.log("Current model ID:", currentModelId);

            if (!currentModelId) {
                console.log(
                    "No model ID found, enabling microphone by default",
                );
                this.enableMicrophone();
                return;
            }

            console.log("Female model IDs to check:", this.femaleModelIds);
            const isFemaleModel = this.femaleModelIds.includes(currentModelId);
            console.log("Is female model?", isFemaleModel);

            if (isFemaleModel) {
                const modelName = this.getModelNameById(currentModelId);
                this.disableMicrophone(modelName);
                console.log(
                    `Microphone disabled for female model: ${modelName} (${currentModelId})`,
                );
            } else {
                this.enableMicrophone();
                console.log(`Microphone enabled for model: ${currentModelId}`);
            }
        } catch (error) {
            console.error("Error checking microphone availability:", error);
            // On error, enable microphone by default
            this.enableMicrophone();
        }
    }

    /**
     * Get current model ID from various sources
     */
    getCurrentModelId() {
        try {
            console.log("Getting current model ID...");

            // Method 1: Try to get from ModelManager
            if (
                window.ModelManager &&
                typeof window.ModelManager.getCurrentModelId === "function"
            ) {
                const modelId = window.ModelManager.getCurrentModelId();
                console.log("ModelManager model ID:", modelId);
                if (modelId) return modelId;
            } else {
                console.log(
                    "ModelManager not available or getCurrentModelId not a function",
                );
            }

            // Method 2: Try to get from localStorage
            const storedModelId = localStorage.getItem("selectedModelId");
            console.log("localStorage model ID:", storedModelId);
            if (storedModelId) return storedModelId;

            // Method 3: Try to get from global modelViewer
            if (this.modelViewer && this.modelViewer.currentModelId) {
                console.log(
                    "modelViewer model ID:",
                    this.modelViewer.currentModelId,
                );
                return this.modelViewer.currentModelId;
            }

            // Method 4: Try to get from global window variables
            if (window.currentModelId) {
                console.log("window.currentModelId:", window.currentModelId);
                return window.currentModelId;
            }

            console.log("No model ID found from any source");
            return null;
        } catch (error) {
            console.error("Error getting current model ID:", error);
            return null;
        }
    }

    /**
     * Get model name by ID for display
     */
    getModelNameById(modelId) {
        try {
            // Try from characterData first
            if (window.characterData && window.characterData[modelId]) {
                return window.characterData[modelId].name;
            }

            // Try from BioDataManager
            if (window.BioDataManager) {
                const bioData = new window.BioDataManager();
                const data = bioData.getBioData(modelId);
                if (
                    data &&
                    data.fullName &&
                    data.fullName !== "Unknown Character"
                ) {
                    return data.fullName;
                }
            }

            // Fallback names for specific female model IDs
            const fallbackNames = {
                "770e2d93-a6a4-41ed-aca1-8951327885b5": "Amelia Earhart",
                "73f610bf-d9c9-4d9f-a026-a10086a66725": "Marie Curie",
            };

            return fallbackNames[modelId] || "this character";
        } catch (error) {
            console.error("Error getting model name:", error);
            return "this character";
        }
    }

    /**
     * Disable microphone and show message
     */
    disableMicrophone(modelName) {
        if (!this.micButton || !this.micButtonContainer) return;

        // Task #530 — end any active voice session before hiding controls
        if (this.micMode === "dictate") {
            this.stopDictation({
                keepText: true,
                refocus: false,
                reason: "model-switch",
            });
        } else if (this.micMode === "call") {
            this.endCall("model-switch");
        }

        // Hide the microphone button
        this.micButtonContainer.style.display = "none";

        // Hide the dictate control too — same speech recognition backend
        if (this.dictateWrap) this.dictateWrap.style.display = "none";

        // Show a message in the chat
        this.showMicrophoneDisabledMessage(modelName);

        // Disable any active speech recognition
        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (error) {
                console.warn("Error stopping speech recognition:", error);
            }
        }

        // Update microphone status
        this.isListening = false;
        this.micButton.classList.remove("listening");
    }

    /**
     * Enable microphone
     */
    enableMicrophone() {
        if (!this.micButton || !this.micButtonContainer) return;

        // Show the microphone button
        this.micButtonContainer.style.display = "flex";

        // Restore the dictate control alongside it (unless SR is unsupported,
        // in which case setupSpeechRecognition already hid it for good)
        if (this.dictateWrap && this.recognition) {
            this.dictateWrap.style.display = "";
        }
    }

    /**
     * Show message that speech recognition is not available for this model
     */
    showMicrophoneDisabledMessage(modelName) {
        const message = `Speech recognition is not available for ${modelName}. Please use text input to communicate.`;

        // Add a system message to the chat
        //this.addMessageToChat("System", message, "system-message");
    }
}
